<template>
  <div>
    <!-- Contenido principal -->
    <div class="card">
      <div class="card-header bg-success pt-0">
        <!-- Primera fila: Filtros básicos + Botones principales -->
        <!-- 
          MEJORA UX v2.5: Etiquetas agregadas a filtros básicos
          - Cada filtro ahora tiene una etiqueta descriptiva visible
          - Mejora significativa en UX móvil donde no se entendía la función de cada campo
          - Iconos agregados para mejor identificación visual
          - Estilos específicos para desktop (text-light) y móvil (text-white fw-bold)
        -->

        
        <!-- Filtros básicos para desktop/tablet optimizados -->
        <div class="d-none d-md-block">
          <!-- Primera fila: Búsqueda General, Fecha Desde, Fecha Hasta, Estado -->
          <div class="row mb-1">
            <div class="col-md-3 mb-1">
              <label class="form-label small text-light mb-1 d-block" for="searchText">
                <i class="fas fa-search me-1"></i> Búsqueda general
              </label>
              <input
                id="searchText"
                type="text"
                class="form-control form-control-sm"
                placeholder="ID o Cliente"
                v-model="filters.searchText"
                @keyup.enter="handleSearch"
              />
            </div>
            <div class="col-md-3 mb-1">
              <label class="form-label small text-light mb-1 d-block" for="dateFrom">
                <i class="fas fa-calendar-alt me-1"></i> Fecha desde
              </label>
              <input
                id="dateFrom"
                type="date"
                class="form-control form-control-sm"
                v-model="filters.dateFrom"
                :max="filters.dateTo || getTodayDate()"
                @change="validateDateRange"
              />
            </div>
            <div class="col-md-3 mb-1">
              <label class="form-label small text-light mb-1 d-block" for="dateTo">
                <i class="fas fa-calendar-check me-1"></i> Fecha hasta
              </label>
              <input
                id="dateTo"
                type="date"
                class="form-control form-control-sm"
                v-model="filters.dateTo"
                :min="filters.dateFrom"
                :max="getTodayDate()"
                @change="validateDateRange"
              />
            </div>
            <div class="col-md-3 mb-1">
              <label class="form-label small text-light mb-1 d-block" for="statusSelect">
                <i class="fas fa-check-circle me-1"></i> Estado
              </label>
              <select id="statusSelect" class="form-select form-select-sm" v-model="filters.status">
                <option value="">Todos los estados</option>
                <option v-for="status in closeStatusCatalog" :key="status" :value="status">
                  {{ status }}
                </option>
              </select>
            </div>
          </div>
          
          <!-- Segunda fila: Sucursal, Método de Pago, y Botones alineados a la derecha -->
          <div class="row mb-1">
            <div class="col-md-3 mb-1">
              <label class="form-label small text-light mb-1 d-block" for="branchSelect">
                <i class="fas fa-building me-1"></i> Sucursal
              </label>
              <select id="branchSelect" class="form-select form-select-sm branch-select-fixed-width" v-model="filters.branch">
                <option value="">Todas las sucursales</option>
                <option v-for="branch in branches" :key="branch" :value="branch">
                  {{ branch }}
                </option>
              </select>
            </div>
            <div class="col-md-3 mb-1">
              <label class="form-label small text-light mb-1 d-block" for="paymentMethodSelect">
                <i class="fas fa-credit-card me-1"></i> Método de pago
              </label>
              <select id="paymentMethodSelect" class="form-select form-select-sm" v-model="filters.paymentMethod">
                <option value="">Todos los métodos</option>
                <option v-for="method in paymentMethodsCatalog" :key="method" :value="method">
                  {{ method }}
                </option>
              </select>
            </div>
            <div class="col-md-6 mb-1">
              <!-- Botones de búsqueda y exportación alineados perfectamente con los campos -->
              <div class="d-flex justify-content-end align-items-end h-100">
                <div class="d-flex mt-auto">
                  <button
                    class="btn btn-info btn-sm rounded-pill btn-filter--border text-nowrap me-2"
                    type="button"
                    @click="handleSearch"
                    :disabled="loading || isSearching"
                  >
                    <i v-if="isSearching" class="fas fa-spinner fa-spin me-1"></i>
                    <i v-else class="fa fa-search me-1" aria-hidden="true"></i>
                    {{ isSearching ? 'Buscando...' : 'Buscar' }}
                  </button>
                  
                  <!-- ✅ BOTÓN RESTAURADO: Exportar Ventas a Excel -->
                  <button
                    class="btn btn-success btn-sm btn-filter--border"
                    type="button"
                    @click="exportRecords"
                    :disabled="loading || isExporting || (!records || records.length === 0)"
                    title="Exportar ventas a Excel con sistema multi-hoja"
                  >
                    <i v-if="isExporting" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
                    <i v-else class="fa fa-file-excel" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Segunda fila: Botones de paneles centrados -->
        <div class="d-none d-md-flex justify-content-center pt-2 border-top border-light border-opacity-25">
          <div class="btn-group" role="group">
            <button
              class="btn btn-sm rounded-pill btn-filter--border text-nowrap panel-toggle-btn"
              :class="{ 'active': showAdvancedFilters }"
              type="button"
              @click="showAdvancedFilters = !showAdvancedFilters"
              title="Mostrar/Ocultar filtros avanzados"
            >
              <i class="fas fa-filter me-2" aria-hidden="true"></i>
              Avanzados
            </button>
            
            <button
              class="btn btn-sm rounded-pill btn-filter--border mx-2 text-nowrap panel-toggle-btn"
              :class="{ 'active': showChartsPanel }"
              type="button"
              @click="showChartsPanel = !showChartsPanel"
              title="Mostrar/Ocultar gráficos"
            >
              <i class="fas fa-chart-bar me-2" aria-hidden="true"></i>
              Gráficos
            </button>
            
            <button
              class="btn btn-sm rounded-pill btn-filter--border text-nowrap panel-toggle-btn"
              :class="{ 'active': showSavedFilters }"
              type="button"
              @click="showSavedFilters = !showSavedFilters"
              title="Mostrar/Ocultar filtros guardados"
            >
              <i class="fas fa-bookmark me-2" aria-hidden="true"></i>
              Guardados
            </button>
          </div>
        </div>

        <!-- Filtros móviles -->
        <div class="d-md-none">
          <button
            class="btn btn-info btn-sm rounded-pill btn-filter--border w-100 mb-2"
            type="button"
            @click="showMobileFilters = !showMobileFilters"
          >
            <i class="fa fa-sliders me-2" aria-hidden="true"></i>
            {{ showMobileFilters ? 'Ocultar Filtros' : 'Mostrar Filtros' }}
          </button>
          
          <!-- Panel de filtros móviles -->
          <div v-if="showMobileFilters" class="mobile-filters">
            <div class="mb-3">
              <label class="form-label small text-white fw-bold mb-2 d-block" for="searchTextMobile">
                <i class="fas fa-search me-2"></i>Búsqueda general
              </label>
              <input
                id="searchTextMobile"
                type="text"
                class="form-control form-control-sm"
                placeholder="ID o Cliente"
                v-model="filters.searchText"
                @keyup.enter="handleSearch"
              />
            </div>
            <div class="row mb-3">
              <div class="col-6">
                <label class="form-label small text-white fw-bold mb-2 d-block" for="dateFromMobile">
                  <i class="fas fa-calendar-alt me-2"></i>Fecha desde
                </label>
                <input
                  id="dateFromMobile"
                  type="date"
                  class="form-control form-control-sm"
                  v-model="filters.dateFrom"
                  :max="filters.dateTo || getTodayDate()"
                  @change="validateDateRange"
                />
              </div>
              <div class="col-6">
                <label class="form-label small text-white fw-bold mb-2 d-block" for="dateToMobile">
                  <i class="fas fa-calendar-check me-2"></i>Fecha hasta
                </label>
                <input
                  id="dateToMobile"
                  type="date"
                  class="form-control form-control-sm"
                  v-model="filters.dateTo"
                  :min="filters.dateFrom"
                  :max="getTodayDate()"
                  @change="validateDateRange"
                />
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-6 pe-2">
                <label class="form-label small text-white fw-bold mb-2 d-block" for="statusSelectMobile">
                  <i class="fas fa-check-circle me-2"></i>Estado
                </label>
                <select id="statusSelectMobile" class="form-select form-select-sm" v-model="filters.status">
                  <option value="">Todos los estados</option>
                  <option v-for="status in closeStatusCatalog" :key="status" :value="status">
                    {{ status }}
                  </option>
                </select>
              </div>
              <div class="col-6 ps-2">
                <label class="form-label small text-white fw-bold mb-2 d-block" for="branchSelectMobile">
                  <i class="fas fa-building me-2"></i>Sucursal
                </label>
                <select id="branchSelectMobile" class="form-select form-select-sm w-100" v-model="filters.branch">
                  <option value="">Todas las sucursales</option>
                  <option v-for="branch in branches" :key="branch" :value="branch">
                    {{ branch }}
                  </option>
                </select>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-12">
                <label class="form-label small text-white fw-bold mb-2 d-block" for="paymentMethodSelectMobile">
                  <i class="fas fa-credit-card me-2"></i>Método de pago
                </label>
                <select id="paymentMethodSelectMobile" class="form-select form-select-sm" v-model="filters.paymentMethod">
                  <option value="">Todos los métodos</option>
                  <option v-for="method in paymentMethodsCatalog" :key="method" :value="method">
                    {{ method }}
                  </option>
                </select>
              </div>
            </div>
            
             <!-- Botones de panel en mobile -->
             <div class="btn-group btn-group-sm w-100 mb-2" role="group">
               <button 
                 type="button" 
                 class="btn btn-sm btn-filter--border panel-toggle-btn mobile-btn-consistent"
                 :class="{ 'active': showAdvancedFilters }"
                 @click="showAdvancedFilters = !showAdvancedFilters"
               >
                 <i class="fas fa-filter me-1" aria-hidden="true"></i>
                 Avanzados
               </button>
               <button 
                 type="button" 
                 class="btn btn-sm btn-filter--border panel-toggle-btn mobile-btn-consistent"
                 :class="{ 'active': showChartsPanel }"
                 @click="showChartsPanel = !showChartsPanel"
               >
                 <i class="fas fa-chart-bar me-1" aria-hidden="true"></i>
                 Gráficos
               </button>
               <button 
                 type="button" 
                 class="btn btn-sm btn-filter--border panel-toggle-btn mobile-btn-consistent"
                 :class="{ 'active': showSavedFilters }"
                 @click="showSavedFilters = !showSavedFilters"
               >
                 <i class="fas fa-bookmark me-1" aria-hidden="true"></i>
                 Guardados
               </button>
             </div>
             
             <!-- Botones de búsqueda y exportación móvil -->
             <div class="btn-group btn-group-sm w-100 mt-2" role="group">
               <button 
                 type="button" 
                 class="btn btn-sm btn-info btn-filter--border mobile-btn-consistent"
                 @click="handleSearch"
                 :disabled="loading || isSearching"
               >
                 <i v-if="isSearching" class="fas fa-spinner fa-spin me-1"></i>
                 <i v-else class="fa fa-search me-1" aria-hidden="true"></i>
                 {{ isSearching ? 'Buscando...' : 'Buscar' }}
               </button>
               <!-- ✅ BOTÓN RESTAURADO MÓVIL: Exportar Ventas a Excel -->
               <button 
                 type="button" 
                 class="btn btn-sm btn-success btn-filter--border mobile-btn-consistent"
                 @click="exportRecords"
                 :disabled="loading || isExporting || (!records || records.length === 0)"
                 title="Exportar ventas a Excel con sistema multi-hoja"
               >
                 <i v-if="isExporting" class="fas fa-spinner fa-spin me-1"></i>
                 <i v-else class="fa fa-file-excel me-1" aria-hidden="true"></i>
                 Excel
               </button>
             </div>
          </div>
        </div>
      </div>
      
      <div class="card-body">

        <!-- 2. Panel de Filtros Avanzados (Colapsable) v2.2 -->
        <div v-if="showAdvancedFilters" class="card mb-3 border-primary">
          <div class="card-header bg-primary text-white" style="padding: 0.375rem 1rem;">
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="mb-0">
                <i class="fas fa-filter me-2"></i>
                Filtros Avanzados - Ventas Gimnasio
              </h6>
              <button 
                type="button" 
                class="btn btn-sm btn-light"
                @click="showAdvancedFilters = false"
                title="Cerrar panel"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
          <div class="card-body">
            <!-- Mensaje informativo sobre filtros avanzados -->
            <div class="alert alert-info alert-sm mb-3" role="alert">
              <div class="d-flex align-items-center">
                <i class="fas fa-info-circle me-2"></i>
                <small>
                  <strong>Filtros Inteligentes:</strong> Los filtros avanzados se aplican automáticamente (1 segundo de delay) 
                  sobre los datos en memoria. Los KPIs y gráficos se actualizan dinámicamente.
                </small>
              </div>
            </div>
            
            <!-- Indicador de filtrado en progreso (usa loading global del padre o flag local) -->
            <div v-if="isApplyingAdvancedFilters || loading" class="alert alert-warning alert-sm mb-3" role="alert">
              <div class="d-flex align-items-center">
                <div class="spinner-border spinner-border-sm me-2" role="status">
                  <span class="visually-hidden"></span>
                </div>
                <small>
                  <strong>Aplicando filtros...</strong> Consultando datos actualizados.
                </small>
              </div>
            </div>
            
            <div class="row">
              <!-- Filtros de Texto Específicos -->
              <div class="col-md-6">
                <h6 class="text-muted mb-3">
                  <i class="fas fa-search me-2"></i>
                  Filtros de Búsqueda Local
                </h6>
                <div class="row">
                  <div class="col-md-6 mb-2">
                    <label class="form-label small text-muted mb-1">
                      Nombre Cliente
                      <span class="badge badge-success ms-1" title="Filtro local activo">✓</span>
                    </label>
                    <input 
                      type="text" 
                      class="form-control form-control-sm" 
                      placeholder="Buscar cliente específico..."
                      v-model="filters.clientName"
                      @input="handleAdvancedFilterChange"
                      title="Filtra sobre los datos ya cargados"
                    >
                  </div>
                  <div class="col-md-6 mb-2">
                    <label class="form-label small text-muted mb-1">
                      Nombre Vendedor
                      <span class="badge badge-success ms-1" title="Filtro local activo">✓</span>
                    </label>
                    <input 
                      type="text" 
                      class="form-control form-control-sm" 
                      placeholder="Buscar vendedor específico..."
                      v-model="filters.sellerName"
                      @input="handleAdvancedFilterChange"
                      title="Filtra sobre los datos ya cargados"
                    >
                  </div>
                </div>
                
                <!-- Filtros de Monto -->
                <h6 class="text-muted mb-3 mt-3">
                  <i class="fas fa-dollar-sign me-2"></i>
                  Rango de Montos
                </h6>
                <div class="row">
                  <div class="col-md-6 mb-2">
                    <label class="form-label small text-muted mb-1">
                      Monto desde
                      <span class="badge badge-success ms-1" title="Filtro local activo">✓</span>
                    </label>
                    <input 
                      type="number" 
                      class="form-control form-control-sm" 
                      placeholder="0"
                      v-model="filters.amountFrom"
                      @input="handleAdvancedFilterChange"
                      title="Filtra sobre los datos ya cargados"
                    >
                  </div>
                  <div class="col-md-6 mb-2">
                    <label class="form-label small text-muted mb-1">
                      Monto hasta
                      <span class="badge badge-success ms-1" title="Filtro local activo">✓</span>
                    </label>
                    <input 
                      type="number" 
                      class="form-control form-control-sm" 
                      placeholder="999999"
                      v-model="filters.amountTo"
                      @input="handleAdvancedFilterChange"
                      title="Filtra sobre los datos ya cargados"
                    >
                  </div>
                </div>
                
                <!-- FILTROS ADICIONALES: Estado, Método de Pago, Sucursal -->
                <h6 class="text-muted mb-3 mt-3">
                  <i class="fas fa-filter me-2"></i>
                  Filtros Adicionales
                </h6>
                
                <!-- Estado de Venta -->
                <div class="mb-3">
                  <label class="form-label small text-muted mb-1">
                    Estado de Venta
                    <span class="badge badge-success ms-1" title="Filtro local activo">✓</span>
                  </label>
                  <select 
                    class="form-select form-select-sm" 
                    v-model="filters.advancedStatus"
                    @change="handleAdvancedFilterChange"
                    title="Filtra sobre los datos ya cargados"
                  >
                    <option value="">Todos los estados</option>
                    <option v-for="status in closeStatusCatalog" :key="status" :value="status">
                      {{ status }}
                    </option>
                  </select>
                </div>
                
                <!-- Método de Pago -->
                <div class="mb-3">
                  <label class="form-label small text-muted mb-1">
                    Método de Pago
                    <span class="badge badge-success ms-1" title="Filtro local activo">✓</span>
                  </label>
                  <select 
                    class="form-select form-select-sm" 
                    v-model="filters.advancedPaymentMethod"
                    @change="handleAdvancedFilterChange"
                    title="Filtra sobre los datos ya cargados"
                  >
                    <option value="">Todos los métodos</option>
                    <option v-for="method in paymentMethodsCatalog" :key="method" :value="method">
                      {{ method }}
                    </option>
                  </select>
                </div>
                
                <!-- Sucursal Específica -->
                <div class="mb-3">
                  <label class="form-label small text-muted mb-1">
                    Sucursal Específica
                    <span class="badge badge-success ms-1" title="Filtro local activo">✓</span>
                  </label>
                  <select 
                    class="form-select form-select-sm" 
                    v-model="filters.advancedBranch"
                    @change="handleAdvancedFilterChange"
                    title="Filtra sobre los datos ya cargados"
                  >
                    <option value="">Todas las sucursales</option>
                    <option v-for="branch in branches" :key="branch" :value="branch">
                      {{ branch }}
                    </option>
                  </select>
                </div>
              </div>

              <!-- Filtros de Fecha -->
              <div class="col-md-6">
                <h6 class="text-muted mb-3">
                  <i class="fas fa-calendar-alt me-2"></i>
                  Rango de Fechas
                </h6>
                <div class="row">
                  <div class="col-md-6">
                    <label class="form-label small">Fecha Desde:</label>
                    <input
                      type="date"
                      class="form-control form-control-sm"
                      v-model="filters.dateFrom"
                      :max="filters.dateTo || getTodayDate()"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small">Fecha Hasta:</label>
                    <input
                      type="date"
                      class="form-control form-control-sm"
                      v-model="filters.dateTo"
                      :min="filters.dateFrom"
                      :max="getTodayDate()"
                    />
                  </div>
                </div>
                
                <!-- Filtros Rápidos de Fecha -->
                <h6 class="text-muted mb-3 mt-3">
                  <i class="fas fa-clock me-2"></i>
                  Filtros Rápidos
                </h6>
                <div class="btn-group-vertical w-100" role="group">
                  <button type="button" class="btn btn-outline-primary btn-sm" @click="setDateFilter('today')">
                    <i class="fas fa-calendar-day me-2"></i> Hoy
                  </button>
                  <button type="button" class="btn btn-outline-primary btn-sm" @click="setDateFilter('yesterday')">
                    <i class="fas fa-calendar-minus me-2"></i> Ayer
                  </button>
                  <button type="button" class="btn btn-outline-primary btn-sm" @click="setDateFilter('week')">
                    <i class="fas fa-calendar-week me-2"></i> Esta Semana
                  </button>
                  <button type="button" class="btn btn-outline-primary btn-sm" @click="setDateFilter('month')">
                    <i class="fas fa-calendar me-2"></i> Este Mes
                  </button>
                  <button type="button" class="btn btn-outline-secondary btn-sm" @click="clearDateFilters()">
                    <i class="fas fa-times me-2"></i> Limpiar Fechas
                  </button>
                </div>
              </div>
            </div>

            <!-- Acciones de Filtros Avanzados -->
            <div class="row mt-3">
              <div class="col-12">
                <div class="btn-toolbar justify-content-between" role="toolbar">
                  <div class="btn-group" role="group">
                    <button 
                      type="button" 
                      class="btn btn-outline-warning btn-sm"
                      @click="clearAdvancedFilters"
                      title="Limpiar solo filtros avanzados"
                      v-if="filters.amountFrom || filters.amountTo || filters.clientName || filters.sellerName || filters.advancedStatus || filters.advancedPaymentMethod || filters.advancedBranch"
                    >
                      <i class="fas fa-undo me-2"></i>
                      Limpiar Avanzados
                    </button>
                    <button 
                      type="button" 
                      class="btn btn-danger btn-sm"
                      @click="exportToPDF"
                      :disabled="loading || isExporting || !hasDataForKPIs"
                      title="Exportar KPIs y gráficos a PDF"
                    >
                      <i v-if="isExporting" class="fas fa-spinner fa-spin me-2"></i>
                      <i v-else class="fas fa-file-pdf me-2"></i>
                      {{ isExporting ? 'Exportando KPIs...' : 'Exportar KPIs PDF' }}
                    </button>
                  </div>
                  <div class="btn-group" role="group">
                    <button 
                      type="button" 
                      class="btn btn-primary btn-sm"
                      @click="openSaveFilterModal"
                    >
                      <i class="fas fa-save me-2"></i>
                      Guardar Filtro Actual
                    </button>
                    <button 
                      type="button" 
                      class="btn btn-outline-secondary btn-sm"
                      @click="clearAdvancedFilters"
                      title="Limpiar filtros avanzados y restaurar datos en memoria"
                    >
                      <i class="fas fa-eraser me-2"></i>
                      Limpiar Todos
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 
          3. KPIs/Resumen de Totales (CORREGIDO v2.3)
          ===============================================
          PROBLEMA RESUELTO: Los KPIs ahora usan allFilteredRecords (datos globales) 
          en lugar de records (solo página actual) para mostrar estadísticas completas
          de todos los registros que coinciden con los filtros básicos y avanzados.
        -->
        <div ref="kpisSection" v-if="hasDataForKPIs" class="card mb-3 bg-light">
          <div class="card-body">
            <div class="row text-center">
              <div class="col-lg-3 col-md-6 mb-3 mb-lg-0">
                <div class="d-flex align-items-center justify-content-center">
                  <div class="icon-shape bg-primary text-white rounded-circle me-3">
                    <i class="fas fa-shopping-cart"></i>
                  </div>
                  <div>
                    <h5 class="mb-0 text-primary">{{ totalSales }}</h5>
                    <small class="text-muted">Total Ventas</small>
                  </div>
                </div>
              </div>
              <div class="col-lg-3 col-md-6 mb-3 mb-lg-0">
                <div class="d-flex align-items-center justify-content-center">
                  <div class="icon-shape bg-success text-white rounded-circle me-3">
                    <i class="fas fa-dollar-sign"></i>
                  </div>
                  <div>
                    <h5 class="mb-0 text-success">{{ formatCurrency(totalAmount) }}</h5>
                    <small class="text-muted">Monto Total</small>
                  </div>
                </div>
              </div>
              <div class="col-lg-3 col-md-6 mb-3 mb-md-0">
                <div class="d-flex align-items-center justify-content-center">
                  <div class="icon-shape bg-info text-white rounded-circle me-3">
                    <i class="fas fa-check-circle"></i>
                  </div>
                  <div>
                    <h5 class="mb-0 text-info">{{ validSales }}</h5>
                    <small class="text-muted">Ventas Vigentes</small>
                  </div>
                </div>
              </div>
              <div class="col-lg-3 col-md-6">
                <div class="d-flex align-items-center justify-content-center">
                  <div class="icon-shape bg-danger text-white rounded-circle me-3">
                    <i class="fas fa-times-circle"></i>
                  </div>
                  <div>
                    <h5 class="mb-0 text-danger">{{ cancelledSales }}</h5>
                    <small class="text-muted">Ventas Anuladas</small>
                  </div>
                </div>
              </div>
            </div>
            <!-- Información adicional MEJORADA v2.3 -->
            <div class="row mt-3 pt-3 border-top">
              <div class="col-md-3 text-center">
                <small class="text-muted">
                  <i class="fas fa-chart-line me-1"></i>
                  Promedio por venta: <strong>{{ formatCurrency(averageSaleAmount) }}</strong>
                </small>
              </div>
              <div class="col-md-3 text-center">
                <small class="text-muted">
                  <i class="fas fa-percentage me-1"></i>
                  Tasa de vigencia: <strong>{{ validSalesPercentage }}%</strong>
                </small>
              </div>
              <div class="col-md-3 text-center">
                <small class="text-muted">
                  <i class="fas fa-database me-1"></i>
                  Datos globales: <strong>{{ (allFilteredRecords && allFilteredRecords.length > 0) ? 'Todos los filtrados' : 'Página actual' }}</strong>
                </small>
              </div>
              <div class="col-md-3 text-center">
                <small class="text-muted">
                  <i class="fas fa-clock me-1"></i>
                  Actualizado: <strong>{{ getCurrentDateTime() }}</strong>
                </small>
              </div>
            </div>
            
                         <!-- NUEVO v2.3: Indicador inteligente de alcance de datos -->
              <div v-if="dataStatusInfo.showAlert" class="row mt-3 mb-1">
               <div class="col-12 text-center">
                 <div :class="`alert alert-${dataStatusInfo.type} alert-sm mb-0 py-2`" style="margin-top: 6px;">
                   <i :class="`fas ${dataStatusInfo.type === 'info' ? 'fa-info-circle' : 'fa-exclamation-triangle'} me-2`"></i>
                   <strong>📊 {{ kpiDataSource.isGlobal ? 'KPIs Globales' : 'KPIs de Página' }}:</strong> 
                   {{ dataStatusInfo.message }}
                 </div>
               </div>
             </div>
          </div>
        </div>

        <!-- 4. Panel de Gráficos (Colapsable) -->
        <div ref="chartsSection" v-if="showChartsPanel" class="card mb-3 border-warning">
          <div class="card-header bg-warning text-dark" style="padding: 0.375rem 1rem;">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="mb-0">
                  <i class="fas fa-chart-bar me-2"></i>
                  {{ tituloAnalisisVisual }}
                </h6>
                <!-- NUEVO v2.3: Indicador de datos analizados -->
                <small class="text-muted">
                  <i class="fas fa-database me-1"></i>
                  <strong>{{ getChartDataSourceInfo() }}</strong>
                </small>
              </div>
              <div class="d-flex gap-2">
                <button 
                  type="button" 
                  class="btn btn-sm btn-danger"
                  @click="exportToPDF"
                  title="Exportar KPIs a PDF"
                >
                  <i class="fas fa-file-pdf me-1"></i>
                  EXPORTAR PDF
                </button>
                <button 
                  type="button" 
                  class="btn btn-sm btn-dark"
                  @click="showChartsPanel = false"
                  title="Cerrar panel"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>
          <div class="card-body">
            <!-- FILA 1: Gráficos principales de estado y métodos -->
            <div class="row">
              <div class="col-lg-6 mb-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h6 class="text-muted mb-0">
                    <i class="fas fa-chart-pie me-2 text-success"></i>
                    Ventas por Estado
                  </h6>
                  <button 
                    v-if="chartData.salesByStatus.length > 0"
                    class="btn btn-sm btn-outline-secondary"
                    @click="openChartFullscreen('status', 'Ventas por Estado')"
                    title="Ver en pantalla completa"
                  >
                    <i class="fas fa-expand"></i>
                  </button>
                </div>
                <div 
                  class="chart-container" 
                  style="height: 200px; position: relative;"
                  :class="{ 'no-data': chartData.salesByStatus.length === 0 }"
                >
                  <!-- Indicador de carga del gráfico -->
                  <div v-if="chartLoadingStates.status" class="chart-loading-overlay">
                    <div class="spinner-border text-success" role="status">
                      <span class="visually-hidden">Cargando gráfico...</span>
                    </div>
                    <p class="mt-2 text-muted small">Procesando datos...</p>
                  </div>
                  <canvas ref="statusChart" v-show="chartData.salesByStatus.length > 0 && !chartLoadingStates.status"></canvas>
                </div>
              </div>
              <div class="col-lg-6 mb-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h6 class="text-muted mb-0">
                    <i class="fas fa-chart-bar me-2 text-primary"></i>
                    Ventas por Método de Pago
                  </h6>
                  <button 
                    v-if="chartData.salesByMethod.length > 0"
                    class="btn btn-sm btn-outline-secondary"
                    @click="openChartFullscreen('method', 'Ventas por Método de Pago')"
                    title="Ver en pantalla completa"
                  >
                    <i class="fas fa-expand"></i>
                  </button>
                </div>
                <div 
                  class="chart-container" 
                  style="height: 200px;"
                  :class="{ 'no-data': chartData.salesByMethod.length === 0 }"
                >
                  <canvas ref="methodChart" v-show="chartData.salesByMethod.length > 0"></canvas>
                </div>
              </div>
            </div>

            <!-- FILA 2: Gráficos de sucursales y vendedores -->
            <div class="row">
              <div class="col-lg-6 mb-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h6 class="text-muted mb-0">
                    <i class="fas fa-building me-2 text-info"></i>
                    Ventas por Sucursal
                  </h6>
                  <button 
                    v-if="chartData.salesByBranch.length > 0"
                    class="btn btn-sm btn-outline-secondary"
                    @click="openChartFullscreen('branch', 'Ventas por Sucursal')"
                    title="Ver en pantalla completa"
                  >
                    <i class="fas fa-expand"></i>
                  </button>
                </div>
                <div 
                  class="chart-container" 
                  style="height: 200px;"
                  :class="{ 'no-data': chartData.salesByBranch.length === 0 }"
                >
                  <canvas ref="branchChart" v-show="chartData.salesByBranch.length > 0"></canvas>
                </div>
              </div>
              <div class="col-lg-6 mb-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h6 class="text-muted mb-0">
                    <i class="fas fa-user-tie me-2 text-secondary"></i>
                    Ventas por Vendedor
                  </h6>
                  <button 
                    v-if="chartData.salesBySeller.length > 0"
                    class="btn btn-sm btn-outline-secondary"
                    @click="openChartFullscreen('seller', 'Ventas por Vendedor')"
                    title="Ver en pantalla completa"
                  >
                    <i class="fas fa-expand"></i>
                  </button>
                </div>
                <div 
                  class="chart-container" 
                  style="height: 200px;"
                  :class="{ 'no-data': chartData.salesBySeller.length === 0 }"
                >
                  <canvas ref="sellerChart" v-show="chartData.salesBySeller.length > 0"></canvas>
                </div>
              </div>
            </div>

            <!-- FILA 3: Gráficos de productos y categorías -->
            <div class="row">
              <div class="col-lg-6 mb-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h6 class="text-muted mb-0">
                    <i class="fas fa-tags me-2 text-success"></i>
                    Ventas por Categoría de Producto
                  </h6>
                  <button 
                    v-if="chartData.salesByCategory.length > 0"
                    class="btn btn-sm btn-outline-secondary"
                    @click="openChartFullscreen('category', 'Ventas por Categoría de Producto')"
                    title="Ver en pantalla completa"
                  >
                    <i class="fas fa-expand"></i>
                  </button>
                </div>
                <div 
                  class="chart-container" 
                  style="height: 200px;"
                  :class="{ 'no-data': chartData.salesByCategory.length === 0 }"
                >
                  <canvas ref="categoryChart" v-show="chartData.salesByCategory.length > 0"></canvas>
                </div>
              </div>
              <div class="col-lg-6 mb-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h6 class="text-muted mb-0">
                    <i class="fas fa-dumbbell me-2 text-primary"></i>
                    Ventas por Plan
                  </h6>
                  <button 
                    v-if="chartData.salesByPlanFamily.length > 0"
                    class="btn btn-sm btn-outline-secondary"
                    @click="openChartFullscreen('planFamily', 'Ventas por Plan')"
                    title="Ver en pantalla completa"
                  >
                    <i class="fas fa-expand"></i>
                  </button>
                </div>
                <div 
                  class="chart-container" 
                  style="height: 200px;"
                  :class="{ 'no-data': chartData.salesByPlanFamily.length === 0 }"
                >
                  <canvas ref="planFamilyChart" v-show="chartData.salesByPlanFamily.length > 0"></canvas>
                </div>
              </div>
            </div>

            <!-- FILA 4: Gráficos de tendencias y distribución -->
            <div class="row">
              <div class="col-lg-6 mb-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h6 class="text-muted mb-0">
                    <i class="fas fa-chart-line me-2 text-warning"></i>
                    Tendencia Diaria de Ventas
                  </h6>
                  <button 
                    v-if="chartData.dailyTrend.length > 0"
                    class="btn btn-sm btn-outline-secondary"
                    @click="openChartFullscreen('trend', 'Tendencia Diaria de Ventas')"
                    title="Ver en pantalla completa"
                  >
                    <i class="fas fa-expand"></i>
                  </button>
                </div>
                <div 
                  class="chart-container" 
                  style="height: 200px;"
                  :class="{ 'no-data': chartData.dailyTrend.length === 0 }"
                >
                  <canvas ref="trendChart" v-show="chartData.dailyTrend.length > 0"></canvas>
                </div>
              </div>
              <div class="col-lg-6 mb-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h6 class="text-muted mb-0">
                    <i class="fas fa-dollar-sign me-2 text-danger"></i>
                    Distribución de Montos
                  </h6>
                  <button 
                    v-if="chartData.amountDistribution.length > 0"
                    class="btn btn-sm btn-outline-secondary"
                    @click="openChartFullscreen('amount', 'Distribución de Montos')"
                    title="Ver en pantalla completa"
                  >
                    <i class="fas fa-expand"></i>
                  </button>
                </div>
                <div 
                  class="chart-container" 
                  style="height: 200px;"
                  :class="{ 'no-data': chartData.amountDistribution.length === 0 }"
                >
                  <canvas ref="amountChart" v-show="chartData.amountDistribution.length > 0"></canvas>
                </div>
              </div>
            </div>

            <!-- FILA 5: Gráficos adicionales de análisis -->
            <div class="row">
              <div class="col-lg-6 mb-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h6 class="text-muted mb-0">
                    <i class="fas fa-laptop me-2 text-info"></i>
                    Ventas por Tipo de Plan
                  </h6>
                  <button 
                    v-if="chartData.salesByModality.length > 0"
                    class="btn btn-sm btn-outline-secondary"
                    @click="openChartFullscreen('modality', 'Ventas por Tipo de Plan')"
                    title="Ver en pantalla completa"
                  >
                    <i class="fas fa-expand"></i>
                  </button>
                </div>
                <div 
                  class="chart-container" 
                  style="height: 200px;"
                  :class="{ 'no-data': chartData.salesByModality.length === 0 }"
                >
                  <canvas ref="modalityChart" v-show="chartData.salesByModality.length > 0"></canvas>
                </div>
              </div>
              <div class="col-lg-6 mb-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h6 class="text-muted mb-0">
                    <i class="fas fa-cash-register me-2 text-secondary"></i>
                    Ventas por Caja
                  </h6>
                  <button 
                    v-if="chartData.salesByCashier.length > 0"
                    class="btn btn-sm btn-outline-secondary"
                    @click="openChartFullscreen('cashier', 'Ventas por Caja')"
                    title="Ver en pantalla completa"
                  >
                    <i class="fas fa-expand"></i>
                  </button>
                </div>
                <div 
                  class="chart-container" 
                  style="height: 200px;"
                  :class="{ 'no-data': chartData.salesByCashier.length === 0 }"
                >
                  <canvas ref="cashierChart" v-show="chartData.salesByCashier.length > 0"></canvas>
                </div>
              </div>
              
              <!-- NUEVOS GRÁFICOS DE DESCUENTOS -->
              <div class="col-lg-6 mb-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h6 class="text-muted mb-0">
                    <i class="fas fa-tags me-2 text-warning"></i>
                    Descuentos por Categoría
                  </h6>
                  <button 
                    v-if="chartData.discountsByCategory.length > 0"
                    class="btn btn-sm btn-outline-secondary"
                    @click="openChartFullscreen('discountCategory', 'Descuentos por Categoría')"
                    title="Ver en pantalla completa"
                  >
                    <i class="fas fa-expand"></i>
                  </button>
                </div>
                <div 
                  class="chart-container" 
                  style="height: 200px;"
                  :class="{ 'no-data': chartData.discountsByCategory.length === 0 }"
                >
                  <canvas ref="discountCategoryChart" v-show="chartData.discountsByCategory.length > 0"></canvas>
                </div>
              </div>
              
              <div class="col-lg-6 mb-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h6 class="text-muted mb-0">
                    <i class="fas fa-chart-line me-2 text-warning"></i>
                    Tendencia Diaria de Descuentos
                  </h6>
                  <button 
                    v-if="chartData.discountsDailyTrend.length > 0"
                    class="btn btn-sm btn-outline-secondary"
                    @click="openChartFullscreen('discountTrend', 'Tendencia Diaria de Descuentos')"
                    title="Ver en pantalla completa"
                  >
                    <i class="fas fa-expand"></i>
                  </button>
                </div>
                <div 
                  class="chart-container" 
                  style="height: 200px;"
                  :class="{ 'no-data': chartData.discountsDailyTrend.length === 0 }"
                >
                  <canvas ref="discountTrendChart" v-show="chartData.discountsDailyTrend.length > 0"></canvas>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 5. Panel de Filtros Guardados v2.2 (Mejorado) -->
        <div v-if="showSavedFilters" class="card mb-3 border-primary">
          <div class="card-header bg-primary text-white" style="padding: 0.375rem 1rem;">
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="mb-0">
                <i class="fas fa-bookmark me-2"></i>
                Filtros Guardados - Ventas Gimnasio
              </h6>
              <div class="d-flex">
                <button 
                  type="button" 
                  class="btn btn-sm btn-light me-2"
                  @click="openSaveFilterModal"
                  title="Guardar filtro actual"
                >
                  <i class="fas fa-save me-1"></i>
                  Guardar Actual
                </button>
                <button 
                  type="button" 
                  class="btn btn-sm btn-light"
                  @click="showSavedFilters = false"
                  title="Cerrar panel"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>
          <div class="card-body">
            <div v-if="sortedSavedFilters.length === 0" class="text-center py-5">
              <div class="empty-state">
                <i class="fas fa-bookmark fa-3x mb-3 text-muted opacity-50"></i>
                <h5 class="text-muted mb-2">📁 No hay filtros guardados</h5>
                <p class="text-muted mb-3">
                  ¡Optimiza tu trabajo guardando configuraciones de filtros frecuentes!
                </p>
                <div class="alert alert-info mx-auto" style="max-width: 500px;">
                  <h6 class="alert-heading">💡 ¿Cómo usar los filtros guardados?</h6>
                  <ol class="mb-0 text-left small">
                    <li>Configura tus filtros favoritos (fechas, estado, método, etc.)</li>
                    <li>Haz clic en <span class="badge bg-primary">Guardar Actual</span></li>
                    <li>Dale un nombre descriptivo a tu filtro</li>
                    <li>Úsalo cuando lo necesites con un solo clic</li>
                  </ol>
                </div>
                <button 
                  type="button" 
                  class="btn btn-primary btn-sm"
                  @click="openSaveFilterModal"
                >
                  <i class="fas fa-plus me-2"></i>
                  Crear Mi Primer Filtro
                </button>
              </div>
            </div>
            <div v-else class="row">
              <div 
                v-for="filter in sortedSavedFilters" 
                :key="filter.id" 
                class="col-lg-4 col-md-6 col-xl-3 mb-3"
              >
                <div class="card border-light h-100 saved-filter-card">
                  <div class="card-header bg-light" style="padding: 0.375rem 0.75rem;">
                    <div class="d-flex justify-content-between align-items-center">
                      <h6 class="card-title mb-0 text-truncate fw-bold" :title="filter.name">
                        <i class="fas fa-bookmark me-2 text-primary"></i>
                        {{ filter.name }}
                      </h6>
                      <span class="badge bg-secondary">{{ getFilterSummary(filter) }}</span>
                    </div>
                  </div>
                  <div class="card-body p-3">
                    <p class="card-text text-muted small mb-2" :title="filter.description">
                      {{ filter.description || 'Sin descripción' }}
                    </p>
                    
                    <!-- Resumen de filtros aplicados -->
                    <div class="filter-tags mb-3">
                      <span v-if="filter.filters.status" class="badge bg-info me-1 mb-1">
                        Estado: {{ filter.filters.status }}
                      </span>
                      <span v-if="filter.filters.paymentMethod" class="badge bg-success me-1 mb-1">
                        Método: {{ filter.filters.paymentMethod }}
                      </span>
                      <span v-if="filter.filters.branch" class="badge bg-warning text-dark me-1 mb-1">
                        Sucursal: {{ getBranchName(filter.filters.branch) }}
                      </span>
                      <span v-if="filter.filters.dateFrom && filter.filters.dateTo" class="badge bg-secondary me-1 mb-1">
                        📅 {{ filter.filters.dateFrom }} - {{ filter.filters.dateTo }}
                      </span>
                      <span v-if="filter.filters.searchText" class="badge bg-primary me-1 mb-1">
                        🔍 "{{ filter.filters.searchText }}"
                      </span>
                      <span v-if="filter.filters.clientName" class="badge bg-info me-1 mb-1">
                        👤 {{ filter.filters.clientName }}
                      </span>
                      <span v-if="filter.filters.sellerName" class="badge bg-success me-1 mb-1">
                        👨‍💼 {{ filter.filters.sellerName }}
                      </span>
                      <span v-if="filter.filters.amountFrom || filter.filters.amountTo" class="badge bg-dark text-white me-1 mb-1">
                        💰 {{ formatAmountRange(filter.filters.amountFrom, filter.filters.amountTo) }}
                      </span>
                      <span v-if="filter.filters.advancedStatus" class="badge bg-warning text-dark me-1 mb-1">
                        📊 {{ filter.filters.advancedStatus }}
                      </span>
                      <span v-if="filter.filters.advancedPaymentMethod" class="badge bg-purple text-white me-1 mb-1">
                        💳 {{ filter.filters.advancedPaymentMethod }}
                      </span>
                      <span v-if="filter.filters.advancedBranch" class="badge bg-secondary me-1 mb-1">
                        🏢 {{ filter.filters.advancedBranch }}
                      </span>
                    </div>
                    
                    <!-- Información temporal -->
                    <div class="text-muted small mb-3">
                      <i class="fas fa-calendar me-1"></i>
                      Creado: {{ formatDate(filter.createdAt) }}
                      <br>
                      <i class="fas fa-sort me-1"></i>
                      Orden: {{ filter.sorting.field }} ({{ filter.sorting.direction === 'asc' ? '↑' : '↓' }})
                    </div>
                    
                    <!-- Botones de acción -->
                    <div class="d-grid gap-2">
                      <!-- Botón principal: Aplicar/Activar filtro -->
                      <button 
                        type="button" 
                        class="btn btn-primary btn-sm"
                        @click="applySavedFilter(filter)"
                        title="Aplicar este filtro"
                      >
                        <i class="fas fa-play me-2"></i>
                        Aplicar Filtro
                      </button>
                      
                      <!-- Botones secundarios -->
                      <div class="btn-group" role="group">
                        <button 
                          type="button" 
                          class="btn btn-outline-info btn-sm"
                          @click="editSavedFilter(filter)"
                          title="Editar filtro"
                        >
                          <i class="fas fa-edit me-1"></i>
                          Editar
                        </button>
                        <button 
                          type="button" 
                          class="btn btn-outline-success btn-sm"
                          @click="duplicateSavedFilter(filter)"
                          title="Duplicar filtro"
                        >
                          <i class="fas fa-copy me-1"></i>
                          Copiar
                        </button>
                        <button 
                          type="button" 
                          class="btn btn-outline-danger btn-sm"
                          @click="deleteSavedFilter(filter)"
                          title="Eliminar filtro"
                        >
                          <i class="fas fa-trash me-1"></i>
                          Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Indicador de búsqueda -->
        <div v-if="isSearching" class="alert alert-info d-flex align-items-center mb-3">
          <div class="spinner-border spinner-border-sm me-2" role="status">
            <span class="visually-hidden"></span>
          </div>
          <span>Buscando ventas...</span>
        </div>

        <!-- 6. Tabla de Ventas -->
        <div class="table-responsive position-relative">
          <!-- Overlay de carga -->
          <div 
            v-if="loading" 
            class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
            style="z-index: 10;"
          >
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden"></span>
            </div>
          </div>

          <table class="table" :class="{ 'opacity-50': loading }">
            <thead>
              <tr>
                <th width="50">
                  <div class="form-check">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      :checked="allSelected"
                      @change="toggleSelectAll"
                      :disabled="loading || !records || records.length === 0"
                      title="Seleccionar/Deseleccionar todo"
                    >
                  </div>
                </th>
                <th 
                  class="sortable-header" 
                  @click="sortBy('idTransaccion')"
                  :class="getSortClass('idTransaccion')"
                  title="Ordenar por ID"
                >
                  ID
                  <i class="fas ms-1" :class="getSortIcon('idTransaccion')"></i>
                </th>
                <th 
                  class="sortable-header" 
                  @click="sortBy('fechaHora')"
                  :class="getSortClass('fechaHora')"
                  title="Ordenar por Fecha"
                >
                  Fecha
                  <i class="fas ms-1" :class="getSortIcon('fechaHora')"></i>
                </th>
                <th 
                  class="sortable-header" 
                  @click="sortBy('nombreSucursal')"
                  :class="getSortClass('nombreSucursal')"
                  title="Ordenar por Sucursal"
                >
                  Sucursal
                  <i class="fas ms-1" :class="getSortIcon('nombreSucursal')"></i>
                </th>
                <th class="caja-column">Caja</th>
                <th 
                  class="sortable-header" 
                  @click="sortBy('nombreCliente')"
                  :class="getSortClass('nombreCliente')"
                  title="Ordenar por Cliente"
                >
                  Cliente
                  <i class="fas ms-1" :class="getSortIcon('nombreCliente')"></i>
                </th>
                <th 
                  class="sortable-header" 
                  @click="sortBy('nombreVendedor')"
                  :class="getSortClass('nombreVendedor')"
                  title="Ordenar por Vendedor"
                >
                  Vendedor
                  <i class="fas ms-1" :class="getSortIcon('nombreVendedor')"></i>
                </th>
                <th 
                  class="sortable-header" 
                  @click="sortBy('metodoPago')"
                  :class="getSortClass('metodoPago')"
                  title="Ordenar por Método de Pago"
                >
                  Método
                  <i class="fas ms-1" :class="getSortIcon('metodoPago')"></i>
                </th>
                <th 
                  class="sortable-header" 
                  @click="sortBy('ventaTotal')"
                  :class="getSortClass('ventaTotal')"
                  title="Ordenar por Total"
                >
                  Total
                  <i class="fas ms-1" :class="getSortIcon('ventaTotal')"></i>
                </th>
                <th 
                  class="sortable-header" 
                  @click="sortBy('estadoVenta')"
                  :class="getSortClass('estadoVenta')"
                  title="Ordenar por Estado"
                >
                  Estado
                  <i class="fas ms-1" :class="getSortIcon('estadoVenta')"></i>
                </th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <!-- Mensaje cuando no hay registros -->
              <tr v-if="!loading && (!records || records.length === 0)">
                <td colspan="11" class="text-center py-4 text-muted">
                  <i class="fas fa-search fa-2x mb-2"></i>
                  <p class="mb-0">No se encontraron ventas con los filtros aplicados</p>
                </td>
              </tr>
              
              <!-- Registros de ventas -->
              <tr 
                v-for="sale in records" 
                :key="sale._id.$oid"
                :class="{ 'table-primary': selectedItems.includes(sale._id.$oid) }"
              >
                <td>
                  <div class="form-check">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      :value="sale._id.$oid"
                      v-model="selectedItems"
                      :disabled="loading"
                    >
                  </div>
                </td>
                <td>{{ sale.idTransaccion }}</td>
                <td>{{ formatDate(sale.fechaHora) }}</td>
                <td>{{ sale.nombreSucursal }}</td>
                <td class="caja-column">{{ sale.cajaPuntoDeVenta }}</td>
                <td>{{ sale.nombreCliente }}</td>
                <td>{{ sale.nombreVendedor }}</td>
                <td>{{ sale.metodoPago }}</td>
                <td>{{ formatCurrency(sale.ventaTotal) }}</td>
                <td>
                  <span :class="getStatusBadgeClass(sale.estadoVenta)">
                    {{ getStatusText(sale.estadoVenta) }}
                  </span>
                </td>
                <td>
                  <div class="btn-group" role="group">
                    <button 
                      class="btn btn-sm btn-info me-2"
                      @click="showDetails(sale)"
                      title="Ver detalles completos de la venta"
                      :disabled="loading"
                    >
                      <i class="fas fa-search-plus"></i>
                    </button>
                    <button 
                      v-if="sale.estadoVenta === '' || sale.estadoVenta === null || sale.estadoVenta === undefined"
                      class="btn btn-sm btn-danger"
                      @click="confirmAnulacion(sale)"
                      title="Anular venta"
                      :disabled="loading || isAnulling"
                    >
                      <i v-if="isAnulling" class="fas fa-spinner fa-spin"></i>
                      <i v-else class="fas fa-times-circle"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginación -->
        <div v-if="records && records.length > 0" class="row mt-3">
          <div class="col-12">
            <!-- Contenedor de paginación visible -->
            <div class="pagination-container bg-light border rounded p-3 mb-3">
              <h6 class="text-muted mb-3">
                <i class="fas fa-list-ol me-2"></i>
                Controles de Paginación
              </h6>
              <!-- Información de registros y elementos por página -->
              <div class="d-flex justify-content-between align-items-center mb-3">
                <div class="d-flex align-items-center">
                  <small class="text-muted me-3">{{ recordsInfo }}</small>
                  <div class="d-flex align-items-center">
                    <label class="form-label small text-muted mb-0 me-2">Mostrar:</label>
                    <select 
                      class="form-select form-select-sm" 
                      style="width: auto;"
                      v-model="pagination.itemsPerPage" 
                      @change="changeItemsPerPage($event.target.value)"
                      :disabled="loading"
                    >
                      <option :value="20">20</option>
                      <option :value="50">50</option>
                      <option :value="100">100</option>
                      <option :value="200">200</option>
                    </select>
                    <small class="text-muted ms-2">por página</small>
                  </div>
                </div>
                
                <!-- Información adicional -->
                <div class="text-end">
                  <small class="text-muted">
                    Página {{ pagination.currentPage }} de {{ totalPages || 1 }}
                  </small>
                </div>
              </div>

              <!-- Spinner de carga justo sobre el selector de páginas (solo ícono) -->
              <div v-if="loading" class="d-flex justify-content-center mb-2">
                <div class="spinner-border spinner-border-sm" role="status" aria-label="Cargando"></div>
              </div>
              
              <!-- Controles de navegación (solo si hay más de una página) -->
              <nav aria-label="Paginación de ventas" v-if="totalPages > 1">
                <ul class="pagination justify-content-center mb-0">
                  <!-- Primera página -->
                  <li class="page-item" :class="{ disabled: pagination.currentPage === 1 || loading }">
                    <button 
                      class="page-link" 
                      @click="goToFirstPage"
                      :disabled="pagination.currentPage === 1 || loading"
                      title="Primera página"
                    >
                      <i class="fas fa-angle-double-left"></i>
                    </button>
                  </li>
                  
                  <!-- Página anterior -->
                  <li class="page-item" :class="{ disabled: pagination.currentPage === 1 || loading }">
                    <button 
                      class="page-link" 
                      @click="goToPreviousPage"
                      :disabled="pagination.currentPage === 1 || loading"
                      title="Página anterior"
                    >
                      <i class="fas fa-angle-left"></i>
                    </button>
                  </li>
                  
                  <!-- Páginas numeradas -->
                  <li 
                    v-for="page in availablePages" 
                    :key="page" 
                    class="page-item" 
                    :class="{ active: page === pagination.currentPage, disabled: loading }"
                  >
                    <button 
                      class="page-link" 
                      @click="changePage(page)"
                      :disabled="loading"
                    >
                      {{ page }}
                    </button>
                  </li>
                  
                  <!-- Página siguiente -->
                  <li class="page-item" :class="{ disabled: pagination.currentPage === totalPages || loading }">
                    <button 
                      class="page-link" 
                      @click="goToNextPage"
                      :disabled="pagination.currentPage === totalPages || loading"
                      title="Página siguiente"
                    >
                      <i class="fas fa-angle-right"></i>
                    </button>
                  </li>
                  
                  <!-- Última página -->
                  <li class="page-item" :class="{ disabled: pagination.currentPage === totalPages || loading }">
                    <button 
                      class="page-link" 
                      @click="goToLastPage"
                      :disabled="pagination.currentPage === totalPages || loading"
                      title="Última página"
                    >
                      <i class="fas fa-angle-double-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
        
        <!-- Indicador cuando no hay registros -->
        <div v-else class="row mt-3">
          <div class="col-12">
            <div class="alert alert-info">
              <h6><i class="fas fa-info-circle me-2"></i>Estado de Paginación</h6>
              <p class="mb-0">
                📊 <strong>Registros actuales:</strong> {{ records ? records.length : 0 }}<br>
                📈 <strong>Total registros:</strong> {{ totalRecords }}<br>
                📄 <strong>Estado:</strong> {{ records && records.length > 0 ? 'Con datos' : 'Sin datos para mostrar' }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 8. Acciones en Lote (Solo si hay elementos seleccionados) -->
    <div v-if="selectedItemsCount > 0" class="card mt-3">
      <div class="card-body">
        <div class="alert alert-primary mb-0">
          <div class="row align-items-center">
            <div class="col-md-6">
              <div class="d-flex align-items-center">
                <div class="me-3">
                  <i class="fas fa-check-square fa-lg text-primary"></i>
                </div>
                <div>
                  <strong>{{ selectedItemsCount }} elemento(s) seleccionado(s)</strong>
                  <br>
                  <small class="text-muted">
                    {{ selectedValidSales }} vigente(s) • Total: {{ formatCurrency(selectedTotalAmount) }}
                  </small>
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="btn-group w-100" role="group">
                <button 
                  type="button" 
                  class="btn btn-danger"
                  @click="confirmBulkCancel"
                  :disabled="!canBulkCancel || isBulkProcessing"
                >
                  <i v-if="isBulkProcessing" class="fas fa-spinner fa-spin me-2"></i>
                  <i v-else class="fas fa-times-circle me-2"></i>
                  {{ isBulkProcessing ? 'Anulando...' : `Anular ${selectedValidSales}` }}
                </button>
                <button 
                  type="button" 
                  class="btn btn-success"
                  @click="exportSelectedToExcel"
                  :disabled="isBulkProcessing"
                >
                  <i class="fas fa-file-excel me-2"></i>
                  Exportar Selección
                </button>
                <button 
                  type="button" 
                  class="btn btn-outline-secondary"
                  @click="clearSelection"
                >
                  <i class="fas fa-times me-2"></i>
                  Limpiar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Detalles Completos de Venta -->
    <SaleDetailModal
      :sale="selectedSale"
      :visible="showSaleDetailModal"
      @close="closeSaleDetailModal"
    />

    <!-- Modal para Guardar Filtro -->
    <div class="modal fade" 
         id="saveFilterModal" 
         tabindex="-1" 
         role="dialog"
         aria-labelledby="saveFilterModalLabel" 
         aria-hidden="true"
         data-bs-backdrop="static"
         style="z-index: 9045 !important;">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
                  <div class="modal-header">
          <h5 class="modal-title" id="saveFilterModalLabel">
            <i :class="isEditingFilter ? 'fas fa-edit me-2' : 'fas fa-save me-2'"></i>
            {{ isEditingFilter ? 'Editar Filtro Guardado' : 'Guardar Configuración de Filtros' }} - Ventas Gimnasio
          </h5>
          <button type="button" class="btn-close" @click="closeSaveFilterModal"></button>
        </div>
          <div class="modal-body">
            <form @submit.prevent="saveCurrentFilter">
              <div class="mb-3">
                <label for="filterName" class="form-label">Nombre del Filtro *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  id="filterName"
                  v-model="filterToSave.name"
                  placeholder="Ej: Ventas del mes actual"
                  required
                  maxlength="50"
                >
                <div class="form-text">Máximo 50 caracteres</div>
              </div>
              <div class="mb-3">
                <label for="filterDescription" class="form-label">Descripción</label>
                <textarea 
                  class="form-control" 
                  id="filterDescription"
                  v-model="filterToSave.description"
                  placeholder="Descripción opcional del filtro..."
                  rows="3"
                  maxlength="200"
                ></textarea>
                <div class="form-text">Máximo 200 caracteres</div>
              </div>
              <div class="alert alert-info">
                <h6 class="alert-heading">
                  <i class="fas fa-info-circle me-2"></i>
                  Configuración a Guardar:
                </h6>
                <ul class="mb-0 small">
                  <li v-if="filters.searchText">Texto General: "{{ filters.searchText }}"</li>
                  <li v-if="filters.clientName">Cliente: "{{ filters.clientName }}"</li>
                  <li v-if="filters.sellerName">Vendedor: "{{ filters.sellerName }}"</li>
                  <li v-if="filters.branch">Sucursal: {{ getBranchName(filters.branch) }}</li>
                  <li v-if="filters.status">Estado: {{ filters.status }}</li>
                  <li v-if="filters.paymentMethod">Método: {{ filters.paymentMethod }}</li>
                  <li v-if="filters.dateFrom">Desde: {{ filters.dateFrom }}</li>
                  <li v-if="filters.dateTo">Hasta: {{ filters.dateTo }}</li>
                  <li v-if="filters.amountFrom">Monto desde: {{ formatCurrency(filters.amountFrom) }}</li>
                  <li v-if="filters.amountTo">Monto hasta: {{ formatCurrency(filters.amountTo) }}</li>
                  <li v-if="filters.advancedStatus">Estado Avanzado: {{ filters.advancedStatus }}</li>
                  <li v-if="filters.advancedPaymentMethod">Método Avanzado: {{ filters.advancedPaymentMethod }}</li>
                  <li v-if="filters.advancedBranch">Sucursal Avanzada: {{ filters.advancedBranch }}</li>
                  <li>Ordenamiento: {{ sorting.field }} ({{ sorting.direction }})</li>
                </ul>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeSaveFilterModal">
              Cancelar
            </button>
            <button type="button" class="btn btn-primary" @click="saveCurrentFilter">
              <i :class="isEditingFilter ? 'fas fa-edit me-2' : 'fas fa-save me-2'"></i>
              {{ isEditingFilter ? 'Actualizar Filtro' : 'Guardar Filtro' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal de Gráfico en Pantalla Completa -->
    <div 
      v-if="fullscreenChart.show" 
      class="modal fade show d-block" 
      tabindex="-1" 
      role="dialog"
      style="background-color: rgba(0, 0, 0, 0.5); overflow-y: auto;"
      @click.self="closeFullscreenChart"
    >
      <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document" style="max-height: 90vh;">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i :class="getChartIcon(fullscreenChart.type)" class="me-3"></i>
              {{ fullscreenChart.title }}
            </h5>
            <button 
              type="button" 
              class="btn btn-danger btn-sm" 
              @click="closeFullscreenChart"
              style="position: absolute; right: 10px; top: 10px; z-index: 1000;"
              title="Cerrar"
            >
              <i class="fas fa-times"></i> Cerrar
            </button>
          </div>
          <div class="modal-body" style="max-height: calc(90vh - 120px); overflow-y: auto;">
            <!-- Canvas del gráfico ampliado -->
            <div class="chart-container mb-4" style="height: 400px; position: relative;">
              <canvas ref="fullscreenChartCanvas"></canvas>
            </div>
            
            <!-- Tabla de datos del gráfico -->
            <div class="table-responsive">
              <h6 class="mb-3">
                <i class="fas fa-table me-2"></i>
                Datos del Gráfico
              </h6>
              <!-- Descripciones para cada tipo de gráfico -->
              <p v-if="fullscreenChart.type === 'status'" class="text-muted small mb-3">
                <i class="fas fa-info-circle me-1"></i>
                Muestra la distribución de ventas según su estado (Vigente o Anulada). Las ventas vigentes son aquellas que no han sido anuladas en el punto de venta. 
                El porcentaje indica la participación del monto recaudado de cada estado respecto al total.
              </p>
              <p v-else-if="fullscreenChart.type === 'method'" class="text-muted small mb-3">
                <i class="fas fa-info-circle me-1"></i>
                Muestra las ventas agrupadas por método de pago utilizado. La cantidad indica el número de transacciones realizadas con cada método. 
                El porcentaje representa la participación del monto recaudado por cada método respecto al total.
              </p>
              <p v-else-if="fullscreenChart.type === 'branch'" class="text-muted small mb-3">
                <i class="fas fa-info-circle me-1"></i>
                Muestra el volumen de ventas realizadas en cada sucursal. 
                El porcentaje indica la participación del monto recaudado por cada sucursal respecto al total del período.
              </p>
              <p v-else-if="fullscreenChart.type === 'seller'" class="text-muted small mb-3">
                <i class="fas fa-info-circle me-1"></i>
                Muestra el desempeño de cada vendedor, incluyendo la cantidad de ventas realizadas y el monto total generado. 
                El porcentaje indica la participación del monto recaudado por cada vendedor respecto al total.
              </p>
              <p v-else-if="fullscreenChart.type === 'category'" class="text-muted small mb-3">
                <i class="fas fa-info-circle me-1"></i>
                Muestra las ventas agrupadas por categoría de producto. El total puede no coincidir con el total general del período, 
                ya que se enfoca únicamente en productos categorizados. El porcentaje indica la participación del monto de cada categoría.
              </p>
              <p v-else-if="fullscreenChart.type === 'planFamily'" class="text-muted small mb-3">
                <i class="fas fa-info-circle me-1"></i>
                Muestra únicamente las ventas que incluyen planes/membresías, agrupadas por tipo de plan. No representa el total general de ventas del período. 
                El porcentaje indica la participación del monto recaudado por cada plan respecto al total de planes vendidos.
              </p>
              <p v-else-if="fullscreenChart.type === 'trend'" class="text-muted small mb-3">
                <i class="fas fa-info-circle me-1"></i>
                Muestra la evolución diaria de las ventas durante el período seleccionado, incluyendo cantidad de transacciones y monto total por día. 
                El porcentaje indica la participación de cada día respecto al total del período.
              </p>
              <p v-else-if="fullscreenChart.type === 'amount'" class="text-muted small mb-3">
                <i class="fas fa-info-circle me-1"></i>
                El porcentaje representa la participación de cada rango respecto al monto total de ventas, 
                permitiendo identificar qué rangos de transacciones contribuyen más al ingreso total.
              </p>
              <p v-else-if="fullscreenChart.type === 'modality'" class="text-muted small mb-3">
                <i class="fas fa-info-circle me-1"></i>
                Muestra las ventas agrupadas por tipo de plan, incluyendo "Otras Ventas sin Membresías" para transacciones que no contienen planes. 
                El porcentaje representa la participación del monto recaudado por cada tipo respecto al total de ventas mostradas.
              </p>
              <p v-else-if="fullscreenChart.type === 'cashier'" class="text-muted small mb-3">
                <i class="fas fa-info-circle me-1"></i>
                Muestra las ventas agrupadas por caja/punto de venta, incluyendo el monto total registrado en cada caja. 
                El porcentaje representa la participación de cada caja respecto al total recaudado.
              </p>
              <p v-else-if="fullscreenChart.type === 'discountCategory'" class="text-muted small mb-3">
                <i class="fas fa-info-circle me-1"></i>
                Muestra los descuentos agrupados por categoría de producto. La cantidad indica el número de ventas que incluyen descuentos en esa categoría. 
                El total de descuentos representa el valor monetario total descontado. El porcentaje indica la participación de cada categoría respecto al total de descuentos aplicados.
              </p>
              <p v-else-if="fullscreenChart.type === 'discountTrend'" class="text-muted small mb-3">
                <i class="fas fa-info-circle me-1"></i>
                Muestra la evolución diaria de los descuentos aplicados durante el período. 
                El porcentaje representa la proporción de descuentos de cada día respecto al total de descuentos del período.
              </p>
              <table class="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>{{ fullscreenChart.labelColumn || 'Categoría' }}</th>
                    <th class="text-end" v-if="fullscreenChart.type === 'amount'">Cantidad de Ventas</th>
                    <th class="text-end" v-else-if="fullscreenChart.data.some(d => d.hasAmount && d.count !== undefined)">Cantidad</th>
                    <th class="text-end" v-if="fullscreenChart.type === 'amount'">Monto Total del Rango</th>
                    <th class="text-end" v-else>{{ fullscreenChart.valueColumn || 'Valor' }}</th>
                    <th class="text-end">Porcentaje</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in fullscreenChart.data" :key="index">
                    <td>
                      <span 
                        v-if="fullscreenChart.showColorIndicator" 
                        class="badge me-2" 
                        :style="{ backgroundColor: item.color }"
                      >&nbsp;&nbsp;</span>
                      {{ fullscreenChart.type === 'trend' || fullscreenChart.type === 'discountTrend' ? formatDateForDisplay(item.label) : item.label }}
                    </td>
                    <!-- Columna de cantidad para distribución de montos -->
                    <td class="text-end" v-if="fullscreenChart.type === 'amount'">
                      {{ new Intl.NumberFormat('es-CL').format(item.value || 0) }}
                    </td>
                    <!-- Columna de cantidad para otros gráficos -->
                    <td class="text-end" v-else-if="fullscreenChart.data.some(d => d.hasAmount && d.count !== undefined)">
                      {{ new Intl.NumberFormat('es-CL').format(item.count || 0) }}
                    </td>
                    <!-- Columna de monto total del rango para distribución de montos -->
                    <td class="text-end" v-if="fullscreenChart.type === 'amount'">
                      {{ formatCurrencyForCharts(item.amount || 0) }}
                    </td>
                    <!-- Columna de valor para otros gráficos -->
                    <td class="text-end" v-else>
                      {{ formatChartValue(item.value, fullscreenChart.type) }}
                    </td>
                    <!-- Porcentaje -->
                    <td class="text-end">
                      {{ fullscreenChart.type === 'amount' ? calculateAmountPercentage(item.amount, fullscreenChart.data) : calculatePercentage(item.value, fullscreenChart.data) }}%
                    </td>
                  </tr>
                </tbody>
                <tfoot class="table-secondary">
                  <tr>
                    <th>Total</th>
                    <!-- Total de cantidad para distribución de montos -->
                    <th class="text-end" v-if="fullscreenChart.type === 'amount'">
                      {{ new Intl.NumberFormat('es-CL').format(fullscreenChart.data.reduce((sum, item) => sum + (item.value || 0), 0)) }}
                    </th>
                    <!-- Total de cantidad para otros gráficos -->
                    <th class="text-end" v-else-if="fullscreenChart.data.some(d => d.hasAmount && d.count !== undefined)">
                      {{ new Intl.NumberFormat('es-CL').format(fullscreenChart.data.reduce((sum, item) => sum + (item.count || 0), 0)) }}
                    </th>
                    <!-- Total de monto para distribución de montos -->
                    <th class="text-end" v-if="fullscreenChart.type === 'amount'">
                      {{ formatCurrencyForCharts(fullscreenChart.data.reduce((sum, item) => sum + (item.amount || 0), 0)) }}
                    </th>
                    <!-- Total de valor para otros gráficos -->
                    <th class="text-end" v-else>
                      {{ formatChartValue(calculateTotal(fullscreenChart.data), fullscreenChart.type) }}
                    </th>
                    <th class="text-end">100%</th>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-danger" @click="exportFullscreenChartToPDF">
              <i class="fas fa-file-pdf me-2"></i>
              Descargar en PDF
            </button>
            <button type="button" class="btn btn-primary" @click="exportFullscreenChart">
              <i class="fas fa-download me-2"></i>
              Descargar como imagen
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from 'bootstrap';
import Chart from 'chart.js';
import SaleDetailModal from './SaleDetailModal.vue';
import chartMixin from '@/mixins/chartMixin';
import excelExportMixin from '@/mixins/excelExportMixin';

// Función debounce para optimizar búsquedas
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default {
  name: 'GymSalesTable',
  components: {
    SaleDetailModal
  },
  mixins: [chartMixin, excelExportMixin],
  props: {
    branches: {
      type: Array,
      default: () => [],
      validator: (value) => Array.isArray(value),
      // ACTUALIZADO v2.3: Ahora es array de nombres directos de sucursales
    },
    statusCatalog: {
      type: Array,
      default: () => ['[VIGENTE]', '[ANULADA]'],
      validator: (value) => Array.isArray(value),
      // Mantener por compatibilidad (estadosCierreVenta)
    },
    trxTypeCatalog: {
      type: Array,
      default: () => [],
      validator: (value) => Array.isArray(value),
      // ACTUALIZADO v2.3: Mapea a paymentMethodsCatalog (estadosMediosPago)
    },
    paymentMethodsCatalog: {
      type: Array,
      default: () => [],
      validator: (value) => Array.isArray(value),
      // NUEVO v2.3: estadosMediosPago
    },
    saleStatusCatalog: {
      type: Array,
      default: () => [],
      validator: (value) => Array.isArray(value),
      // NUEVO v2.3: estadosVenta ([CERRADA], [ABIERTA])
    },
    closeStatusCatalog: {
      type: Array,
      default: () => [],
      validator: (value) => Array.isArray(value),
      // NUEVO v2.3: estadosCierreVenta ([VIGENTE], [ANULADA])
    },
    records: {
      type: Array,
      default: () => [],
      validator: (value) => Array.isArray(value)
    },
    loading: {
      type: Boolean,
      default: false
    },
    totalRecords: {
      type: Number,
      default: 0
    },
    // NUEVO v2.3: Props para paginación local
    allFilteredRecords: {
      type: Array,
      default: () => []
    },
    originalFilteredRecords: {
      type: Array,
      default: () => []
    },
    localPagination: {
      type: Object,
      default: () => ({
        currentPage: 1,
        itemsPerPage: 20,
        totalPages: 1
      })
    }
  },
  data() {
    return {
      filters: {
        searchText: '',
        branch: '',
        status: '',
        paymentMethod: '',
        dateFrom: '',
        dateTo: '',
        // Filtros avanzados locales
        amountFrom: '',
        amountTo: '',
        clientName: '',
        sellerName: '',
        cashier: '',
        planType: '',
        // NUEVOS FILTROS AVANZADOS DINÁMICOS
        advancedStatus: '',
        advancedPaymentMethod: '',
        advancedBranch: ''
      },
      detalleModal: null,
      canFetchMore: true,
      // Estados de carga
      isSearching: false,
      isAnulling: false,
      isExporting: false,
      isBulkProcessing: false,
      isApplyingAdvancedFilters: false,
      // Estados del modal de detalles
      showSaleDetailModal: false,
      selectedSale: null,
      // Sistema de paginación LOCAL v2.2
      pagination: {
        currentPage: 1,
        totalPages: 0,
        itemsPerPage: 20,
        totalRecords: 0
      },
      // NOTA: Las siguientes propiedades se reciben como props del componente padre:
      // - allFilteredRecords: Array con todos los registros filtrados
      // - originalFilteredRecords: Array con datos originales sin filtros avanzados
      // - localPagination: Objeto con configuración de paginación local
      // Sistema de ordenamiento (CORREGIDO v2.5.2 - ID de mayor a menor para ventas más recientes)
      sorting: {
        field: 'idTransaccion',
        direction: 'desc'
      },
      // UI States
      showAdvancedFilters: false,
      showChartsPanel: false,
      showSavedFilters: false,
      showMobileFilters: false,
      // Selección múltiple
      selectedItems: [],
      allSelected: false,
      bulkSelectMode: false,
      // Filtros guardados mejorados
      savedFilters: [],
      filterToSave: {
        name: '',
        description: ''
      },
      filterToEdit: null, // Filtro que se está editando
      isEditingFilter: false, // Bandera para saber si estamos editando
      showSaveFilterModal: false,
      // Gráficos (ACTUALIZADOS v2.3 - Sistema expandido para usar allFilteredRecords)
      chartData: {
        // Gráficos existentes
        salesByStatus: [],
        salesByMethod: [],
        salesByBranch: [],
        dailyTrend: [],
        // Nuevos gráficos v2.3
        salesBySeller: [],
        salesByCategory: [],
        salesByPlanFamily: [],
        amountDistribution: [],
        salesByModality: [],
        salesByCashier: [],
        // NUEVOS GRÁFICOS DE DESCUENTOS
        discountsByCategory: [],
        discountsDailyTrend: []
      },
      // NUEVO v3: Métricas desde Backend
      useServerMetrics: false,
      serverKPIs: null,
      serverCharts: null,
      // Instancias de Chart.js (EXPANDIDAS v2.3)
      chartInstances: {
        // Instancias existentes
        statusChart: null,
        methodChart: null,
        branchChart: null,
        trendChart: null,
        // Nuevas instancias v2.3
        sellerChart: null,
        categoryChart: null,
        planFamilyChart: null,
        amountChart: null,
        modalityChart: null,
        cashierChart: null,
              // NUEVAS INSTANCIAS DE GRÁFICOS DE DESCUENTOS
      discountCategoryChart: null,
      discountTrendChart: null
    },
    // Estado para el modal de pantalla completa
    fullscreenChart: {
      show: false,
      type: '',
      title: '',
      data: [],
      instance: null,
      labelColumn: 'Categoría',
      valueColumn: 'Valor',
      showColorIndicator: true
    }
    };
  },
  computed: {
    // Calcular totales de paginación
    totalPages() {
      return Math.ceil(this.totalRecords / this.pagination.itemsPerPage);
    },
    // Páginas disponibles para mostrar en paginador
    availablePages() {
      const pages = [];
      const maxPages = 5; // Mostrar máximo 5 páginas
      const current = this.pagination.currentPage;
      const total = this.totalPages;
      
      let start = Math.max(1, current - Math.floor(maxPages / 2));
      let end = Math.min(total, start + maxPages - 1);
      
      // Ajustar el inicio si estamos cerca del final
      if (end - start < maxPages - 1) {
        start = Math.max(1, end - maxPages + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      return pages;
    },
    // Información de registros mostrados
    recordsInfo() {
      const start = (this.pagination.currentPage - 1) * this.pagination.itemsPerPage + 1;
      const end = Math.min(this.pagination.currentPage * this.pagination.itemsPerPage, this.totalRecords);
      return `Mostrando ${start} a ${end} de ${this.totalRecords} registros`;
    },
    // Habilitar exportación KPIs cuando hay datos (desde servidor o página actual)
    hasDataForKPIs() {
      if (this.useServerMetrics && this.serverKPIs) return true;
      if (this.allFilteredRecords && this.allFilteredRecords.length > 0) return true;
      if (this.records && this.records.length > 0) return true;
      return false;
    },
    
    // Título dinámico del panel de análisis visual con rango de fechas
    tituloAnalisisVisual() {
      const rangoFechas = this.obtenerRangoFechasParaPDF();
      const tituloBase = 'ANÁLISIS VISUAL DE VENTAS';
      
      if (rangoFechas && rangoFechas.trim() !== '') {
        return `${tituloBase} - ${rangoFechas}`;
      } else {
        return tituloBase;
      }
    },
    // CORREGIDO v2.3: Estadísticas calculadas sobre TODOS los datos filtrados
    totalSales() {
      if (this.useServerMetrics && this.serverKPIs) {
        return this.serverKPIs.totalSales || 0;
      }
      // Fallback legacy
      const dataSource = this.allFilteredRecords && this.allFilteredRecords.length > 0 
        ? this.allFilteredRecords 
        : this.records;
      const total = dataSource ? dataSource.length : 0;
      
      // Log para debugging (solo si hay cambios significativos)
      if (total > 0 && this.allFilteredRecords && this.records && 
          this.allFilteredRecords.length !== this.records.length) {
        console.log('📈 KPI totalSales usando datos globales:', {
          datosTotalesFiltrados: this.allFilteredRecords.length,
          datosPaginaActual: this.records.length,
          totalCalculado: total,
          origen: 'allFilteredRecords'
        });
      }
      
      return total;
    },
    totalAmount() {
      if (this.useServerMetrics && this.serverKPIs) {
        return this.serverKPIs.totalAmount || 0;
      }
      const dataSource = this.allFilteredRecords && this.allFilteredRecords.length > 0 
        ? this.allFilteredRecords 
        : this.records;
      if (!dataSource || dataSource.length === 0) return 0;
      return dataSource.reduce((sum, sale) => {
        const amount = this.parseMonetaryValue(sale.ventaTotal) || 0;
        return sum + amount;
      }, 0);
    },
    validSales() {
      if (this.useServerMetrics && this.serverKPIs) {
        return this.serverKPIs.validSales || 0;
      }
      const dataSource = this.allFilteredRecords && this.allFilteredRecords.length > 0 
        ? this.allFilteredRecords 
        : this.records;
      if (!dataSource || dataSource.length === 0) return 0;
      return dataSource.filter(sale => 
        sale.estadoVenta === '' || sale.estadoVenta === null || sale.estadoVenta === undefined
      ).length;
    },
    cancelledSales() {
      if (this.useServerMetrics && this.serverKPIs) {
        return this.serverKPIs.cancelledSales || 0;
      }
      const dataSource = this.allFilteredRecords && this.allFilteredRecords.length > 0 
        ? this.allFilteredRecords 
        : this.records;
      if (!dataSource || dataSource.length === 0) return 0;
      return dataSource.filter(sale => sale.estadoVenta === '[ANULADA]').length;
    },
    averageSaleAmount() {
      if (this.useServerMetrics && this.serverKPIs) {
        return this.serverKPIs.averageSaleAmount || 0;
      }
      if (this.totalSales === 0) return 0;
      return this.totalAmount / this.totalSales;
    },
    validSalesPercentage() {
      if (this.useServerMetrics && this.serverKPIs) {
        return this.serverKPIs.validSalesPercentage || 0;
      }
      if (this.totalSales === 0) return 0;
      return Math.round((this.validSales / this.totalSales) * 100);
    },
    // Selección múltiple
    selectedItemsCount() {
      return this.selectedItems.length;
    },
    selectedValidSales() {
      // ACTUALIZADO v2.3: Reconocer VIGENTE cuando estadoVenta está vacío
      return this.selectedItems.filter(item => {
        const sale = this.records.find(record => record._id.$oid === item);
        const estado = sale?.estadoVenta;
        return estado === '' || estado === null || estado === undefined;
      }).length;
    },
    selectedTotalAmount() {
      return this.selectedItems.reduce((sum, itemId) => {
        const sale = this.records.find(record => record._id.$oid === itemId);
        return sum + (this.parseMonetaryValue(sale?.ventaTotal) || 0);
      }, 0);
    },
    canBulkCancel() {
      return this.selectedValidSales > 0;
    },
    // Filtros guardados ordenados
    sortedSavedFilters() {
      return this.savedFilters.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    },
    
    // NUEVO v2.3: Computed para indicar el alcance de los KPIs
    kpiDataSource() {
      if (this.useServerMetrics && this.serverKPIs) {
        const total = this.serverKPIs.totalSales || 0;
        return {
          dataSource: null,
          isGlobal: true,
          totalCount: total,
          pageCount: this.records ? this.records.length : 0,
          description: `KPIs del servidor (totales: ${total})`
        };
      }
      const hasAllFiltered = this.allFilteredRecords && this.allFilteredRecords.length > 0;
      return {
        dataSource: hasAllFiltered ? this.allFilteredRecords : this.records,
        isGlobal: hasAllFiltered,
        totalCount: hasAllFiltered ? this.allFilteredRecords.length : (this.records ? this.records.length : 0),
        pageCount: this.records ? this.records.length : 0,
        description: hasAllFiltered 
          ? `Datos globales (${this.allFilteredRecords.length} registros filtrados)` 
          : `Página actual (${this.records.length} registros)`
      };
    },
    
    // NUEVO v2.3: Computed para mostrar información del estado de datos
    dataStatusInfo() {
      const kpi = this.kpiDataSource;
      if (this.useServerMetrics && this.serverKPIs) {
        return {
          showAlert: true,
          message: `KPIs del servidor sobre ${kpi.totalCount} registros totales (mostrando ${kpi.pageCount} en página actual)`,
          type: 'info'
        };
      }
      if (kpi.isGlobal && kpi.totalCount !== kpi.pageCount) {
        return {
          showAlert: true,
          message: `KPIs calculados sobre ${kpi.totalCount} registros totales (mostrando ${kpi.pageCount} en página actual)`,
          type: 'info'
        };
      } else if (!kpi.isGlobal && kpi.pageCount > 0) {
        return {
          showAlert: false,
          message: `KPIs calculados solo sobre la página actual (${kpi.pageCount} registros)`,
          type: 'warning'
        };
      }
      return { showAlert: false, message: '', type: 'info' };
    }
  },
  watch: {
    // Observar cambios en totalRecords para actualizar paginación
    totalRecords(newVal) {
      this.pagination.totalRecords = newVal;
    },
    // Observar cambios en records para actualizar selección (SOLO selección, no gráficos ni KPIs)
    records: {
      handler(newRecords, oldRecords) {
        // Limpiar selecciones que ya no existen
        if (newRecords && newRecords.length > 0) {
          const currentIds = newRecords.map(record => record._id.$oid);
          this.selectedItems = this.selectedItems.filter(id => currentIds.includes(id));
          this.updateAllSelectedState();
          
          // NUEVO v2.3: Log sobre KPIs (solo si hay cambio significativo)
          if (oldRecords && newRecords.length !== oldRecords.length) {
            console.log('📄 Página de registros actualizada - KPIs NO se ven afectados:', {
              registrosPaginaAnterior: oldRecords.length,
              registrosPaginaActual: newRecords.length,
              kpisUsanDatosGlobales: this.allFilteredRecords && this.allFilteredRecords.length > 0,
              nota: 'Los KPIs usan allFilteredRecords, no records de página'
            });
          }
          
          // NOTA: Los gráficos se actualizarán en watcher de allFilteredRecords
        } else {
          this.selectedItems = [];
          this.allSelected = false;
        }
      },
      immediate: true
    },
    // MEJORADO v2.3: Observar cambios en allFilteredRecords para actualización automática de gráficos
    allFilteredRecords: {
      handler(newAllRecords, oldAllRecords) {
        // GARANTIZAR ACTUALIZACIÓN AUTOMÁTICA DE GRÁFICOS en cada cambio de filtros
        console.log('📊 Detectado cambio en allFilteredRecords (GymSales):', {
          registrosNuevos: newAllRecords ? newAllRecords.length : 0,
          registrosAnteriores: oldAllRecords ? oldAllRecords.length : 0,
          actualizacionRequerida: true
        });
        
        // SIEMPRE actualizar gráficos cuando cambien los datos filtrados
        // Esto garantiza que los gráficos se actualicen con filtros básicos Y avanzados
        if (this.updateChartData && typeof this.updateChartData === 'function') {
          // Si pasamos de no tener datos a tener datos, forzar actualización inmediata
          const hadNoData = !oldAllRecords || oldAllRecords.length === 0;
          const hasData = newAllRecords && newAllRecords.length > 0;
          
          if (hadNoData && hasData) {
            console.log('🚀 Forzando actualización inmediata de gráficos (pasamos de 0 a', newAllRecords.length, 'registros)');
            // Limpiar cualquier actualización pendiente
            if (this.chartUpdateTimeout) {
              clearTimeout(this.chartUpdateTimeout);
              this.chartUpdateTimeout = null;
            }
            // Resetear el último tiempo de actualización para forzar actualización inmediata
            this.lastChartUpdate = null;
          }
          
          this.updateChartData();
        } else {
          console.warn('⚠️ updateChartData no está disponible todavía');
          // Programar una actualización cuando esté disponible
          setTimeout(() => {
            if (this.updateChartData && typeof this.updateChartData === 'function') {
              this.updateChartData();
            }
          }, 100);
        }
        
        // Log para debugging de gráficos
        if (newAllRecords && newAllRecords.length > 0) {
          console.log('📈 Gráficos actualizados automáticamente:', {
            registrosTotalesFiltrados: newAllRecords.length,
            registrosPaginaActual: this.records ? this.records.length : 0,
            fuenteDatos: 'allFilteredRecords (datos globales filtrados)',
            tipoActualizacion: 'Automática por cambio de filtros'
          });
          
          // Sincronizar paginación local si cambió el tamaño de datos
          if (!oldAllRecords || newAllRecords.length !== oldAllRecords.length) {
            this.setupLocalPagination(this.pagination.itemsPerPage);
          }
        } else {
          console.log('📊 Sin datos - gráficos limpiados automáticamente');
        }
      },
      immediate: true,
      deep: false // No necesitamos deep watch para arrays simples
    },
    
    // NUEVO v2.3: Observar cambios en originalFilteredRecords
    originalFilteredRecords: {
      handler(newOriginalRecords) {
        if (newOriginalRecords && newOriginalRecords.length > 0) {
          console.log('💾 originalFilteredRecords actualizado (GymSales):', newOriginalRecords.length, 'registros');
        }
      },
      immediate: true
    },
    // NUEVO: Observar cambios en localPagination para sincronizar paginación
    localPagination: {
      handler(newPagination) {
        if (newPagination) {
          // Sincronizar paginación local con componente
          this.pagination.currentPage = newPagination.currentPage;
          this.pagination.itemsPerPage = newPagination.itemsPerPage;
          console.log('📄 Paginación sincronizada (GymSales):', newPagination);
        }
      },
      deep: true,
      immediate: true
    },
    // Observar cambios en selectedItems para actualizar allSelected
    selectedItems: {
      handler() {
        this.updateAllSelectedState();
      },
      deep: true
    },
    // Observar cambios en el panel de gráficos
    showChartsPanel: {
      handler(newVal) {
        if (newVal) {
          // Cuando se abre el panel, renderizar gráficos después de que el DOM se actualice
          this.$nextTick(() => {
            setTimeout(() => {
              this.renderCharts();
            }, 100);
          });
        }
      }
    },
    
    // ========================================
    // WATCHERS PARA FILTROS AVANZADOS LOCALES v2.2
    // ========================================
    
    // Watchers para filtros avanzados (HABILITADOS - filtrado local con delay automático)
    // NOTA: Estos watchers aplican filtros localmente sobre los datos ya cargados
    'filters.clientName': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          console.log('👤 Nombre de cliente cambió (GymSales):', newVal);
          this.handleAdvancedFilterChange();
        }
      }
    },
    'filters.sellerName': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          console.log('👨‍💼 Nombre de vendedor cambió (GymSales):', newVal);
          this.handleAdvancedFilterChange();
        }
      }
    },
    'filters.amountFrom': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          console.log('💰 Monto desde cambió (GymSales):', newVal);
          this.handleAdvancedFilterChange();
        }
      }
    },
    'filters.amountTo': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          console.log('💰 Monto hasta cambió (GymSales):', newVal);
          this.handleAdvancedFilterChange();
        }
      }
    },
    // NUEVOS WATCHERS PARA FILTROS AVANZADOS DINÁMICOS
    'filters.advancedStatus': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          console.log('📊 Estado avanzado cambió (GymSales):', newVal);
          this.handleAdvancedFilterChange();
        }
      }
    },
    'filters.advancedPaymentMethod': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          console.log('💳 Método de pago avanzado cambió (GymSales):', newVal);
          this.handleAdvancedFilterChange();
        }
      }
    },
    'filters.advancedBranch': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          console.log('🏢 Sucursal avanzada cambió (GymSales):', newVal);
          this.handleAdvancedFilterChange();
        }
      }
    }
  },
  mounted() {
    this.saveFilterModal = new Modal(document.getElementById('saveFilterModal'));
    
    // Configurar paginación inicial
    this.pagination.totalRecords = this.totalRecords;
    this.pagination.currentPage = 1;
    
    // Crear versión debounced del handleSearch
    this.debouncedSearch = debounce(this.performSearch, 300);
    
    // NUEVO: Crear versión debounced para filtros avanzados (1 segundo) que dispara búsqueda por API
    this.debouncedAdvancedFilter = debounce(() => {
      // Disparar búsqueda unificada para que el padre consulte API + métricas
      this.handleSearch();
    }, 1000);
    
    // Cargar filtros guardados
    this.loadSavedFilters();
    
    // NUEVO: Configurar filtros de fecha para el mes en curso automáticamente
    this.setCurrentMonthFilter();
    
    // Inicializar gráficos si es necesario
    this.$nextTick(() => {
      if (this.updateChartData && typeof this.updateChartData === 'function') {
        this.updateChartData();
      } else {
        console.warn('⚠️ updateChartData no está disponible en mounted');
      }
    });
    
         // NOTA IMPORTANTE PARA DESARROLLADORES - v2.5 UX MEJORADA + CATÁLOGOS REALES + KPIs CORREGIDOS + GRÁFICOS EXPANDIDOS (GymSales):
     // Este componente ahora usa CATÁLOGOS REALES de la API con SISTEMA DE GRÁFICOS AVANZADO y UX MEJORADA:
     // 1. CATÁLOGOS: sucursales, estadosMediosPago, estadosVenta, estadosCierreVenta
     // 2. FILTROS BÁSICOS: Usan operación /v1/sales/get/gymsales
     // 3. FILTROS AVANZADOS: Trabajan sobre datos en memoria sin consultar servidor
     // 4. PAGINACIÓN LOCAL: No consulta endpoint en cambios de página, usa slice del array completo
     // 5. GRÁFICOS EXPANDIDOS v2.3: 10 gráficos diferentes que usan allFilteredRecords (todos los datos)
     // 6. BÚSQUEDA INTELIGENTE: Detecta automáticamente tipo de búsqueda (ID, RUT, texto)
     // 7. KPIs CORREGIDOS: Ahora usan allFilteredRecords (globales) no records (página actual)
     // 8. ACTUALIZACIÓN AUTOMÁTICA DE GRÁFICOS: Se actualizan automáticamente con cada cambio de filtros
            console.info('🔧 GymSalesTable v2.5: UX mejorada, etiquetas de filtros, sistema multi-hoja, catálogos reales, gráficos expandidos y KPIs globales implementados', {
       catalogosReales: true,
       paginacionLocal: true,
       filtrosAvanzadosLocales: true,
       filtrosGuardadosMejorados: true,
       kpisGlobalesCorregidos: true,
       graficosExpandidos: true, // NUEVO v2.3
       sistemaMultiHoja: true, // NUEVO v2.4
       exportacionMejorada: true, // NUEVO v2.4
       uxMejoradaFiltrosBasicos: true, // NUEVO v2.5
       etiquetasDescriptivas: true, // NUEVO v2.5
       accesibilidadMejorada: true, // NUEVO v2.5
       catalogos: {
         sucursales: this.branches ? this.branches.length : 0,
         estadosMediosPago: this.paymentMethodsCatalog ? this.paymentMethodsCatalog.length : 0,
         estadosVenta: this.saleStatusCatalog ? this.saleStatusCatalog.length : 0,
         estadosCierreVenta: this.closeStatusCatalog ? this.closeStatusCatalog.length : 0
       },
       dataProps: {
         records: this.records ? this.records.length : 0,
         allFilteredRecords: this.allFilteredRecords ? this.allFilteredRecords.length : 0,
         localPagination: this.localPagination
       },
       kpisInfo: {
         usanDatosGlobales: 'allFilteredRecords (todos los registros filtrados)',
         noUsanDatosDePagina: 'records (solo página actual)',
         beneficio: 'Estadísticas completas sin importar la página actual'
       },
       graficosInfo: { // NUEVO v2.3
         totalGraficos: 10,
         tiposGraficos: ['pie', 'doughnut', 'bar', 'horizontalBar', 'line'],
         fuenteDatos: 'allFilteredRecords (datos globales filtrados)',
         actualizacion: 'Automática con cada cambio de filtros',
         categoriasAnalizadas: [
           'Estados de venta', 'Métodos de pago', 'Sucursales', 'Vendedores',
           'Categorías de producto', 'Planes', 'Tipos de Plan', 
           'Distribución de montos', 'Cajas/POS', 'Tendencia temporal'
         ]
       },
       filtrosNuevos: {
         searchText: 'Búsqueda inteligente: ID, RUT, Cliente',
         amountFrom: 'Monto mínimo',
         amountTo: 'Monto máximo',
         clientName: 'Nombre específico de cliente',
         sellerName: 'Nombre específico de vendedor'
       },
       mejorasUX_v25: { // NUEVO v2.5
         etiquetasFiltrosBasicos: 'Labels descriptivos agregados en desktop y móvil',
         iconosVisuales: 'search, building, check-circle, credit-card',
         accesibilidad: 'Atributos "for" asociados a inputs/selects',
         problemaResuelto: 'Vista móvil no identificaba función de cada filtro',
         estilosEspecíficos: 'text-light para desktop, text-white fw-bold para móvil',
         responsiveDesign: 'Adaptativos para diferentes tamaños de pantalla'
       },
       sistemaMultiHojaInfo: { // NUEVO v2.4
         totalHojas: 5,
         hojasDisponibles: ['CONSOLIDADO', 'VENTAS', 'CONTROL Y DETALLE', 'PRODUCTOS', 'MEMBRESIAS'],
         relacionPorID: 'ID Transacción en columna C de cada hoja',
         beneficios: [
           'Vista ejecutiva consolidada (compatible)',
           'Análisis granular por tipo de datos',
           'Sin pérdida de elementos múltiples',
           'Relación perfecta entre hojas'
         ],
         implementacion: 'Dashboard: generateExcelFile() + métodos auxiliares especializados'
       }
     });
  },
  beforeDestroy() {
    // Limpiar TODAS las instancias de gráficos (EXPANDIDO v2.3)
    console.log('🧹 Limpiando todas las instancias de gráficos...');
    
    Object.keys(this.chartInstances).forEach(key => {
      if (this.chartInstances[key]) {
        console.log(`🗑️ Destruyendo gráfico: ${key}`);
        this.chartInstances[key].destroy();
        this.chartInstances[key] = null;
      }
    });
    
    // Limpiar datos de gráficos
    this.chartData = {
      salesByStatus: [],
      salesByMethod: [],
      salesByBranch: [],
      dailyTrend: [],
      salesBySeller: [],
      salesByCategory: [],
      salesByPlanFamily: [],
      amountDistribution: [],
      salesByModality: [],
      salesByCashier: [],
      // NUEVOS GRÁFICOS DE DESCUENTOS
      discountsByCategory: [],
      discountsDailyTrend: []
    };
    
    console.log('✅ Limpieza de gráficos completada');
  },
  methods: {
    // NUEVO v3: aplicar KPIs y Gráficos entregados por Backend
    applyMetricsFromAPI(metricsResult) {
      try {
        if (!metricsResult) return;
        const { kpis, charts } = metricsResult;
        // KPIs
        this.serverKPIs = kpis || null;
        // Reflejar en badges/UI existentes sin recalcular en memoria
        if (this.serverKPIs) {
          // totalRecords controla el paginador; no lo pisamos aquí
          // Actualizamos indicadores locales si se usan en la UI de KPIs
          this.totalSalesAmount = this.serverKPIs.totalAmount || 0;
          this.totalValidSales = this.serverKPIs.validSales || 0;
          this.totalCancelledSales = this.serverKPIs.cancelledSales || 0;
        }
        // Charts
        this.serverCharts = charts || null;
        if (this.serverCharts) {
          // Reemplazar chartData directamente desde backend
          this.chartData.salesByStatus = charts.salesByStatus || [];
          this.chartData.salesByMethod = charts.salesByMethod || [];
          this.chartData.salesByBranch = charts.salesByBranch || [];
          this.chartData.dailyTrend = charts.dailyTrend || [];
          this.chartData.salesBySeller = charts.salesBySeller || [];
          this.chartData.salesByCategory = charts.salesByCategory || [];
          this.chartData.salesByPlanFamily = charts.salesByPlanFamily || [];
          this.chartData.amountDistribution = charts.amountDistribution || [];
          this.chartData.salesByModality = charts.salesByModality || [];
          this.chartData.salesByCashier = charts.salesByCashier || [];
          this.chartData.discountsByCategory = charts.discountsByCategory || [];
          this.chartData.discountsDailyTrend = charts.discountsDailyTrend || [];
        }
        this.useServerMetrics = true;
        // Render inmediato
        if (typeof this.renderCharts === 'function') this.renderCharts();
      } catch (err) {
        console.error('❌ Error aplicando métricas de Backend:', err);
      }
    },
    handleSearch() {
      this.isSearching = true;
      this.debouncedSearch();
    },
    
    // ========================================
    // MÉTODOS PARA MANEJO DE FECHAS Y VALIDACIÓN
    // ========================================
    validateDateRange() {
      if (!this.filters.dateFrom || !this.filters.dateTo) return true;
      
      const fromDate = new Date(this.filters.dateFrom);
      const toDate = new Date(this.filters.dateTo);
      
      // Validar que las fechas sean válidas
      if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
        this.$swal.fire({
          icon: 'error',
          title: 'Fechas inválidas',
          text: 'Por favor, selecciona fechas válidas.',
          confirmButtonColor: '#6bd098'
        });
        return false;
      }
      
      // Validar que la fecha desde no sea mayor que la fecha hasta
      if (fromDate > toDate) {
        this.$swal.fire({
          icon: 'error',
          title: 'Rango de fechas inválido',
          text: 'La fecha "desde" no puede ser mayor que la fecha "hasta".',
          confirmButtonColor: '#6bd098'
        });
        return false;
      }
      
      const diffTime = Math.abs(toDate - fromDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const maxDays = 365; // 1 año máximo
      
      if (diffDays > maxDays) {
        this.$swal.fire({
          icon: 'error',
          title: 'Rango de fechas demasiado amplio',
          text: `El rango de fechas no puede ser mayor a 1 año. Rango actual: ${diffDays} días.`,
          confirmButtonColor: '#6bd098'
        });
        return false;
      }
      
      return true;
    },
    
    getTodayDate() {
      const today = new Date();
      return today.toISOString().split('T')[0];
    },
    
    // Obtener el primer día del mes actual
    getFirstDayOfCurrentMonth() {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      return firstDay.toISOString().split('T')[0];
    },
    
    // Obtener el último día del mes actual
    getLastDayOfCurrentMonth() {
      const today = new Date();
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return lastDay.toISOString().split('T')[0];
    },
    
    // Configurar filtros para el mes en curso automáticamente
    setCurrentMonthFilter() {
      this.filters.dateFrom = this.getFirstDayOfCurrentMonth();
      this.filters.dateTo = this.getLastDayOfCurrentMonth();
      
      console.log('📅 Filtros configurados automáticamente para el mes en curso:', {
        fechaDesde: this.filters.dateFrom,
        fechaHasta: this.filters.dateTo,
        mes: new Date().toLocaleString('es-CL', { month: 'long', year: 'numeric' })
      });
    },
    performSearch() {
      // ACTUALIZADO v2.3: Emitir tanto search tradicional como searchUnified
      const searchParams = {
        ...this.filters,
        page: this.pagination.currentPage,
        itemsPerPage: this.pagination.itemsPerPage,
        sortField: this.sorting.field,
        sortDirection: this.sorting.direction
      };
      
      // Emitir búsqueda tradicional para compatibilidad
      this.$emit('search', searchParams);
      
      // NUEVO: Emitir búsqueda unificada para nuevas funcionalidades
      this.$emit('searchUnified', {
        isLocalPageChange: false, // Es una nueva búsqueda, no cambio de página
        ...searchParams
      });
      
      // El loading se desactiva cuando el componente padre termine de cargar
      setTimeout(() => {
        this.isSearching = false;
      }, 300);
    },
    
    // ========================================
    // MÉTODOS DE PAGINACIÓN LOCAL v2.2
    // ========================================
    setupLocalPagination(itemsPerPage) {
      this.localPagination.itemsPerPage = itemsPerPage;
      this.localPagination.totalPages = Math.ceil(this.allFilteredRecords.length / itemsPerPage);
      this.localPagination.currentPage = 1;
      
      console.log('📄 Paginación local configurada (GymSales):', {
        totalRegistros: this.allFilteredRecords.length,
        itemsPorPagina: itemsPerPage,
        totalPaginas: this.localPagination.totalPages
      });
    },
    
    updateLocalPagination(page, itemsPerPage) {
      // Actualizar configuración si cambia items per page
      if (itemsPerPage && itemsPerPage !== this.localPagination.itemsPerPage) {
        this.localPagination.itemsPerPage = itemsPerPage;
        this.localPagination.totalPages = Math.ceil(this.allFilteredRecords.length / itemsPerPage);
      }
      
      // Validar página
      if (page < 1) page = 1;
      if (page > this.localPagination.totalPages) page = this.localPagination.totalPages;
      
      this.localPagination.currentPage = page;
      
      // Calcular slice de datos para la página actual
      const startIndex = (page - 1) * this.localPagination.itemsPerPage;
      const endIndex = startIndex + this.localPagination.itemsPerPage;
      
      // Actualizar records (datos mostrados en tabla) CON SLICE LOCAL
      this.$emit('update:records', this.allFilteredRecords.slice(startIndex, endIndex));
      
      // Actualizar totalRecords para que la paginación funcione correctamente
      this.$emit('update:totalRecords', this.allFilteredRecords.length);
      
      console.log('📄 Página actualizada (GymSales):', {
        paginaActual: page,
        registrosMostrados: this.records.length,
        rangoIndices: `${startIndex}-${endIndex-1}`,
        totalRegistros: this.allFilteredRecords.length
      });
    },
    
    performLocalPageChange(page, itemsPerPage = null) {
      const searchParams = {
        isLocalPageChange: true, // Flag para indicar que es cambio de página local
        page: page - 1, // Convertir a base 0 para compatibilidad
        itemsPerPage: itemsPerPage || this.pagination.itemsPerPage,
        // No incluir filtros porque no es una nueva búsqueda
      };
      
      console.log('📄 Cambio de página local (GymSales):', {
        page: page,
        itemsPerPage: searchParams.itemsPerPage,
        totalRegistrosFiltrados: this.allFilteredRecords.length
      });
      
      // Emitir evento de búsqueda unificada con flag local
      this.$emit('searchUnified', searchParams);
    },
    showDetails(sale) {
      this.selectedSale = sale;
      this.showSaleDetailModal = true;
      console.log('📋 Mostrando detalles completos de la venta:', {
        idTransaccion: sale.idTransaccion,
        cliente: sale.nombreCliente,
        total: sale.ventaTotal,
        formasPago: sale.infoVenta?.venta?.length || 0,
        items: sale.infoVenta?.detalleVenta?.length || 0,
        planes: sale.infoVenta?.plan?.length || 0
      });
    },
    closeSaleDetailModal() {
      this.showSaleDetailModal = false;
      setTimeout(() => {
        this.selectedSale = null;
      }, 150);
    },

    // ========================================
    // MÉTODOS DE MANEJO DE FECHAS CORREGIDOS v2.5.1
    // ========================================
    
    // Función para convertir fechas chilenas DD-MM-YYYY a formato JavaScript
    parseChileanDate(dateString) {
      if (!dateString || dateString.trim() === '') {
        return null;
      }
      
      try {
        // Formato esperado: "31-05-2024 15:41:36" o "31-05-2024"
        const dateTimeParts = dateString.trim().split(' ');
        const datePart = dateTimeParts[0]; // "31-05-2024"
        const timePart = dateTimeParts[1] || '00:00:00'; // "15:41:36" o "00:00:00"
        
        // Separar fecha DD-MM-YYYY
        const dateComponents = datePart.split('-');
        if (dateComponents.length !== 3) {
          console.warn('⚠️ Formato de fecha inválido:', dateString);
          return null;
        }
        
        const day = parseInt(dateComponents[0]);
        const month = parseInt(dateComponents[1]) - 1; // JavaScript usa meses 0-11
        const year = parseInt(dateComponents[2]);
        
        // Separar tiempo HH:mm:ss
        const timeComponents = timePart.split(':');
        const hours = parseInt(timeComponents[0]) || 0;
        const minutes = parseInt(timeComponents[1]) || 0;
        const seconds = parseInt(timeComponents[2]) || 0;
        
        // Crear objeto Date válido
        const jsDate = new Date(year, month, day, hours, minutes, seconds);
        
        // Validar que la fecha sea válida
        if (isNaN(jsDate.getTime())) {
          console.warn('⚠️ Fecha resultante inválida:', dateString, '→', jsDate);
          return null;
        }
        
        return jsDate;
      } catch (error) {
        console.error('❌ Error al parsear fecha chilena:', dateString, error);
        return null;
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return 'Sin fecha';
      
      const date = this.parseChileanDate(dateString);
      if (!date) {
        console.warn('⚠️ No se pudo formatear la fecha:', dateString);
        return 'Fecha inválida';
      }
      
      return date.toLocaleString('es-CL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP'
      }).format(value);
    },
    getStatusBadgeClass(status) {
      // ACTUALIZADO v2.3: Nueva lógica de estados
      // estadoVenta = "" → [VIGENTE] (verde pastel)
      // estadoVenta = "[ANULADA]" → [ANULADA] (rojo pastel)
      
      return {
        'badge': true,
        'bg-success-pastel': status === '' || status === null || status === undefined, // VIGENTE
        'bg-danger-pastel': status === '[ANULADA]' // ANULADA
      };
    },
    
    // NUEVO v2.3: Método para obtener el texto del estado que se muestra
    getStatusText(status) {
      // Si estadoVenta está vacío, null o undefined → mostrar [VIGENTE]
      if (status === '' || status === null || status === undefined) {
        return '[VIGENTE]';
      }
      // Si estadoVenta = "[ANULADA]" → mostrar [ANULADA]
      if (status === '[ANULADA]') {
        return '[ANULADA]';
      }
      // Para cualquier otro valor, mostrar tal como viene
      return status;
    },
    confirmAnulacion(sale) {
      this.$swal.fire({
        title: '¿Estás seguro?',
        text: `¿Deseas anular la venta #${sale.idTransaccion.$numberLong}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sí, anular',
        cancelButtonText: 'Cancelar'
      }).then((result) => {
        if (result.isConfirmed) {
          this.anularVenta(sale);
        }
      });
    },
    // Métodos de paginación (ACTUALIZADOS para usar paginación local)
    changePage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.pagination.currentPage) {
        this.pagination.currentPage = page;
        this.localPagination.currentPage = page; // Sincronizar con paginación local
        
        // Si existen datos locales, paginar localmente; de lo contrario, pedir al servidor
        if (this.allFilteredRecords && this.allFilteredRecords.length > 0) {
          this.updateLocalPagination(page);
        } else {
          // Emitir evento unificado para que el padre cargue la página desde el servidor
          const searchParams = {
            isLocalPageChange: true,
            page: page - 1, // base 0
            itemsPerPage: this.pagination.itemsPerPage
          };
          this.$emit('searchUnified', searchParams);
        }
      }
    },
    goToFirstPage() {
      this.changePage(1);
    },
    goToLastPage() {
      this.changePage(this.totalPages);
    },
    goToPreviousPage() {
      this.changePage(this.pagination.currentPage - 1);
    },
    goToNextPage() {
      this.changePage(this.pagination.currentPage + 1);
    },
    changeItemsPerPage(newSize) {
      this.pagination.itemsPerPage = parseInt(newSize);
      this.localPagination.itemsPerPage = parseInt(newSize); // Sincronizar
      
      // Si existen datos locales, paginar localmente; de lo contrario, pedir al servidor
      if (this.allFilteredRecords && this.allFilteredRecords.length > 0) {
        this.updateLocalPagination(1, parseInt(newSize));
      } else {
        this.pagination.currentPage = 1; // Resetear a primera página
        const searchParams = {
          isLocalPageChange: true,
          page: 0,
          itemsPerPage: this.pagination.itemsPerPage
        };
        this.$emit('searchUnified', searchParams);
      }
    },
    // Métodos de ordenamiento
    sortBy(field) {
      console.log('🔧 GymSalesTable.sortBy:', field);
      
      if (this.sorting.field === field) {
        this.sorting.direction = this.sorting.direction === 'asc' ? 'desc' : 'asc';
      } else {
        this.sorting.field = field;
        this.sorting.direction = 'asc';
      }
      
      console.log('📊 Nuevo ordenamiento:', this.sorting.field, this.sorting.direction);
      
      // ✅ CORREGIDO: Ordenar en memoria en lugar de consultar API
      this.applySortingLocally();
    },
    
    // ========================================
    // MÉTODO NUEVO: APLICAR ORDENAMIENTO LOCAL
    // ========================================
    applySortingLocally() {
      // Verificar que tenemos datos para ordenar
      if (!this.allFilteredRecords || this.allFilteredRecords.length === 0) {
        console.warn('⚠️ No hay datos filtrados para ordenar (GymSales)');
        return;
      }
      
      console.log('🔧 Aplicando ordenamiento local (GymSales):', {
        campo: this.sorting.field,
        direccion: this.sorting.direction,
        registros: this.allFilteredRecords.length,
        paginaActual: this.localPagination.currentPage
      });
      
      // Crear copia de los datos para ordenar
      const sortedData = [...this.allFilteredRecords];
      
      // Aplicar ordenamiento
      const sortedResult = this.sortDataLocally(sortedData, this.sorting.field, this.sorting.direction);
      
      // Actualizar datos ordenados
      this.$emit('update:allFilteredRecords', sortedResult);
      
      // Mantener la página actual si es válida, si no resetear a página 1
      const currentPage = this.localPagination.currentPage;
      const totalPages = Math.ceil(this.allFilteredRecords.length / this.localPagination.itemsPerPage);
      const validPage = currentPage <= totalPages ? currentPage : 1;
      
      // Reconfigurar paginación manteniendo la página válida
      this.setupLocalPagination(this.localPagination.itemsPerPage);
      this.updateLocalPagination(validPage, this.localPagination.itemsPerPage);
      
      console.log('✅ Ordenamiento local aplicado exitosamente (GymSales):', {
        registrosOrdenados: sortedResult.length,
        paginaFinal: validPage,
        totalPaginas: totalPages
      });
    },
    
    getSortClass(field) {
      return {
        'sort-active': this.sorting.field === field,
        'sort-asc': this.sorting.field === field && this.sorting.direction === 'asc',
        'sort-desc': this.sorting.field === field && this.sorting.direction === 'desc'
      };
    },
    getSortIcon(field) {
      if (this.sorting.field !== field) {
        return 'fa-sort text-muted';
      }
      return this.sorting.direction === 'asc' ? 'fa-sort-up text-primary' : 'fa-sort-down text-primary';
    },
    // Métodos de filtros de fecha
    getTodayDate() {
      return new Date().toISOString().split('T')[0];
    },
    setDateFilter(period) {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      switch (period) {
        case 'today':
          this.filters.dateFrom = this.getTodayDate();
          this.filters.dateTo = this.getTodayDate();
          break;
        case 'yesterday':
          const yesterdayStr = yesterday.toISOString().split('T')[0];
          this.filters.dateFrom = yesterdayStr;
          this.filters.dateTo = yesterdayStr;
          break;
        case 'week':
          const weekStart = new Date(today);
          // CORREGIDO: Calcular desde el lunes (no domingo) hasta hoy
          // getDay() devuelve: 0=Domingo, 1=Lunes, 2=Martes, 3=Miércoles, etc.
          // Para obtener el lunes: (getDay() + 6) % 7 días atrás
          const daysFromMonday = (today.getDay() + 6) % 7;
          weekStart.setDate(today.getDate() - daysFromMonday);
          this.filters.dateFrom = weekStart.toISOString().split('T')[0];
          this.filters.dateTo = this.getTodayDate();
          break;
        case 'month':
          const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
          this.filters.dateFrom = monthStart.toISOString().split('T')[0];
          this.filters.dateTo = this.getTodayDate();
          break;
      }
      
      console.log('📅 Filtro rápido de fecha configurado:', period, { 
        dateFrom: this.filters.dateFrom, 
        dateTo: this.filters.dateTo 
      });
      // Disparar búsqueda a servidor para registros y métricas
      this.isApplyingAdvancedFilters = true;
      this.handleSearch();
    },
    clearDateFilters() {
      // Limpiar los campos de fecha
      this.filters.dateFrom = '';
      this.filters.dateTo = '';
      
      console.log('🗑️ Filtros de fecha limpiados');
      // Ejecutar búsqueda con fechas vacías (sin rango)
      this.isApplyingAdvancedFilters = true;
      this.handleSearch();
    },
    clearAllFilters() {
      this.filters.searchText = '';
      this.filters.branch = '';
      this.filters.status = '';
      this.filters.paymentMethod = '';
      this.filters.dateFrom = '';
      this.filters.dateTo = '';
      // Limpiar filtros avanzados
      this.filters.amountFrom = '';
      this.filters.amountTo = '';
      this.filters.clientName = '';
      this.filters.sellerName = '';
      
      this.pagination.currentPage = 1;
      this.localPagination.currentPage = 1;
      
      // ACTUALIZADO v2.3: Limpiar todos los filtros SÍ debe hacer búsqueda
      console.log('🗑️ Todos los filtros limpiados - ejecutando búsqueda');
      this.handleSearch();
    },
    
    // ========================================
    // MÉTODOS DE FILTROS AVANZADOS LOCALES v2.2
    // ========================================
    handleAdvancedFilterChange() {
      // Mostrar indicador de que se están aplicando filtros
      this.isApplyingAdvancedFilters = true;
      
      // Aplicar debounce automáticamente para mejorar UX
      console.log('🔄 Filtro avanzado cambiado (GymSales), aplicando en 1 segundo...');
      if (this.debouncedAdvancedFilter) {
        this.debouncedAdvancedFilter();
      } else {
        // Fallback si no existe el debounce
        console.warn('⚠️ debouncedAdvancedFilter no existe, aplicando directamente');
        // Disparar búsqueda al servidor
        this.handleSearch();
      }
    },
    
    // Ahora los filtros avanzados disparan búsqueda al servidor (dejamos stub por compatibilidad)
    applyAdvancedFiltersLocally() {
      // En el nuevo flujo, los filtros avanzados van al servidor; cerramos el indicador
      this.isApplyingAdvancedFilters = false;
    },
    
    applyAdvancedFiltersToData(records, advancedFilters) {
      let filteredData = [...records];
      
      // Filtro por texto general (busca en ID o cliente)
      if (advancedFilters.searchText && advancedFilters.searchText.trim()) {
        const searchTerm = advancedFilters.searchText.toLowerCase().trim();
        filteredData = filteredData.filter(sale => {
          const idTransaccion = (sale.idTransaccion?.$numberLong || '').toString().toLowerCase();
          const clienteName = (sale.nombreCliente || '').toLowerCase();
          
          return idTransaccion.includes(searchTerm) || 
                 clienteName.includes(searchTerm);
        });
      }
      
      // Filtro por nombre de cliente específico
      if (advancedFilters.clientName && advancedFilters.clientName.trim()) {
        const clientTerm = advancedFilters.clientName.toLowerCase().trim();
        filteredData = filteredData.filter(sale => {
          const clienteName = (sale.nombreCliente || '').toLowerCase();
          return clienteName.includes(clientTerm);
        });
      }
      
      // Filtro por nombre de vendedor específico
      if (advancedFilters.sellerName && advancedFilters.sellerName.trim()) {
        const sellerTerm = advancedFilters.sellerName.toLowerCase().trim();
        filteredData = filteredData.filter(sale => {
          const sellerName = (sale.nombreVendedor || '').toLowerCase();
          return sellerName.includes(sellerTerm);
        });
      }
      
      // Filtro por rango de monto desde
      if (advancedFilters.amountFrom && !isNaN(parseFloat(advancedFilters.amountFrom))) {
        const minAmount = parseFloat(advancedFilters.amountFrom);
        filteredData = filteredData.filter(sale => {
          const amount = this.parseMonetaryValue(sale.ventaTotal) || 0;
          return amount >= minAmount;
        });
      }
      
      // Filtro por rango de monto hasta
      if (advancedFilters.amountTo && !isNaN(parseFloat(advancedFilters.amountTo))) {
        const maxAmount = parseFloat(advancedFilters.amountTo);
        filteredData = filteredData.filter(sale => {
          const amount = this.parseMonetaryValue(sale.ventaTotal) || 0;
          return amount <= maxAmount;
        });
      }
      
      // NUEVOS FILTROS AVANZADOS DINÁMICOS
      
      // Filtro por estado de venta avanzado
      if (advancedFilters.advancedStatus && advancedFilters.advancedStatus.trim()) {
        const statusFilter = advancedFilters.advancedStatus;
        filteredData = filteredData.filter(sale => {
          // Aplicar misma lógica que en filtros básicos
          if (statusFilter === '[VIGENTE]') {
            return sale.estadoVenta === '' || sale.estadoVenta === null || sale.estadoVenta === undefined;
          } else if (statusFilter === '[ANULADA]') {
            return sale.estadoVenta === '[ANULADA]';
          } else {
            return sale.estadoVenta === statusFilter;
          }
        });
      }
      
      // Filtro por método de pago avanzado
      if (advancedFilters.advancedPaymentMethod && advancedFilters.advancedPaymentMethod.trim()) {
        const methodFilter = advancedFilters.advancedPaymentMethod;
        filteredData = filteredData.filter(sale => {
          const saleMethod = sale.metodoPago || '';
          return saleMethod === methodFilter;
        });
      }
      
      // Filtro por sucursal avanzada
      if (advancedFilters.advancedBranch && advancedFilters.advancedBranch.trim()) {
        const branchFilter = advancedFilters.advancedBranch;
        filteredData = filteredData.filter(sale => {
          const saleBranch = sale.nombreSucursal || '';
          return saleBranch === branchFilter;
        });
      }
      
      return filteredData;
    },
    
    updateDataWithLocalFilters(filteredData) {
      console.log('🔄 Actualizando datos con filtros locales (GymSales):', {
        registrosFiltrados: filteredData.length,
        actualizacionGraficos: 'Automática por watcher de allFilteredRecords'
      });
      
      // Actualizar allFilteredRecords que disparará automáticamente el watcher
      this.$emit('update:allFilteredRecords', filteredData);
      
      // Reconfigurar paginación
      this.setupLocalPagination(this.localPagination.itemsPerPage);
      this.updateLocalPagination(1, this.localPagination.itemsPerPage);
      
      // Los gráficos se actualizarán automáticamente por el watcher de allFilteredRecords
      // GARANTÍA: La actualización de allFilteredRecords disparará automáticamente updateChartData()
    },
    
    clearAdvancedFilters() {
      // Limpiar solo los filtros avanzados
      this.filters.amountFrom = '';
      this.filters.amountTo = '';
      this.filters.clientName = '';
      this.filters.sellerName = '';
      // NUEVOS FILTROS AVANZADOS DINÁMICOS
      this.filters.advancedStatus = '';
      this.filters.advancedPaymentMethod = '';
      this.filters.advancedBranch = '';
      
      // Ocultar indicador de filtrado
      this.isApplyingAdvancedFilters = false;
      
      console.log('🔄 Limpiando filtros avanzados (GymSales)');
      
      // Restaurar datos originales si existen
      if (this.originalFilteredRecords && this.originalFilteredRecords.length > 0) {
        this.updateDataWithLocalFilters(this.originalFilteredRecords);
      }
    },
    refreshData() {
      this.handleSearch();
    },
    
    // ========================================
    // ✅ MÉTODO ACTIVO v2.4: EXPORTACIÓN MULTI-HOJA (Sistema implementado)
    // ========================================
    exportRecords() {
      /* 
        🚀 MÉTODO PRINCIPAL DE EXPORTACIÓN v2.4
        
        ✅ FUNCIONALIDAD:
        - Emite evento 'export' al componente padre (GymSalesDashboard)
        - El dashboard procesa con sistema multi-hoja (5 hojas especializadas)
        - Compatible con OnLineSalesTable para mantener consistencia
        
        ✅ SISTEMA MULTI-HOJA GENERADO:
        1. CONSOLIDADO: Vista ejecutiva con concatenación (compatible)
        2. VENTAS: Información básica de transacciones
        3. CONTROL Y DETALLE: Formas de pago detalladas (1 fila por forma)
        4. PRODUCTOS: Análisis de inventario (1 fila por producto)
        5. MEMBRESIAS: Análisis de planes (1 fila por plan/membresía)
        
        ✅ BENEFICIOS:
        - Sin pérdida de datos: Todos los elementos múltiples se exportan
        - Análisis granular: Cada hoja optimizada para su propósito
        - Compatibilidad: CONSOLIDADO funciona igual que antes
        - Relación perfecta: ID Transacción conecta todas las hojas
      */
      
      // ✅ ACTIVAR SPINNER: Mostrar ícono de loading al usuario
      this.isExporting = true;
      
      console.log('📊 Iniciando exportación multi-hoja - Spinner activado');
      
      // Emitir evento 'export' con los filtros actuales como OnLineSalesTable.vue
      this.$emit(
        "export",
        this.filters.status,           // selectedStatus
        this.filters.paymentMethod,    // selectedTrxType (en este caso es método de pago)
        this.filters.branch,           // selectedBranch
        this.filters.dateFrom,         // dateFrom
        this.filters.dateTo,           // dateTo
        this.filters.searchText        // searchText adicional para GymSales
      );
      
      console.log('📊 Exportación multi-hoja solicitada:', {
        filtros: this.filters,
        registrosEnPagina: this.records ? this.records.length : 0,
        registrosTotalesFiltrados: this.allFilteredRecords ? this.allFilteredRecords.length : 0,
        metodo: 'evento export → sistema multi-hoja v2.4',
        hojasGeneradas: 5,
        tipoAnalisis: ['Ejecutivo', 'Ventas', 'Auditoría', 'Inventario', 'Retención']
      });
      
      // ⏰ DESACTIVAR SPINNER: Dar tiempo al usuario de ver el spinner y luego que el dashboard lo maneje
      setTimeout(() => {
        this.isExporting = false;
        console.log('📊 Exportación delegada al dashboard - Spinner desactivado');
      }, 500); // 500ms para que el usuario vea el feedback visual
    },
    getCurrentDateTime() {
      return new Date().toLocaleString('es-CL');
    },
    
    // NUEVO: Método para debuggear estructura de datos real
    debugDataStructure(dataSource) {
      if (!dataSource || dataSource.length === 0) {
        console.log('🔍 Debug: No hay datos para analizar');
        return;
      }
      
      console.log('🔍 =========================');
      console.log('🔍 DEBUGGING ESTRUCTURA DE DATOS');
      console.log('🔍 =========================');
      
      // Analizar primera venta como muestra
      const firstSale = dataSource[0];
      console.log('📋 Muestra de venta (primera):', {
        id: firstSale._id,
        idTransaccion: firstSale.idTransaccion,
        fechaHora: firstSale.fechaHora,
        nombreSucursal: firstSale.nombreSucursal,
        nombreVendedor: firstSale.nombreVendedor,
        nombreCliente: firstSale.nombreCliente,
        metodoPago: firstSale.metodoPago,
        ventaTotal: firstSale.ventaTotal,
        estadoVenta: firstSale.estadoVenta,
        tieneInfoVenta: !!firstSale.infoVenta
      });
      
      // Analizar infoVenta si existe
      if (firstSale.infoVenta) {
        console.log('💰 infoVenta estructura:', {
          tieneVenta: !!firstSale.infoVenta.venta,
          ventaLength: firstSale.infoVenta.venta ? firstSale.infoVenta.venta.length : 0,
          tieneDetalleVenta: !!firstSale.infoVenta.detalleVenta,
          detalleVentaLength: firstSale.infoVenta.detalleVenta ? firstSale.infoVenta.detalleVenta.length : 0,
          tienePlan: !!firstSale.infoVenta.plan,
          planLength: firstSale.infoVenta.plan ? firstSale.infoVenta.plan.length : 0
        });
        
        // Analizar detalleVenta si existe
        if (firstSale.infoVenta.detalleVenta && firstSale.infoVenta.detalleVenta.length > 0) {
          const firstItem = firstSale.infoVenta.detalleVenta[0];
          console.log('🛒 Muestra de detalleVenta:', {
            codigo: firstItem.codigo,
            detalle: firstItem.detalle,
            categoriaProducto: firstItem.categoriaProducto,
            cantidad: firstItem.cantidad,
            precio: firstItem.precio,
            todasLasClaves: Object.keys(firstItem)
          });
        } else {
          console.log('🛒 detalleVenta está vacío o no existe');
        }
        
        // Analizar plan si existe
        if (firstSale.infoVenta.plan && firstSale.infoVenta.plan.length > 0) {
          const firstPlan = firstSale.infoVenta.plan[0];
          console.log('🏋️ Muestra de plan:', {
            descripcionPlan: firstPlan.descripcionPlan,
            familiaPlan: firstPlan.familiaPlan,
            modalidad: firstPlan.modalidad,
            duracion: firstPlan.duracion,
            monto: firstPlan.monto,
            todasLasClaves: Object.keys(firstPlan)
          });
        } else {
          console.log('🏋️ plan está vacío o no existe');
        }
      } else {
        console.log('❌ infoVenta no existe en esta venta');
      }
      
      // Estadísticas generales del dataset
      const stats = {
        totalVentas: dataSource.length,
        ventasConVendedor: dataSource.filter(s => s.nombreVendedor && s.nombreVendedor.trim()).length,
        ventasConInfoVenta: dataSource.filter(s => s.infoVenta).length,
        ventasConDetalleVenta: dataSource.filter(s => s.infoVenta && s.infoVenta.detalleVenta && s.infoVenta.detalleVenta.length > 0).length,
        ventasConPlan: dataSource.filter(s => s.infoVenta && s.infoVenta.plan && s.infoVenta.plan.length > 0).length,
        ventasConMontoValido: dataSource.filter(s => this.parseMonetaryValue(s.ventaTotal) > 0).length
      };
      
      console.log('📊 Estadísticas del dataset:', stats);
      console.log('🔍 =========================');
    },
    
    // NUEVO v2.3: Método para mostrar información de los datos analizados en gráficos
    getChartDataSourceInfo() {
      const dataSource = this.allFilteredRecords && this.allFilteredRecords.length > 0 
        ? this.allFilteredRecords 
        : this.records;
        
      if (!dataSource || dataSource.length === 0) {
        return 'Sin datos para analizar';
      }
      
      const totalRecords = dataSource.length;
      const isGlobal = this.allFilteredRecords && this.allFilteredRecords.length > 0;
      
      if (isGlobal) {
        return `Analizando ${totalRecords.toLocaleString()} ventas filtradas (vista global)`;
      } else {
        return `Analizando ${totalRecords.toLocaleString()} ventas (página actual)`;
      }
    },
    
    /* 
    ❌ MÉTODO DESACTIVADO v2.4 - Sistema multi-hoja implementado
    
    // Método de exportación directa (NO SE UTILIZA - Reemplazado por sistema multi-hoja)
    async exportToExcel() {
      try {
        this.isExporting = true;
        
        if (process.env.NODE_ENV === 'development') {
          // Simulación en desarrollo
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          this.$swal.fire({
            icon: 'success',
            title: '¡Exportación exitosa!',
            text: 'Los datos se han exportado correctamente (modo desarrollo)',
            confirmButtonColor: '#6bd098'
          });
        } else {
          const response = await this.$CentralGymOPS_API.post('', {
            Parameters: {
              filters: this.filters,
              sorting: this.sorting,
              format: 'excel'
            }
          }, {
            headers: {
              token: sessionStorage.getItem("jCxbraoQeD"),
              operation: "/v1/sales/export/gym"
            },
            responseType: 'blob'
          });
          
          // Crear y descargar archivo
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `ventas_gimnasio_${new Date().toISOString().split('T')[0]}.xlsx`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          
          this.$swal.fire({
            icon: 'success',
            title: '¡Exportación exitosa!',
            text: 'El archivo se ha descargado correctamente',
            confirmButtonColor: '#6bd098'
          });
        }
      } catch (error) {
        console.error('Error al exportar:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error al exportar',
          text: 'No se pudo generar el archivo de exportación. Por favor, intenta nuevamente.',
          confirmButtonColor: '#6bd098'
        });
      } finally {
        this.isExporting = false;
      }
    },
    */
    // Métodos de selección múltiple
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedItems = [];
      } else {
        this.selectedItems = this.records.map(record => record._id.$oid);
      }
    },
    updateAllSelectedState() {
      if (!this.records || this.records.length === 0) {
        this.allSelected = false;
        return;
      }
      const allIds = this.records.map(record => record._id.$oid);
      this.allSelected = allIds.length > 0 && allIds.every(id => this.selectedItems.includes(id));
    },
    clearSelection() {
      this.selectedItems = [];
    },
    // Métodos de acciones en lote
    async confirmBulkCancel() {
      const result = await this.$swal.fire({
        title: '¿Estás seguro?',
        text: `¿Deseas anular ${this.selectedValidSales} venta(s) seleccionada(s)?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: `Sí, anular ${this.selectedValidSales}`,
        cancelButtonText: 'Cancelar'
      });

      if (result.isConfirmed) {
        await this.bulkCancelSales();
      }
    },
    async bulkCancelSales() {
      try {
        this.isBulkProcessing = true;
        
        // ACTUALIZADO v2.3: Obtener IDs de ventas vigentes seleccionadas (estadoVenta vacío)
        const validSalesToCancel = this.selectedItems.filter(itemId => {
          const sale = this.records.find(record => record._id.$oid === itemId);
          const estado = sale?.estadoVenta;
          return sale && (estado === '' || estado === null || estado === undefined);
        });

        this.$swal.fire({
          title: "Anulando ventas...",
          text: `Procesando ${validSalesToCancel.length} venta(s)`,
          showConfirmButton: false,
          allowOutsideClick: false,
          didOpen: () => {
            this.$swal.showLoading();
          },
        });

        if (process.env.NODE_ENV === 'development') {
          // Simulación en desarrollo
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Actualizar estado en los registros
          validSalesToCancel.forEach(saleId => {
            const saleIndex = this.records.findIndex(record => record._id.$oid === saleId);
            if (saleIndex !== -1) {
              this.records[saleIndex].estadoVenta = '[ANULADA]';
            }
          });

          this.$swal.fire({
            icon: "success",
            title: "¡Éxito!",
            text: `${validSalesToCancel.length} venta(s) anulada(s) correctamente`,
            confirmButtonColor: "#6bd098",
          });
        } else {
          // Llamada real a la API en producción
          const response = await this.$CentralGymOPS_API.post('', {
            Parameters: {
              saleIds: validSalesToCancel
            }
          }, {
            headers: {
              "Content-Type": "application/json",
              token: sessionStorage.getItem("jCxbraoQeD"),
              operation: "/v1/sales/bulk/cancel"
            }
          });

          if (response.data && response.data.success) {
            // Actualizar estado en los registros
            validSalesToCancel.forEach(saleId => {
              const saleIndex = this.records.findIndex(record => record._id.$oid === saleId);
              if (saleIndex !== -1) {
                this.records[saleIndex].estadoVenta = '[ANULADA]';
              }
            });

            this.$swal.fire({
              icon: "success",
              title: "¡Éxito!",
              text: `${validSalesToCancel.length} venta(s) anulada(s) correctamente`,
              confirmButtonColor: "#6bd098",
            });
          }
        }

        // Limpiar selección
        this.clearSelection();

      } catch (error) {
        console.error('Error en anulación masiva:', error);
        this.$swal.fire({
          icon: "error",
          title: "Error en anulación masiva",
          text: "No se pudieron anular algunas ventas. Por favor, intenta nuevamente.",
          confirmButtonColor: "#6bd098",
        });
      } finally {
        this.isBulkProcessing = false;
      }
    },
    async exportSelectedToExcel() {
      try {
        this.isExporting = true;
        
        const selectedSales = this.records.filter(record => 
          this.selectedItems.includes(record._id.$oid)
        );

        if (process.env.NODE_ENV === 'development') {
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          this.$swal.fire({
            icon: 'success',
            title: '¡Exportación exitosa!',
            text: `${selectedSales.length} ventas seleccionadas exportadas (modo desarrollo)`,
            confirmButtonColor: '#6bd098'
          });
        } else {
          const response = await this.$CentralGymOPS_API.post('', {
            Parameters: {
              saleIds: this.selectedItems,
              format: 'excel'
            }
          }, {
            headers: {
              token: sessionStorage.getItem("jCxbraoQeD"),
              operation: "/v1/sales/export/selected"
            },
            responseType: 'blob'
          });
          
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `ventas_seleccionadas_${new Date().toISOString().split('T')[0]}.xlsx`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          
          this.$swal.fire({
            icon: 'success',
            title: '¡Exportación exitosa!',
            text: `${selectedSales.length} ventas seleccionadas exportadas correctamente`,
            confirmButtonColor: '#6bd098'
          });
        }
      } catch (error) {
        console.error('Error al exportar selección:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error al exportar',
          text: 'No se pudo exportar la selección. Por favor, intenta nuevamente.',
          confirmButtonColor: '#6bd098'
        });
      } finally {
        this.isExporting = false;
      }
    },
    // Método para obtener rango de fechas para PDF
    obtenerRangoFechasParaPDF() {
      try {
        let fechaDesde = null;
        let fechaHasta = null;
        
        // 1. Verificar si hay fechas en filtros avanzados
        if (this.filters.dateFrom && this.filters.dateTo) {
          fechaDesde = this.filters.dateFrom;
          fechaHasta = this.filters.dateTo;
          console.log('📅 Usando fechas de filtros avanzados:', { fechaDesde, fechaHasta });
        } 
        // 2. Si no hay filtros de fecha, obtener del primer y último registro
        else if (this.allFilteredRecords && this.allFilteredRecords.length > 0) {
          // Obtener todas las fechas y ordenarlas
          const fechasRegistros = this.allFilteredRecords
            .map(record => record.fechaHora)
            .filter(fecha => fecha) // Filtrar fechas válidas
            .sort(); // Ordenar cronológicamente
          
          if (fechasRegistros.length > 0) {
            const primeraFecha = fechasRegistros[0];
            const ultimaFecha = fechasRegistros[fechasRegistros.length - 1];
            
            // Convertir a formato YYYY-MM-DD para procesamiento
            fechaDesde = this.convertirFechaAPIaISO(primeraFecha);
            fechaHasta = this.convertirFechaAPIaISO(ultimaFecha);
            
            console.log('📅 Usando fechas de registros en memoria:', { 
              primeraFecha, 
              ultimaFecha, 
              fechaDesde, 
              fechaHasta 
            });
          }
        }
        
        // 3. Formatear las fechas para mostrar en título
        if (fechaDesde && fechaHasta) {
          const fechaDesdeFormateada = this.formatearFechaParaTitulo(fechaDesde);
          const fechaHastaFormateada = this.formatearFechaParaTitulo(fechaHasta);
          
          // Si las fechas son iguales, mostrar solo una fecha
          if (fechaDesde === fechaHasta) {
            return fechaDesdeFormateada;
          } else {
            return `${fechaDesdeFormateada} al ${fechaHastaFormateada}`;
          }
        } else {
          return '';
        }
      } catch (error) {
        console.error('❌ Error al obtener rango de fechas para PDF:', error);
        return 'Período no disponible';
      }
    },
    
    // Método auxiliar para convertir fecha de API (DD-MM-YYYY o DD/MM/YYYY HH:mm:ss) a ISO (YYYY-MM-DD)
    convertirFechaAPIaISO(fechaAPI) {
      if (!fechaAPI) return null;
      
      try {
        // Extraer solo la parte de fecha (antes del espacio si hay hora)
        const soloFecha = fechaAPI.split(' ')[0];
        
        // Detectar formato DD-MM-YYYY o DD/MM/YYYY
        let partes;
        if (soloFecha.includes('-')) {
          partes = soloFecha.split('-');
        } else if (soloFecha.includes('/')) {
          partes = soloFecha.split('/');
        } else {
          return null;
        }
        
        if (partes.length === 3) {
          const dia = partes[0].padStart(2, '0');
          const mes = partes[1].padStart(2, '0');
          const año = partes[2];
          
          return `${año}-${mes}-${dia}`;
        }
        
        return null;
      } catch (error) {
        console.error('❌ Error al convertir fecha API a ISO:', error);
        return null;
      }
    },
    
    // Método auxiliar para formatear fecha en formato DD-MM-YYYY
    formatearFechaParaTitulo(fechaISO) {
      if (!fechaISO) return '';
      
      try {
        const fecha = new Date(fechaISO + 'T00:00:00'); // Agregar hora para evitar problemas de zona horaria
        const dia = fecha.getDate().toString().padStart(2, '0');
        const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
        const año = fecha.getFullYear();
        
        return `${dia}-${mes}-${año}`;
      } catch (error) {
        console.error('❌ Error al formatear fecha para título:', error);
        return fechaISO;
      }
    },

    // Método helper para parsear valores monetarios en formato chileno
    parseMonetaryValue(value) {
      if (typeof value === 'number') return value;
      if (!value) return 0;
      
      // Convertir a string y limpiar espacios
      let stringValue = String(value).trim();
      
      // Remover símbolo de moneda si existe
      stringValue = stringValue.replace(/[$]/g, '');
      
      // Si el valor contiene puntos y comas, asumir formato chileno (1.500.000,50)
      if (stringValue.includes('.') && stringValue.includes(',')) {
        // Formato chileno: punto para miles, coma para decimales
        stringValue = stringValue.replace(/\./g, '').replace(',', '.');
      } else if (stringValue.includes('.') && !stringValue.includes(',')) {
        // Verificar si es formato chileno sin decimales (1.500.000) o formato US (1500.50)
        const parts = stringValue.split('.');
        if (parts.length === 2 && parts[1].length > 2) {
          // Probablemente formato chileno (1.500.000)
          stringValue = stringValue.replace(/\./g, '');
        }
        // Si tiene 2 decimales o menos, asumir formato US
      } else if (stringValue.includes(',') && !stringValue.includes('.')) {
        // Solo comas, puede ser formato US (1,500) o chileno con decimales (1500,50)
        const parts = stringValue.split(',');
        if (parts.length === 2 && parts[1].length <= 2) {
          // Formato chileno con decimales (1500,50)
          stringValue = stringValue.replace(',', '.');
        } else {
          // Formato US con separador de miles (1,500)
          stringValue = stringValue.replace(/,/g, '');
        }
      }
      
      const parsed = parseFloat(stringValue);
      return isNaN(parsed) ? 0 : parsed;
    },

    // Método helper para formatear moneda para gráficos
    formatCurrencyForCharts(amount) {
      if (!amount || amount === 0) return '$0';
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    },

    // Métodos de exportación PDF - KPIs y Gráficos
    async exportToPDF() {
      console.log('🎯 Iniciando proceso de exportación PDF...');
      this.isExporting = true;
      
      // Validar que hay datos para exportar (desde métricas del servidor o registros)
      if (!this.hasDataForKPIs) {
        console.warn('⚠️ No hay datos disponibles para exportar');
        this.$swal.fire({
          icon: 'warning',
          title: 'Sin datos para exportar',
          text: 'No hay información de ventas disponible para generar el reporte de KPIs.',
          confirmButtonColor: '#6bd098'
        });
        this.isExporting = false;
        return false;
      }

      // Verificar estado del DOM antes de proceder
      const domCheck = this.checkDOMStateForExport();
      console.log('🔍 Estado del DOM:', domCheck);
      
      if (!domCheck.kpisAvailable) {
        console.error('❌ Sección de KPIs no disponible en el DOM');
        this.$swal.fire({
          icon: 'error',
          title: 'Error de visualización',
          text: 'La sección de KPIs no está visible. Por favor, recarga la página e intenta nuevamente.',
          confirmButtonColor: '#6bd098'
        });
        this.isExporting = false;
        return false;
      }

      // Mostrar modal de progreso
      this.$swal.fire({
        title: 'Generando Reporte KPIs',
        text: 'Preparando exportación...',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          this.$swal.showLoading();
        }
      });

      try {
        // Esperar un momento para asegurar que el DOM esté estable
        await this.$nextTick();
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const ok = await this.exportKPIsToPDF();
        this.isExporting = false;
        return ok;
      } catch (error) {
        console.error('❌ Error inesperado en exportToPDF:', error);
        this.isExporting = false;
        this.$swal.close();
        this.$swal.fire({
          icon: 'error',
          title: 'Error inesperado',
          text: 'Ocurrió un error inesperado. Por favor, intenta nuevamente.',
          confirmButtonColor: '#6bd098'
        });
        return false;
      }
    },

    // Verificar estado del DOM antes de exportar
    checkDOMStateForExport() {
      const state = {
        kpisAvailable: false,
        chartsAvailable: false,
        kpisElement: null,
        chartsElement: null,
        hasData: this.hasDataForKPIs,
        showChartsPanel: this.showChartsPanel
      };

      // Verificar sección de KPIs
      if (this.$refs.kpisSection) {
        state.kpisAvailable = true;
        state.kpisElement = {
          width: this.$refs.kpisSection.offsetWidth,
          height: this.$refs.kpisSection.offsetHeight,
          visible: this.$refs.kpisSection.offsetParent !== null
        };
      }

      // Verificar sección de gráficos
      if (this.$refs.chartsSection) {
        state.chartsAvailable = true;
        state.chartsElement = {
          width: this.$refs.chartsSection.offsetWidth,
          height: this.$refs.chartsSection.offsetHeight,
          visible: this.$refs.chartsSection.offsetParent !== null
        };
      }

      return state;
    },

    async exportKPIsToPDF() {
      try {
        console.log('📊 Iniciando exportación de KPIs a PDF...');
        
        // Importar dinámicamente las librerías necesarias
        let html2canvas, jsPDF;
        try {
          console.log('📚 Importando librerías para PDF...');
          const html2canvasModule = await import('html2canvas');
          html2canvas = html2canvasModule.default || html2canvasModule;
          
          const jsPDFModule = await import('jspdf');
          // jsPDF v3 exporta diferente, intentar ambas formas
          jsPDF = jsPDFModule.jsPDF || jsPDFModule.default || jsPDFModule;
          
          console.log('✅ Librerías importadas:', { 
            html2canvas: typeof html2canvas, 
            jsPDF: typeof jsPDF 
          });
        } catch (importError) {
          console.error('❌ Error al importar librerías PDF:', importError);
          throw new Error('No se pudieron cargar las librerías necesarias para generar el PDF');
        }

        // Verificar que existen las secciones a capturar
        if (!this.$refs.kpisSection) {
          console.warn('⚠️ No se encontró la sección de KPIs para capturar');
          throw new Error('No se encontró la sección de KPIs para exportar');
        }

        // Asegurar que los gráficos estén visibles para capturarlos si existen
        const wasChartsVisible = this.showChartsPanel;
        if (!this.showChartsPanel && (this.serverCharts || this.chartData)) {
          console.log('📊 Mostrando gráficos temporalmente para captura...');
          this.showChartsPanel = true;
          await this.$nextTick();
          // Esperar a que las animaciones/render de Chart.js terminen
          await this.waitForChartsToSettle(2000);
        }

        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 15;
        const contentWidth = pageWidth - (margin * 2);
        let yPosition = margin;

        // 1. Agregar título y metadata
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('REPORTE KPIs - VENTAS DE SUCURSALES', pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 10;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        const fechaGeneracion = new Date().toLocaleDateString('es-CL', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
        pdf.text(`Generado: ${fechaGeneracion}`, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 15;

        // 2. Capturar y agregar KPIs
        try {
          this.$swal.update({ text: 'Capturando KPIs...' });
          console.log('📸 Capturando sección de KPIs...');
          
          const kpisCanvas = await html2canvas(this.$refs.kpisSection, {
            allowTaint: true,
            useCORS: true,
            scale: 3,
            backgroundColor: '#ffffff',
            logging: false
          });

          const kpisImgData = kpisCanvas.toDataURL('image/png');
          const kpisHeight = (kpisCanvas.height * contentWidth) / kpisCanvas.width;

          // Verificar si cabe en la página actual
          if (yPosition + kpisHeight > pageHeight - margin) {
            pdf.addPage();
            yPosition = margin;
          }

          pdf.addImage(kpisImgData, 'PNG', margin, yPosition, contentWidth, kpisHeight);
          yPosition += kpisHeight + 10;
          console.log('✅ KPIs capturados exitosamente');
        } catch (kpisError) {
          console.error('❌ Error al capturar KPIs:', kpisError);
          throw new Error('Error al capturar la sección de KPIs');
        }

        // 3. Capturar y agregar gráficos (si están visibles)
        if (this.$refs.chartsSection && this.showChartsPanel) {
          try {
            this.$swal.update({ text: 'Capturando gráficos...' });
            console.log('📸 Capturando sección de gráficos...');
            
            const chartsCanvas = await html2canvas(this.$refs.chartsSection, {
              allowTaint: true,
              useCORS: true,
              scale: 3,
              backgroundColor: '#ffffff',
              logging: false
            });

            const chartsImgData = chartsCanvas.toDataURL('image/png');
            const chartsHeight = (chartsCanvas.height * contentWidth) / chartsCanvas.width;

            // Verificar si cabe en la página actual
            if (yPosition + chartsHeight > pageHeight - margin) {
              pdf.addPage();
              yPosition = margin;
              
              // Agregar título de sección en nueva página
              pdf.setFontSize(14);
              pdf.setFont('helvetica', 'bold');
              pdf.text('ANÁLISIS VISUAL DE VENTAS', margin, yPosition);
              yPosition += 10;
            }

            pdf.addImage(chartsImgData, 'PNG', margin, yPosition, contentWidth, chartsHeight);
            yPosition += chartsHeight + 10;
            console.log('✅ Gráficos capturados exitosamente');
          } catch (chartsError) {
            console.warn('⚠️ Error al capturar gráficos (continuando sin ellos):', chartsError);
            // No lanzar error aquí, continuar sin los gráficos
          }
        }

        // 4. Agregar información adicional en pie de página
        const totalPages = pdf.internal.getNumberOfPages();
        const totalAnalizados = this.serverKPIs?.totalSales ?? (this.allFilteredRecords?.length || this.records?.length || 0);
        
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          
          // Información izquierda: Total registros
          pdf.text(`Total registros analizados: ${totalAnalizados}`, margin, pageHeight - 5);
          
          // Información centro: Texto de CentralGym
          pdf.text('Informe generado desde CentralGym App - https://centralgym.cl', pageWidth / 2, pageHeight - 5, { align: 'center' });
          
          // Información derecha: Numeración de páginas
          pdf.text(`Página ${i} de ${totalPages}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
        }

        // 5. Generar nombre de archivo similar al Excel
        const fechaArchivo = this.$moment ? this.$moment().format("DD-MM-yyyy - HH-mm-ss") : 
                           new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        const nombreArchivo = `KPIs Ventas Gimnasio - ${fechaArchivo}.pdf`;

        // 6. Descargar PDF
        this.$swal.update({ text: 'Generando archivo PDF...' });
        console.log('💾 Guardando PDF:', nombreArchivo);
        pdf.save(nombreArchivo);

        // 7. Restaurar estado de gráficos si era necesario
        if (!wasChartsVisible) {
          this.showChartsPanel = false;
        }

        // 8. Cerrar cualquier modal de carga antes de mostrar el de éxito
        this.$swal.close();

        // 9. Esperar un momento para asegurar que el PDF se guardó
        await new Promise(resolve => setTimeout(resolve, 500));

        // 10. Mostrar confirmación
        this.$swal.fire({
          icon: 'success',
          title: '¡Exportación Exitosa!',
          html: `
            <div class="text-left">
              <p><strong>✅ El reporte de KPIs se ha generado correctamente</strong></p>
              <p><strong>📁 Archivo:</strong> ${nombreArchivo}</p>
              <p><strong>📊 Contenido incluido:</strong></p>
              <ul>
                <li>📈 KPIs principales: Total Ventas, Monto Total, Ventas Vigentes/Anuladas</li>
                ${this.showChartsPanel ? '<li>📊 Gráficos de análisis visual</li>' : ''}
                <li>📋 Total de registros analizados: ${totalAnalizados.toLocaleString('es-CL')}</li>
              </ul>
              <p class="text-muted"><small>El archivo se ha descargado en tu carpeta de descargas</small></p>
            </div>
          `,
          confirmButtonColor: '#6bd098',
          confirmButtonText: 'Excelente',
          timer: 10000,
          timerProgressBar: true
        });

        console.log('✅ Exportación KPIs a PDF completada exitosamente:', {
          archivo: nombreArchivo,
          registrosAnalizados: totalAnalizados,
          graficosIncluidos: this.showChartsPanel,
          kpisIncluidos: true,
          totalPaginas: totalPages
        });
        
        return true;
      } catch (error) {
        console.error('❌ Error en exportKPIsToPDF:', error);
        
        // Cerrar cualquier modal de carga
        this.$swal.close();
        
        // Mostrar error específico
        const errorMessage = error.message || 'Error desconocido al generar el PDF';
        
        this.$swal.fire({
          icon: 'error',
          title: 'Error al exportar PDF',
          html: `
            <div class="text-left">
              <p><strong>No se pudo generar el reporte de KPIs</strong></p>
              <p class="text-danger">${errorMessage}</p>
              <p><strong>Posibles soluciones:</strong></p>
              <ul>
                <li>Verifica que haya datos cargados en la tabla</li>
                <li>Intenta recargar la página y volver a intentar</li>
                <li>Si el problema persiste, contacta a soporte técnico</li>
              </ul>
            </div>
          `,
          confirmButtonColor: '#6bd098',
          confirmButtonText: 'Entendido'
        });
        
        return false;
      }
    },
    // Métodos para gráficos en pantalla completa
    openChartFullscreen(chartType, title) {
      console.log('🖼️ Abriendo gráfico en pantalla completa:', chartType, title);
      
      // Configurar el modal según el tipo de gráfico
      this.fullscreenChart.type = chartType;
      this.fullscreenChart.title = title;
      this.fullscreenChart.show = true;
      
      // Obtener los datos según el tipo de gráfico
      let chartData = [];
      let labelColumn = 'Categoría';
      let valueColumn = 'Valor';
      let showColorIndicator = true;
      
      switch(chartType) {
        case 'status':
          chartData = this.chartData.salesByStatus.map((item, index) => ({
            label: item.label,
            value: item.amount || item.value, // Preferir amount si está disponible
            count: item.value,
            color: this.getStatusColor(item.label),
            hasAmount: !!item.amount
          }));
          labelColumn = 'Estado';
          valueColumn = chartData.some(d => d.hasAmount) ? 'Monto Total' : 'Cantidad de Ventas';
          break;
          
        case 'method':
          chartData = this.chartData.salesByMethod.map((item, index) => ({
            label: item.label,
            value: item.amount || item.value, // Preferir amount si está disponible
            count: item.value,
            color: this.getPaymentMethodColor(index),
            hasAmount: !!item.amount
          }));
          labelColumn = 'Método de Pago';
          valueColumn = chartData.some(d => d.hasAmount) ? 'Monto Total' : 'Cantidad de Ventas';
          break;
          
        case 'branch':
          chartData = this.chartData.salesByBranch.map((item, index) => ({
            label: item.label,
            value: item.amount || item.value, // Preferir amount si está disponible
            count: item.value,
            color: this.getBranchColor(index),
            hasAmount: !!item.amount
          }));
          labelColumn = 'Sucursal';
          valueColumn = chartData.some(d => d.hasAmount) ? 'Monto Total' : 'Cantidad de Ventas';
          break;
          
        case 'trend':
          chartData = this.chartData.dailyTrend.map((item, index) => ({
            label: item.label, // Fecha en formato YYYY-MM-DD
            value: item.amount || 0, // Monto monetario para el gráfico
            count: item.value || 0, // Cantidad de ventas
            color: '#6bd098',
            hasAmount: true // Siempre mostramos montos en tendencia
          }));
          labelColumn = 'Fecha';
          valueColumn = 'Monto Total';
          showColorIndicator = false;
          break;
          
        case 'seller':
          chartData = this.chartData.salesBySeller.map((item, index) => ({
            label: item.label,
            value: item.amount || item.value, // Preferir amount si está disponible
            count: item.value,
            color: this.getSellerColor(index),
            hasAmount: !!item.amount
          }));
          labelColumn = 'Vendedor';
          valueColumn = chartData.some(d => d.hasAmount) ? 'Monto Total' : 'Cantidad de Ventas';
          break;
          
        case 'category':
          chartData = this.chartData.salesByCategory.map((item, index) => ({
            label: item.label,
            value: item.amount || item.value, // Preferir amount si está disponible
            count: item.value,
            color: this.getCategoryColor(index),
            hasAmount: !!item.amount
          }));
          labelColumn = 'Categoría de Producto';
          valueColumn = chartData.some(d => d.hasAmount) ? 'Monto Total' : 'Cantidad Vendida';
          break;
          
        case 'planFamily':
          chartData = this.chartData.salesByPlanFamily.map((item, index) => ({
            label: item.label,
            value: item.amount || item.value, // Preferir amount si está disponible
            count: item.value,
            color: this.getPlanFamilyColor(index),
            hasAmount: !!item.amount
          }));
          labelColumn = 'Plan';
          valueColumn = chartData.some(d => d.hasAmount) ? 'Monto Total' : 'Cantidad de Ventas';
          break;
          
        case 'amount':
          chartData = this.chartData.amountDistribution.map((item, index) => ({
            label: item.label,
            value: item.value || 0, // Cantidad de ventas en el rango
            amount: item.totalAmount || item.amount || 0, // Monto total del rango
            color: this.getAmountColor(index),
            hasAmount: true,
            isAmountDistribution: true // Flag especial para este gráfico
          }));
          labelColumn = 'Rango de Monto';
          valueColumn = 'Cantidad de Ventas';
          break;
          
        case 'modality':
          chartData = this.chartData.salesByModality.map((item, index) => ({
            label: item.label === 'Sin tipo' ? 'Otras Ventas sin Membresías' : item.label,
            value: item.amount || 0, // Monto total para el gráfico
            count: item.value || 0, // Cantidad de ventas
            color: this.getModalityColor(index),
            hasAmount: true // Siempre mostramos montos
          }));
          labelColumn = 'Tipo de Plan';
          valueColumn = 'Monto Total';
          break;
          
        case 'cashier':
          chartData = this.chartData.salesByCashier.map((item, index) => ({
            label: item.label,
            value: item.amount || 0, // Monto total para el gráfico
            count: item.value || 0, // Cantidad de ventas
            color: this.getCashierColor(index),
            hasAmount: true // Siempre mostramos montos
          }));
          labelColumn = 'Caja/POS';
          valueColumn = 'Monto Total';
          break;
          
        case 'discountCategory':
          chartData = this.chartData.discountsByCategory.map((item, index) => ({
            label: item.label,
            value: item.amount || 0, // Monto total de descuentos
            count: item.value || 0, // Cantidad de ventas con descuento
            color: this.getDiscountCategoryColor(index),
            hasAmount: true
          }));
          labelColumn = 'Categoría';
          valueColumn = 'Total Descuentos';
          break;
          
        case 'discountTrend':
          chartData = this.chartData.discountsDailyTrend.map((item, index) => ({
            label: item.label || item.date, // Usar label que es el campo correcto
            value: item.totalDiscount || item.amount,
            color: '#ff6b6b',
            hasAmount: true // Los descuentos siempre son montos
          }));
          labelColumn = 'Fecha';
          valueColumn = 'Total Descuentos';
          showColorIndicator = false;
          break;
      }
      
      this.fullscreenChart.data = chartData;
      this.fullscreenChart.labelColumn = labelColumn;
      this.fullscreenChart.valueColumn = valueColumn;
      this.fullscreenChart.showColorIndicator = showColorIndicator;
      
      console.log('📊 Datos preparados para pantalla completa:', {
        tipo: chartType,
        datos: chartData,
        totalElementos: chartData.length,
        labelColumn,
        valueColumn
      });
      
      // Esperar a que el modal esté renderizado
      this.$nextTick(() => {
        // Esperar un poco más para asegurar que el modal esté completamente visible
        setTimeout(() => {
          // Siempre usar el método createFullscreenChartFromData que es más confiable
          const canvas = this.$refs.fullscreenChartCanvas;
          if (canvas) {
            this.createFullscreenChartFromData(canvas, chartType);
          } else {
            console.error('❌ Canvas no disponible para renderizar el gráfico');
          }
        }, 300);
      });
    },
    
    renderFullscreenChart(chartType) {
      // Verificar que Chart.js esté disponible
      if (typeof Chart === 'undefined') {
        console.error('❌ Chart.js no está disponible');
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'La librería de gráficos no está cargada correctamente',
          confirmButtonColor: '#6bd098'
        });
        return;
      }
      
      const canvas = this.$refs.fullscreenChartCanvas;
      if (!canvas) {
        console.error('❌ No se encontró el canvas para el gráfico en pantalla completa');
        // Intentar nuevamente después de un breve delay
        setTimeout(() => {
          const retryCanvas = this.$refs.fullscreenChartCanvas;
          if (retryCanvas) {
            console.log('🔄 Canvas encontrado en segundo intento');
            this.renderFullscreenChart(chartType);
          }
        }, 100);
        return;
      }
      
      console.log('📊 Canvas encontrado, preparando renderizado...');
      
      // Destruir instancia anterior si existe
      if (this.fullscreenChart.instance) {
        try {
          this.fullscreenChart.instance.destroy();
          this.fullscreenChart.instance = null;
        } catch (e) {
          console.warn('⚠️ Error al destruir gráfico anterior:', e);
        }
      }
      
      // Obtener la instancia del gráfico original
      const chartInstance = this.chartInstances[chartType + 'Chart'];
      
      if (!chartInstance || !chartInstance.config) {
        console.warn('⚠️ No se encontró la instancia del gráfico:', chartType);
        console.log('📊 Datos disponibles:', {
          chartType: chartType,
          dataAvailable: this.chartData[this.getChartDataKey(chartType)] ? 'Sí' : 'No',
          dataLength: this.chartData[this.getChartDataKey(chartType)]?.length || 0
        });
        // Intentar crear el gráfico desde los datos
        this.createFullscreenChartFromData(canvas, chartType);
        return;
      }
      
      console.log('📊 Renderizando gráfico en pantalla completa:', chartType);
      console.log('📊 Configuración original del gráfico:', {
        type: chartInstance.config.type,
        hasData: !!chartInstance.config.data,
        labels: chartInstance.config.data?.labels?.length || 0,
        datasets: chartInstance.config.data?.datasets?.length || 0
      });
      
      // Crear una nueva configuración basada en el gráfico original (sin clonar profundamente)
      const originalData = chartInstance.config.data;
      
      // Verificar que tengamos datos válidos
      if (!originalData || !originalData.labels || !originalData.datasets) {
        console.error('❌ Datos del gráfico original no válidos');
        this.createFullscreenChartFromData(canvas, chartType);
        return;
      }
      
      const chartConfig = {
        type: chartInstance.config.type,
        data: {
          labels: originalData.labels.slice(), // Copia simple del array
          datasets: originalData.datasets.map((dataset, index) => {
            // Crear un nuevo objeto dataset con solo las propiedades necesarias
            const newDataset = {
              label: dataset.label || `Serie ${index + 1}`,
              data: dataset.data ? dataset.data.slice() : []
            };
            
            // Copiar propiedades de estilo si existen
            if (dataset.backgroundColor) {
              newDataset.backgroundColor = Array.isArray(dataset.backgroundColor) 
                ? dataset.backgroundColor.slice() 
                : dataset.backgroundColor;
            }
            if (dataset.borderColor) {
              newDataset.borderColor = Array.isArray(dataset.borderColor) 
                ? dataset.borderColor.slice() 
                : dataset.borderColor;
            }
            if (dataset.borderWidth !== undefined) newDataset.borderWidth = dataset.borderWidth;
            if (dataset.fill !== undefined) newDataset.fill = dataset.fill;
            if (dataset.tension !== undefined) newDataset.tension = dataset.tension;
            
            return newDataset;
          })
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            duration: 0 // Desactivar animación para renderizado inmediato
          },
          legend: {
            position: 'bottom',
            labels: {
              fontSize: 14,
              padding: 20,
              usePointStyle: true
            }
          },
          scales: chartInstance.config.options.scales ? {
            xAxes: chartInstance.config.options.scales.xAxes ? [{
              ...chartInstance.config.options.scales.xAxes[0],
              ticks: {
                ...chartInstance.config.options.scales.xAxes[0].ticks,
                fontSize: 12
              }
            }] : undefined,
            yAxes: chartInstance.config.options.scales.yAxes ? [{
              ...chartInstance.config.options.scales.yAxes[0],
              ticks: {
                ...chartInstance.config.options.scales.yAxes[0].ticks,
                fontSize: 12
              }
            }] : undefined
          } : undefined,
          tooltips: {
            ...chartInstance.config.options.tooltips,
            enabled: true
          }
        }
      };
      
      // Crear nueva instancia del gráfico
      try {
        console.log('📊 Configuración del gráfico preparada:', {
          type: chartConfig.type,
          labels: chartConfig.data.labels.length,
          datasets: chartConfig.data.datasets.length,
          canvas: canvas ? 'Canvas disponible' : 'Canvas NO disponible'
        });
        
        // Verificar que el canvas esté disponible y tenga un contexto válido
        if (!canvas || !canvas.getContext) {
          throw new Error('Canvas no válido o no disponible');
        }
        
        this.fullscreenChart.instance = new Chart(canvas, chartConfig);
        console.log('✅ Gráfico renderizado exitosamente');
      } catch (error) {
        console.error('❌ Error al renderizar el gráfico:', error);
        console.error('Detalles del error:', {
          message: error.message,
          stack: error.stack,
          chartType: chartType,
          canvasAvailable: !!canvas,
          dataAvailable: !!chartConfig.data
        });
        
        // Intentar renderizar con el método de respaldo
        console.log('🔄 Intentando con método de respaldo...');
        this.createFullscreenChartFromData(canvas, chartType);
      }
    },
    
    // Método de respaldo para crear el gráfico desde los datos
    createFullscreenChartFromData(canvas, chartType) {
      console.log('🔧 Creando gráfico desde datos para:', chartType);
      
      let chartConfig = {
        type: 'bar', // Por defecto
        data: {
          labels: [],
          datasets: [{
            data: [],
            backgroundColor: [],
            borderColor: [],
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            duration: 0 // Desactivar animación para renderizado inmediato
          },
          legend: {
            position: 'bottom',
            labels: {
              fontSize: 14,
              padding: 20,
              usePointStyle: true
            }
          },
          tooltips: {
            enabled: true,
            mode: 'index',
            intersect: false
          }
        }
      };
      
      // Configurar según el tipo de gráfico
      switch(chartType) {
        case 'status':
          chartConfig.type = 'pie';
          chartConfig.data.labels = this.chartData.salesByStatus.map(item => item.label);
          // Usar montos si están disponibles, de lo contrario usar cantidades
          const hasAmounts = this.chartData.salesByStatus.some(item => item.amount);
          chartConfig.data.datasets[0].data = this.chartData.salesByStatus.map(item => 
            hasAmounts && item.amount ? item.amount : item.value
          );
          chartConfig.data.datasets[0].backgroundColor = this.chartData.salesByStatus.map((item, i) => 
            this.getStatusColor(item.label)
          );
          // Configurar tooltips para mostrar ambos valores
          chartConfig.options.tooltips.callbacks = {
            label: (tooltipItem, data) => {
              const item = this.chartData.salesByStatus[tooltipItem.index];
              const label = data.labels[tooltipItem.index];
              const parts = [];
              
              if (hasAmounts && item.amount) {
                // Si estamos mostrando montos en el gráfico
                parts.push(`${label}: ${this.formatCurrencyForCharts(item.amount)}`);
                parts.push(`Cantidad: ${new Intl.NumberFormat('es-CL').format(item.value)} ventas`);
              } else {
                // Si estamos mostrando cantidades en el gráfico
                parts.push(`${label}: ${new Intl.NumberFormat('es-CL').format(item.value)} ventas`);
                if (item.amount) {
                  parts.push(`Monto: ${this.formatCurrencyForCharts(item.amount)}`);
                }
              }
              
              return parts;
            }
          };
          break;
          
        case 'method':
          chartConfig.type = 'horizontalBar';
          chartConfig.data.labels = this.chartData.salesByMethod.map(item => item.label);
          const methodHasAmounts = this.chartData.salesByMethod.some(item => item.amount);
          chartConfig.data.datasets[0].data = this.chartData.salesByMethod.map(item => 
            methodHasAmounts && item.amount ? item.amount : item.value
          );
          chartConfig.data.datasets[0].backgroundColor = this.chartData.salesByMethod.map((item, i) => 
            this.getPaymentMethodColor(i)
          );
          chartConfig.data.datasets[0].label = methodHasAmounts ? 'Monto Total' : 'Cantidad de Ventas';
          // Configurar tooltips
          chartConfig.options.tooltips.callbacks = {
            label: (tooltipItem, data) => {
              const item = this.chartData.salesByMethod[tooltipItem.index];
              const parts = [];
              
              if (methodHasAmounts && item.amount) {
                parts.push(`Monto: ${this.formatCurrencyForCharts(item.amount)}`);
                parts.push(`Cantidad: ${new Intl.NumberFormat('es-CL').format(item.value)} ventas`);
              } else {
                parts.push(`Ventas: ${new Intl.NumberFormat('es-CL').format(item.value)}`);
                if (item.amount) {
                  parts.push(`Monto: ${this.formatCurrencyForCharts(item.amount)}`);
                }
              }
              
              return parts;
            }
          };
          break;
          
        case 'branch':
          chartConfig.type = 'horizontalBar';
          chartConfig.data.labels = this.chartData.salesByBranch.map(item => item.label);
          const branchHasAmounts = this.chartData.salesByBranch.some(item => item.amount);
          chartConfig.data.datasets[0].data = this.chartData.salesByBranch.map(item => 
            branchHasAmounts && item.amount ? item.amount : item.value
          );
          chartConfig.data.datasets[0].backgroundColor = this.chartData.salesByBranch.map((item, i) => 
            this.getBranchColor(i)
          );
          chartConfig.data.datasets[0].label = branchHasAmounts ? 'Monto Total' : 'Cantidad de Ventas';
          // Configurar tooltips
          chartConfig.options.tooltips.callbacks = {
            label: (tooltipItem, data) => {
              const item = this.chartData.salesByBranch[tooltipItem.index];
              const parts = [];
              
              if (branchHasAmounts && item.amount) {
                parts.push(`Monto: ${this.formatCurrencyForCharts(item.amount)}`);
                parts.push(`Cantidad: ${new Intl.NumberFormat('es-CL').format(item.value)} ventas`);
              } else {
                parts.push(`Ventas: ${new Intl.NumberFormat('es-CL').format(item.value)}`);
                if (item.amount) {
                  parts.push(`Monto: ${this.formatCurrencyForCharts(item.amount)}`);
                }
              }
              
              return parts;
            }
          };
          break;
          
        case 'trend':
          chartConfig.type = 'line';
          // Usar item.label que es el campo correcto para las fechas
          chartConfig.data.labels = this.chartData.dailyTrend.map(item => {
            // Formatear fecha para mejor visualización
            const dateLabel = item.label || item.date;
            if (dateLabel) {
              const dateParts = dateLabel.split('-');
              if (dateParts.length === 3) {
                const day = dateParts[2];
                const month = dateParts[1];
                return `${day}/${month}`;
              }
            }
            return dateLabel;
          });
          chartConfig.data.datasets[0] = {
            label: 'Monto Total Diario',
            data: this.chartData.dailyTrend.map(item => item.amount || 0),
            borderColor: '#6bd098',
            backgroundColor: 'rgba(107, 208, 152, 0.1)',
            tension: 0.4,
            fill: true
          };
          // Configurar scales para el eje Y (montos)
          chartConfig.options.scales = {
            yAxes: [{
              ticks: {
                beginAtZero: true,
                callback: (value) => {
                  return this.formatCurrencyForCharts(value);
                }
              },
              scaleLabel: {
                display: true,
                labelString: 'Monto Total'
              }
            }],
            xAxes: [{
              scaleLabel: {
                display: true,
                labelString: 'Fecha'
              }
            }]
          };
          // Configurar tooltips
          chartConfig.options.tooltips.callbacks = {
            title: (tooltipItems) => {
              // Formatear fecha completa para el tooltip
              const index = tooltipItems[0].index;
              const dateLabel = this.chartData.dailyTrend[index].label || this.chartData.dailyTrend[index].date;
              if (dateLabel) {
                const dateParts = dateLabel.split('-');
                if (dateParts.length === 3) {
                  const year = dateParts[0];
                  const month = dateParts[1];
                  const day = dateParts[2];
                  const monthNames = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 
                                    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
                  return `${day} de ${monthNames[parseInt(month) - 1]} de ${year}`;
                }
              }
              return dateLabel;
            },
            label: (tooltipItem, data) => {
              const item = this.chartData.dailyTrend[tooltipItem.index];
              const parts = [];
              
              // Mostrar monto (amount) y cantidad (value)
              parts.push(`💰 Monto: ${this.formatCurrencyForCharts(item.amount || 0)}`);
              parts.push(`📊 Cantidad de Ventas: ${new Intl.NumberFormat('es-CL').format(item.value || 0)}`);
              
              return parts;
            }
          };
          break;
          
        case 'seller':
          chartConfig.type = 'horizontalBar';
          chartConfig.data.labels = this.chartData.salesBySeller.map(item => {
            // Truncar nombres largos para mejor visualización
            return item.label.length > 25 ? item.label.substring(0, 22) + '...' : item.label;
          });
          const sellerHasAmounts = this.chartData.salesBySeller.some(item => item.amount);
          chartConfig.data.datasets[0].data = this.chartData.salesBySeller.map(item => 
            sellerHasAmounts && item.amount ? item.amount : item.value
          );
          chartConfig.data.datasets[0].backgroundColor = this.chartData.salesBySeller.map((item, i) => 
            this.getSellerColor(i)
          );
          chartConfig.data.datasets[0].label = sellerHasAmounts ? 'Monto Total' : 'Cantidad de Ventas';
          // Configurar tooltips
          chartConfig.options.tooltips.callbacks = {
            title: (tooltipItems) => {
              // Mostrar el nombre completo del vendedor en el título del tooltip
              const index = Array.isArray(tooltipItems) ? tooltipItems[0].index : tooltipItems.index;
              return this.chartData.salesBySeller[index].label;
            },
            label: (tooltipItem, data) => {
              const item = this.chartData.salesBySeller[tooltipItem.index];
              const parts = [];
              
              if (sellerHasAmounts && item.amount) {
                parts.push(`💰 Monto: ${this.formatCurrencyForCharts(item.amount)}`);
                parts.push(`📊 Cantidad: ${new Intl.NumberFormat('es-CL').format(item.value)} ventas`);
              } else {
                parts.push(`📊 Ventas: ${new Intl.NumberFormat('es-CL').format(item.value)}`);
                if (item.amount) {
                  parts.push(`💰 Monto: ${this.formatCurrencyForCharts(item.amount)}`);
                }
              }
              
              // Calcular porcentaje
              const total = this.chartData.salesBySeller.reduce((sum, item) => {
                if (sellerHasAmounts && item.amount) {
                  return sum + item.amount;
                }
                return sum + item.value;
              }, 0);
              const currentValue = sellerHasAmounts && item.amount ? item.amount : item.value;
              const percentage = total > 0 ? ((currentValue / total) * 100).toFixed(1) : '0';
              parts.push(`📈 Representa el ${percentage}% del total`);
              
              return parts;
            }
          };
          
          // Configurar opciones específicas para gráfico horizontal
          chartConfig.options.scales = {
            xAxes: [{
              ticks: {
                beginAtZero: true,
                callback: (value) => {
                  if (sellerHasAmounts) {
                    return this.formatCurrencyForCharts(value);
                  }
                  return value;
                }
              },
              scaleLabel: {
                display: true,
                labelString: sellerHasAmounts ? 'Monto Total' : 'Cantidad de Ventas'
              }
            }]
          };
          break;
          
        case 'category':
          chartConfig.type = 'horizontalBar';
          chartConfig.data.labels = this.chartData.salesByCategory.map(item => {
            // Truncar nombres largos para mejor visualización
            return item.label.length > 30 ? item.label.substring(0, 27) + '...' : item.label;
          });
          const categoryHasAmounts = this.chartData.salesByCategory.some(item => item.amount);
          chartConfig.data.datasets[0].data = this.chartData.salesByCategory.map(item => 
            categoryHasAmounts && item.amount ? item.amount : item.value
          );
          chartConfig.data.datasets[0].backgroundColor = this.chartData.salesByCategory.map((item, i) => 
            this.getCategoryColor(i)
          );
          chartConfig.data.datasets[0].label = categoryHasAmounts ? 'Monto Total' : 'Cantidad Vendida';
          // Configurar tooltips
          chartConfig.options.tooltips.callbacks = {
            title: (tooltipItems) => {
              // Mostrar el nombre completo de la categoría en el título del tooltip
              const index = Array.isArray(tooltipItems) ? tooltipItems[0].index : tooltipItems.index;
              return this.chartData.salesByCategory[index].label;
            },
            label: (tooltipItem, data) => {
              const item = this.chartData.salesByCategory[tooltipItem.index];
              const parts = [];
              
              if (categoryHasAmounts && item.amount) {
                parts.push(`💰 Monto: ${this.formatCurrencyForCharts(item.amount)}`);
                parts.push(`📊 Cantidad: ${new Intl.NumberFormat('es-CL').format(item.value)} productos`);
              } else {
                parts.push(`📊 Cantidad: ${new Intl.NumberFormat('es-CL').format(item.value)} productos`);
                if (item.amount) {
                  parts.push(`💰 Monto: ${this.formatCurrencyForCharts(item.amount)}`);
                }
              }
              
              // Calcular porcentaje
              const total = this.chartData.salesByCategory.reduce((sum, item) => {
                if (categoryHasAmounts && item.amount) {
                  return sum + item.amount;
                }
                return sum + item.value;
              }, 0);
              const currentValue = categoryHasAmounts && item.amount ? item.amount : item.value;
              const percentage = total > 0 ? ((currentValue / total) * 100).toFixed(1) : '0';
              parts.push(`📈 Representa el ${percentage}% del total`);
              
              return parts;
            }
          };
          
          // Configurar opciones específicas para gráfico horizontal
          chartConfig.options.scales = {
            xAxes: [{
              ticks: {
                beginAtZero: true,
                callback: (value) => {
                  if (categoryHasAmounts) {
                    return this.formatCurrencyForCharts(value);
                  }
                  return value;
                }
              },
              scaleLabel: {
                display: true,
                labelString: categoryHasAmounts ? 'Monto Total' : 'Cantidad Vendida'
              }
            }]
          };
          break;
          
        case 'planFamily':
          chartConfig.type = 'horizontalBar';
          chartConfig.data.labels = this.chartData.salesByPlanFamily.map(item => item.label);
          const planHasAmounts = this.chartData.salesByPlanFamily.some(item => item.amount);
          chartConfig.data.datasets[0].data = this.chartData.salesByPlanFamily.map(item => 
            planHasAmounts && item.amount ? item.amount : item.value
          );
          chartConfig.data.datasets[0].backgroundColor = this.chartData.salesByPlanFamily.map((item, i) => 
            this.getPlanFamilyColor(i)
          );
          chartConfig.data.datasets[0].label = planHasAmounts ? 'Monto Total' : 'Membresías';
          // Configurar tooltips
          chartConfig.options.tooltips.callbacks = {
            label: (tooltipItem, data) => {
              const item = this.chartData.salesByPlanFamily[tooltipItem.index];
              const parts = [];
              
              if (planHasAmounts && item.amount) {
                parts.push(`Monto: ${this.formatCurrencyForCharts(item.amount)}`);
                parts.push(`Cantidad: ${new Intl.NumberFormat('es-CL').format(item.value)} membresías`);
              } else {
                parts.push(`Membresías: ${new Intl.NumberFormat('es-CL').format(item.value)}`);
                if (item.amount) {
                  parts.push(`Monto: ${this.formatCurrencyForCharts(item.amount)}`);
                }
              }
              
              return parts;
            }
          };
          break;
          
        case 'amount':
          chartConfig.type = 'bar';
          chartConfig.data.labels = this.chartData.amountDistribution.map(item => item.label);
          chartConfig.data.datasets[0].data = this.chartData.amountDistribution.map(item => item.value);
          chartConfig.data.datasets[0].backgroundColor = this.chartData.amountDistribution.map((item, i) => 
            this.getAmountColor(i)
          );
          chartConfig.data.datasets[0].label = 'Cantidad de Ventas';
          // Configurar tooltips para mostrar cantidad y monto
          chartConfig.options.tooltips.callbacks = {
            label: (tooltipItem, data) => {
              const item = this.chartData.amountDistribution[tooltipItem.index];
              const parts = [];
              parts.push(`📊 Cantidad: ${new Intl.NumberFormat('es-CL').format(item.value || 0)} ventas`);
              parts.push(`💰 Monto Total: ${this.formatCurrencyForCharts(item.totalAmount || item.amount || 0)}`);
              
              // Calcular porcentaje del monto
              const totalAmount = this.chartData.amountDistribution.reduce((sum, d) => 
                sum + (d.totalAmount || d.amount || 0), 0);
              const percentage = totalAmount > 0 ? 
                ((item.totalAmount || item.amount || 0) / totalAmount * 100).toFixed(1) : '0';
              parts.push(`📈 Representa el ${percentage}% del monto total`);
              
              return parts;
            }
          };
          break;
          
        case 'modality':
          chartConfig.type = 'doughnut';
          chartConfig.data.labels = this.chartData.salesByModality.map(item => 
            item.label === 'Sin tipo' ? 'Otras Ventas sin Membresías' : item.label
          );
          chartConfig.data.datasets[0].data = this.chartData.salesByModality.map(item => item.amount || 0);
          chartConfig.data.datasets[0].backgroundColor = this.chartData.salesByModality.map((item, i) => 
            this.getModalityColor(i)
          );
          // Configurar tooltips
          chartConfig.options.tooltips.callbacks = {
            label: (tooltipItem, data) => {
              const item = this.chartData.salesByModality[tooltipItem.index];
              const label = data.labels[tooltipItem.index];
              const parts = [];
              
              parts.push(`💰 Monto Total: ${this.formatCurrencyForCharts(item.amount || 0)}`);
              parts.push(`📊 Cantidad de Ventas: ${new Intl.NumberFormat('es-CL').format(item.value || 0)}`);
              
              // Calcular porcentaje del monto
              const totalAmount = this.chartData.salesByModality.reduce((sum, d) => sum + (d.amount || 0), 0);
              const percentage = totalAmount > 0 ? 
                ((item.amount || 0) / totalAmount * 100).toFixed(1) : '0';
              parts.push(`📈 Representa el ${percentage}% del monto total`);
              
              return parts;
            }
          };
          break;
          
        case 'cashier':
          chartConfig.type = 'horizontalBar';
          chartConfig.data.labels = this.chartData.salesByCashier.map(item => item.label);
          chartConfig.data.datasets[0].data = this.chartData.salesByCashier.map(item => item.amount || 0);
          chartConfig.data.datasets[0].backgroundColor = this.chartData.salesByCashier.map((item, i) => 
            this.getCashierColor(i)
          );
          chartConfig.data.datasets[0].label = 'Monto Total';
          // Configurar escalas para mostrar montos en formato monetario
          chartConfig.options.scales = {
            xAxes: [{
              ticks: {
                beginAtZero: true,
                callback: function(value) {
                  return new Intl.NumberFormat('es-CL', {
                    style: 'currency',
                    currency: 'CLP',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  }).format(value);
                }
              },
              scaleLabel: {
                display: true,
                labelString: 'Monto Total'
              }
            }],
            yAxes: [{
              ticks: {
                maxRotation: 0
              }
            }]
          };
          // Configurar tooltips
          chartConfig.options.tooltips.callbacks = {
            label: (tooltipItem, data) => {
              const item = this.chartData.salesByCashier[tooltipItem.index];
              const parts = [];
              
              parts.push(`💰 Monto Total: ${this.formatCurrencyForCharts(item.amount || 0)}`);
              parts.push(`📊 Cantidad de Ventas: ${new Intl.NumberFormat('es-CL').format(item.value || 0)}`);
              
              // Calcular porcentaje del monto
              const totalAmount = this.chartData.salesByCashier.reduce((sum, d) => sum + (d.amount || 0), 0);
              const percentage = totalAmount > 0 ? 
                ((item.amount || 0) / totalAmount * 100).toFixed(1) : '0';
              parts.push(`📈 Representa el ${percentage}% del total`);
              
              return parts;
            }
          };
          break;
          
        case 'discountCategory':
          chartConfig.type = 'horizontalBar';
          chartConfig.data.labels = this.chartData.discountsByCategory.map(item => item.label);
          chartConfig.data.datasets[0].data = this.chartData.discountsByCategory.map(item => item.amount || 0);
          chartConfig.data.datasets[0].backgroundColor = this.chartData.discountsByCategory.map((item, i) => 
            this.getDiscountCategoryColor(i)
          );
          chartConfig.data.datasets[0].label = 'Total Descuentos';
          // Configurar escalas para mostrar montos en formato monetario
          chartConfig.options.scales = {
            xAxes: [{
              ticks: {
                beginAtZero: true,
                callback: function(value) {
                  return new Intl.NumberFormat('es-CL', {
                    style: 'currency',
                    currency: 'CLP',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  }).format(value);
                }
              },
              scaleLabel: {
                display: true,
                labelString: 'Total Descuentos'
              }
            }],
            yAxes: [{
              ticks: {
                maxRotation: 0
              }
            }]
          };
          // Configurar tooltips
          chartConfig.options.tooltips.callbacks = {
            label: (tooltipItem, data) => {
              const item = this.chartData.discountsByCategory[tooltipItem.index];
              const parts = [];
              
              parts.push(`💸 Total Descuentos: ${this.formatCurrencyForCharts(item.amount || 0)}`);
              parts.push(`📊 Cantidad de Ventas: ${new Intl.NumberFormat('es-CL').format(item.value || 0)}`);
              
              // Calcular porcentaje del monto
              const totalAmount = this.chartData.discountsByCategory.reduce((sum, d) => sum + (d.amount || 0), 0);
              const percentage = totalAmount > 0 ? 
                ((item.amount || 0) / totalAmount * 100).toFixed(1) : '0';
              parts.push(`📈 Representa el ${percentage}% del total de descuentos`);
              
              return parts;
            }
          };
          break;
          
        case 'discountTrend':
          chartConfig.type = 'line';
          chartConfig.data.labels = this.chartData.discountsDailyTrend.map(item => {
            // Formatear fecha para mejor visualización
            const dateLabel = item.label || item.date;
            if (dateLabel) {
              const dateParts = dateLabel.split('-');
              if (dateParts.length === 3) {
                const day = dateParts[2];
                const month = dateParts[1];
                return `${day}/${month}`;
              }
            }
            return dateLabel;
          });
          chartConfig.data.datasets[0] = {
            label: 'Descuentos Diarios',
            data: this.chartData.discountsDailyTrend.map(item => item.totalDiscount || item.amount),
            borderColor: '#ff6b6b',
            backgroundColor: 'rgba(255, 107, 107, 0.1)',
            tension: 0.4,
            fill: true
          };
          // Configurar tooltips
          chartConfig.options.tooltips.callbacks = {
            title: (tooltipItems) => {
              // Formatear fecha completa para el tooltip
              const index = tooltipItems[0].index;
              const dateLabel = this.chartData.discountsDailyTrend[index].label || this.chartData.discountsDailyTrend[index].date;
              if (dateLabel) {
                const dateParts = dateLabel.split('-');
                if (dateParts.length === 3) {
                  const year = dateParts[0];
                  const month = dateParts[1];
                  const day = dateParts[2];
                  const monthNames = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 
                                    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
                  return `${day} de ${monthNames[parseInt(month) - 1]} de ${year}`;
                }
              }
              return dateLabel;
            },
            label: (tooltipItem, data) => {
              const item = this.chartData.discountsDailyTrend[tooltipItem.index];
              const amount = item.totalDiscount || item.amount || 0;
              return `Descuento Total: ${this.formatCurrencyForCharts(amount)}`;
            }
          };
          break;
      }
      
      // Verificar que tengamos datos antes de crear el gráfico
      if (!chartConfig.data.labels || chartConfig.data.labels.length === 0) {
        console.error('❌ No hay datos disponibles para crear el gráfico');
        this.$swal.fire({
          icon: 'warning',
          title: 'Sin datos',
          text: 'No hay datos disponibles para mostrar el gráfico',
          confirmButtonColor: '#6bd098'
        });
        this.closeFullscreenChart();
        return;
      }
      
      // Crear el gráfico
      try {
        console.log('📊 Creando gráfico con configuración:', {
          type: chartConfig.type,
          labels: chartConfig.data.labels.length,
          datasets: chartConfig.data.datasets.length,
          firstDatasetLength: chartConfig.data.datasets[0]?.data?.length || 0
        });
        
        // Verificar Canvas
        if (!canvas || !canvas.getContext) {
          throw new Error('Canvas no válido');
        }
        
        // Destruir instancia anterior si existe
        if (this.fullscreenChart.instance) {
          try {
            this.fullscreenChart.instance.destroy();
            this.fullscreenChart.instance = null;
          } catch (e) {
            console.warn('⚠️ Error al destruir gráfico anterior:', e);
          }
        }
        
        this.fullscreenChart.instance = new Chart(canvas.getContext('2d'), chartConfig);
        console.log('✅ Gráfico creado desde datos exitosamente');
      } catch (error) {
        console.error('❌ Error al crear gráfico desde datos:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo crear el gráfico. Por favor, intenta nuevamente.',
          confirmButtonColor: '#6bd098'
        });
        this.closeFullscreenChart();
      }
    },
    
    closeFullscreenChart() {
      this.fullscreenChart.show = false;
      if (this.fullscreenChart.instance) {
        this.fullscreenChart.instance.destroy();
        this.fullscreenChart.instance = null;
      }
    },
    
    formatChartValue(value, chartType) {
      // Para el gráfico de distribución de montos, el value es cantidad, no monto
      if (chartType === 'amount') {
        return new Intl.NumberFormat('es-CL').format(value);
      }
      
      // Verificar si el tipo de gráfico muestra montos o si los datos tienen hasAmount
      const isMonetaryChart = chartType === 'discountCategory' || 
                             chartType === 'discountTrend';
      
      // Verificar si los datos del gráfico actual tienen información de monto
      const hasMonetaryData = this.fullscreenChart.data && 
                             this.fullscreenChart.data.some(d => d.hasAmount);
      
      if (isMonetaryChart || hasMonetaryData) {
        // Formatear como moneda
        return new Intl.NumberFormat('es-CL', {
          style: 'currency',
          currency: 'CLP',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(value);
      } else {
        // Formatear como número
        return new Intl.NumberFormat('es-CL').format(value);
      }
    },
    
    calculatePercentage(value, data) {
      const total = data.reduce((sum, item) => sum + item.value, 0);
      if (total === 0) return '0';
      return ((value / total) * 100).toFixed(1);
    },
    
    // Método específico para calcular porcentaje basado en amounts
    calculateAmountPercentage(amount, data) {
      const total = data.reduce((sum, item) => sum + (item.amount || 0), 0);
      if (total === 0) return '0';
      return ((amount / total) * 100).toFixed(1);
    },
    
    calculateTotal(data) {
      return data.reduce((sum, item) => sum + item.value, 0);
    },
    
    getChartIcon(chartType) {
      const icons = {
        status: 'fas fa-chart-pie text-success',
        method: 'fas fa-chart-bar text-primary',
        branch: 'fas fa-building text-info',
        trend: 'fas fa-chart-line text-warning',
        seller: 'fas fa-user-tie text-secondary',
        category: 'fas fa-tags text-danger',
        planFamily: 'fas fa-dumbbell text-primary',
        amount: 'fas fa-dollar-sign text-danger',
        modality: 'fas fa-calendar-alt text-info',
        cashier: 'fas fa-cash-register text-secondary',
        discountCategory: 'fas fa-percent text-warning',
        discountTrend: 'fas fa-chart-area text-danger'
      };
      return icons[chartType] || 'fas fa-chart-bar';
    },
    
    async exportFullscreenChart() {
      try {
        const originalCanvas = this.$refs.fullscreenChartCanvas;
        if (!originalCanvas) return;
        
        // Crear un canvas temporal con fondo blanco
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = originalCanvas.width;
        tempCanvas.height = originalCanvas.height;
        const ctx = tempCanvas.getContext('2d');
        
        // Dibujar fondo blanco
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
        
        // Copiar el gráfico original sobre el fondo blanco
        ctx.drawImage(originalCanvas, 0, 0);
        
        // Convertir canvas temporal a blob
        tempCanvas.toBlob((blob) => {
          // Crear URL temporal
          const url = window.URL.createObjectURL(blob);
          
          // Crear elemento de descarga
          const a = document.createElement('a');
          a.href = url;
          a.download = `${this.fullscreenChart.title} - ${new Date().toLocaleDateString('es-CL')}.png`;
          
          // Simular clic y limpiar
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          
          // Notificar éxito
          this.$swal.fire({
            icon: 'success',
            title: 'Imagen descargada',
            text: 'El gráfico se ha descargado como imagen PNG con fondo blanco',
            timer: 2000,
            showConfirmButton: false
          });
        }, 'image/png');
      } catch (error) {
        console.error('Error al exportar gráfico:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo descargar la imagen'
        });
      }
    },
    
    async exportFullscreenChartToPDF() {
      try {
        // Mostrar modal de carga
        this.$swal.fire({
          title: '¡Generando PDF!',
          html: `
            <div class="text-center">
              <p class="mb-4">Procesando el gráfico "${this.fullscreenChart.title}"...</p>
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Cargando...</span>
              </div>
            </div>
          `,
          showConfirmButton: false,
          allowOutsideClick: false,
          allowEscapeKey: false
        });

        // Importar dinámicamente las librerías necesarias
        const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
          import('html2canvas'),
          import('jspdf')
        ]);

        // Crear nueva instancia de PDF
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 15;
        const contentWidth = pageWidth - (margin * 2);
        let yPosition = margin;

        // 1. HEADER DEL PDF
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('REPORTE DE ANÁLISIS - VENTAS DE SUCURSALES', pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 10;

        // Título del gráfico específico
        pdf.setFontSize(14);
        pdf.text(this.fullscreenChart.title.toUpperCase(), pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 10;

        // Fecha de generación
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        const fechaGeneracion = new Date().toLocaleDateString('es-CL', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
        pdf.text(`Generado: ${fechaGeneracion}`, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 8;

        // Rango de fechas de los filtros
        if (this.filters.dateFrom || this.filters.dateTo) {
          const dateFrom = this.filters.dateFrom || 'Inicio';
          const dateTo = this.filters.dateTo || 'Actualidad';
          pdf.text(`Período: ${this.formatDateForDisplay(dateFrom)} - ${this.formatDateForDisplay(dateTo)}`, pageWidth / 2, yPosition, { align: 'center' });
          yPosition += 10;
        } else {
          yPosition += 5;
        }

        // 2. CAPTURAR EL GRÁFICO
        const chartCanvas = this.$refs.fullscreenChartCanvas;
        if (chartCanvas) {
          try {
            // Crear canvas con fondo blanco
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = chartCanvas.width;
            tempCanvas.height = chartCanvas.height;
            const ctx = tempCanvas.getContext('2d');
            
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
            ctx.drawImage(chartCanvas, 0, 0);
            
            const chartImgData = tempCanvas.toDataURL('image/png');
            const chartHeight = (tempCanvas.height * contentWidth) / tempCanvas.width;
            
            // Verificar si cabe en la página
            if (yPosition + chartHeight > pageHeight - margin - 20) {
              pdf.addPage();
              yPosition = margin;
            }
            
            pdf.addImage(chartImgData, 'PNG', margin, yPosition, contentWidth, chartHeight);
            yPosition += chartHeight + 10;
          } catch (error) {
            console.error('Error al capturar gráfico:', error);
          }
        }

        // 3. CAPTURAR LA TABLA DE DATOS
        const tableElement = this.$el.querySelector('.modal-body .table-responsive table');
        if (tableElement) {
          try {
            // Verificar si necesitamos nueva página
            if (yPosition > pageHeight - margin - 40) {
              pdf.addPage();
              yPosition = margin;
            }

            // Título de la tabla
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Datos Detallados del Gráfico', margin, yPosition);
            yPosition += 8;
            
            console.log('📊 Capturando tabla completa:', {
              filas: tableElement.rows.length,
              altura: tableElement.offsetHeight,
              datos: this.fullscreenChart.data.length
            });

            // Clonar la tabla para evitar el scroll y capturar todo el contenido
            const tableClone = tableElement.cloneNode(true);
            tableClone.classList.add('table', 'table-striped', 'table-hover');
            
            const tempContainer = document.createElement('div');
            tempContainer.style.position = 'absolute';
            tempContainer.style.left = '-9999px';
            tempContainer.style.top = '-9999px';
            tempContainer.style.width = '1200px';
            tempContainer.style.backgroundColor = '#ffffff';
            tempContainer.style.padding = '20px';
            tempContainer.appendChild(tableClone);
            document.body.appendChild(tempContainer);

            // Asegurar que los estilos se apliquen correctamente
            const style = document.createElement('style');
            style.textContent = `
              .table { width: 100%; margin-bottom: 1rem; color: #212529; border-collapse: collapse; }
              .table th, .table td { padding: 0.75rem; vertical-align: top; border-top: 1px solid #dee2e6; }
              .table thead th { vertical-align: bottom; border-bottom: 2px solid #dee2e6; font-weight: bold; background-color: #f8f9fa; }
              .table tbody + tbody { border-top: 2px solid #dee2e6; }
              .table-striped tbody tr:nth-of-type(odd) { background-color: rgba(0,0,0,.05); }
              .table-hover tbody tr:hover { background-color: rgba(0,0,0,.075); }
              .table-secondary { background-color: #e9ecef !important; }
              .table-secondary th { font-weight: bold; }
              .text-end { text-align: right !important; }
              .badge { display: inline-block; padding: 0.25em 0.4em; font-size: 75%; font-weight: 700; line-height: 1; text-align: center; white-space: nowrap; vertical-align: baseline; border-radius: 0.25rem; width: 20px; height: 20px; }
              .me-2 { margin-right: 0.5rem !important; }
            `;
            tempContainer.appendChild(style);
            
            // Asegurar que las badges tengan sus colores inline
            const badges = tableClone.querySelectorAll('.badge');
            badges.forEach(badge => {
              const bgColor = badge.style.backgroundColor;
              if (bgColor) {
                badge.style.cssText = `background-color: ${bgColor} !important; display: inline-block; width: 20px; height: 20px; border-radius: 3px;`;
              }
            });
            
            // Capturar la tabla completa
            const tableCanvas = await html2canvas(tempContainer, {
              backgroundColor: '#ffffff',
              scale: 2,
              logging: false,
              windowWidth: 1200,
              windowHeight: tempContainer.scrollHeight,
              useCORS: true,
              allowTaint: true
            });

            // Remover el contenedor temporal
            document.body.removeChild(tempContainer);

            const tableImgData = tableCanvas.toDataURL('image/png');
            const tableHeight = (tableCanvas.height * contentWidth) / tableCanvas.width;
            
            console.log('📊 Tabla capturada:', {
              anchoCanvas: tableCanvas.width,
              altoCanvas: tableCanvas.height,
              alturaPDF: tableHeight,
              registros: this.fullscreenChart.data.length
            });
            
            // Verificar si necesitamos dividir la tabla en múltiples páginas
            const maxTableHeight = pageHeight - yPosition - margin - 20;
            
            if (tableHeight > maxTableHeight) {
              // La tabla es muy alta, dividirla en páginas
              const scale = contentWidth / tableCanvas.width;
              const pageHeightInCanvas = maxTableHeight / scale;
              let currentY = 0;
              
              while (currentY < tableCanvas.height) {
                // Verificar si necesitamos nueva página (excepto la primera iteración)
                if (currentY > 0) {
                  pdf.addPage();
                  yPosition = margin;
                  
                  // Agregar título de continuación
                  pdf.setFontSize(10);
                  pdf.setFont('helvetica', 'italic');
                  pdf.text('Datos Detallados del Gráfico (continuación)', margin, yPosition);
                  yPosition += 8;
                }
                
                // Calcular la altura del segmento a capturar
                const segmentHeight = Math.min(pageHeightInCanvas, tableCanvas.height - currentY);
                
                // Crear un canvas temporal para el segmento
                const segmentCanvas = document.createElement('canvas');
                segmentCanvas.width = tableCanvas.width;
                segmentCanvas.height = segmentHeight;
                const segmentCtx = segmentCanvas.getContext('2d');
                
                // Copiar el segmento de la tabla
                segmentCtx.drawImage(
                  tableCanvas,
                  0, currentY, // Origen en el canvas original
                  tableCanvas.width, segmentHeight, // Tamaño del segmento
                  0, 0, // Destino en el nuevo canvas
                  tableCanvas.width, segmentHeight // Tamaño en el destino
                );
                
                // Agregar el segmento al PDF
                const segmentImgData = segmentCanvas.toDataURL('image/png');
                const segmentPdfHeight = segmentHeight * scale;
                pdf.addImage(segmentImgData, 'PNG', margin, yPosition, contentWidth, segmentPdfHeight);
                
                currentY += segmentHeight;
                yPosition += segmentPdfHeight;
              }
            } else {
              // La tabla cabe en la página actual
              pdf.addImage(tableImgData, 'PNG', margin, yPosition, contentWidth, tableHeight);
              yPosition += tableHeight;
            }
          } catch (error) {
            console.error('Error al capturar tabla:', error);
          }
        }

        // 4. FOOTER DEL PDF (en todas las páginas)
        const totalPages = pdf.internal.getNumberOfPages();
        const totalRegistros = this.fullscreenChart.data.length;
        
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          
          // Información izquierda
          pdf.text(`Total elementos analizados: ${totalRegistros}`, margin, pageHeight - 5);
          
          // Información centro
          pdf.text('Informe generado desde CentralGym App - https://centralgym.cl', pageWidth / 2, pageHeight - 5, { align: 'center' });
          
          // Información derecha
          pdf.text(`Página ${i} de ${totalPages}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
        }

        // 5. Generar nombre del archivo
        const chartName = this.fullscreenChart.title.replace(/[^a-z0-9]/gi, '_');
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        const fileName = `Grafico_${chartName}_${timestamp}.pdf`;

        // 6. Descargar PDF
        pdf.save(fileName);

        // Cerrar modal de carga
        this.$swal.close();

        // Mostrar confirmación
        await new Promise(resolve => setTimeout(resolve, 500));
        this.$swal.fire({
          icon: 'success',
          title: '¡PDF Generado!',
          html: `
            <div class="text-left">
              <p><strong>✅ El informe PDF se ha generado correctamente</strong></p>
              <p><strong>📁 Archivo:</strong> ${fileName}</p>
              <p><strong>📊 Contenido incluido:</strong></p>
              <ul>
                <li>📈 Gráfico: ${this.fullscreenChart.title}</li>
                <li>📅 Período de análisis</li>
                <li>📋 Tabla de datos completa (${totalRegistros} registros)</li>
                <li>📄 ${totalPages} página${totalPages > 1 ? 's' : ''} en total</li>
              </ul>
              ${totalPages > 1 ? '<p class="text-info"><i class="fas fa-info-circle"></i> La tabla se dividió automáticamente en múltiples páginas para incluir todos los datos.</p>' : ''}
            </div>
          `,
          confirmButtonColor: '#6bd098',
          confirmButtonText: 'Excelente',
          timer: 10000,
          timerProgressBar: true
        });

      } catch (error) {
        console.error('Error al generar PDF:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error al generar PDF',
          text: 'No se pudo crear el documento PDF. Por favor, intente nuevamente.',
          confirmButtonColor: '#6bd098'
        });
      }
    },
    // Métodos auxiliares para obtener colores de gráficos
    getStatusColor(status) {
      const colors = {
        '[VIGENTE]': '#6bd098',
        '[ANULADA]': '#ff6b6b',
        'Vigente': '#6bd098',
        'Anulada': '#ff6b6b'
      };
      return colors[status] || '#666666';
    },
    
    getPaymentMethodColor(index) {
      const colors = ['#51cbce', '#fbc658', '#ef8157', '#6bd098', '#f17e5d', '#66615b', '#7b88ff'];
      return colors[index % colors.length];
    },
    
    getBranchColor(index) {
      const colors = ['#7b88ff', '#51bcda', '#6bd098', '#fbc658', '#ef8157', '#f17e5d'];
      return colors[index % colors.length];
    },
    
    getSellerColor(index) {
      const colors = ['#66615b', '#51cbce', '#fbc658', '#ef8157', '#6bd098', '#f17e5d'];
      return colors[index % colors.length];
    },
    
    getCategoryColor(index) {
      const colors = ['#ef8157', '#f17e5d', '#fbc658', '#6bd098', '#51cbce', '#7b88ff'];
      return colors[index % colors.length];
    },
    
    getPlanFamilyColor(index) {
      const colors = ['#51cbce', '#6bd098', '#fbc658', '#ef8157', '#f17e5d', '#66615b'];
      return colors[index % colors.length];
    },
    
    getAmountColor(index) {
      const colors = ['#ff6b6b', '#f17e5d', '#fbc658', '#6bd098', '#51cbce'];
      return colors[index % colors.length];
    },
    
    getModalityColor(index) {
      const colors = ['#7b88ff', '#51bcda', '#6bd098', '#fbc658', '#ef8157'];
      return colors[index % colors.length];
    },
    
    getCashierColor(index) {
      const colors = ['#66615b', '#7b88ff', '#51cbce', '#6bd098', '#fbc658'];
      return colors[index % colors.length];
    },
    
    getDiscountCategoryColor(index) {
      const colors = ['#fbc658', '#ef8157', '#f17e5d', '#ff6b6b', '#66615b'];
      return colors[index % colors.length];
    },
    
    // Método auxiliar para obtener la clave de datos según el tipo de gráfico
    getChartDataKey(chartType) {
      const mapping = {
        'status': 'salesByStatus',
        'method': 'salesByMethod',
        'branch': 'salesByBranch',
        'trend': 'dailyTrend',
        'seller': 'salesBySeller',
        'category': 'salesByCategory',
        'planFamily': 'salesByPlanFamily',
        'amount': 'amountDistribution',
        'modality': 'salesByModality',
        'cashier': 'salesByCashier',
        'discountCategory': 'discountsByCategory',
        'discountTrend': 'discountsDailyTrend'
      };
      return mapping[chartType] || null;
    },
    
    // Método para formatear fechas de YYYY-MM-DD a formato legible
    formatDateForDisplay(dateString) {
      if (!dateString) return '';
      
      // Manejar casos especiales
      if (dateString === 'Inicio') return 'Inicio del período';
      if (dateString === 'Actualidad') return 'Actualidad';
      
      const dateParts = dateString.split('-');
      if (dateParts.length === 3) {
        const year = dateParts[0];
        const month = dateParts[1];
        const day = dateParts[2];
        
        const monthNames = [
          'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
          'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
        ];
        
        const monthName = monthNames[parseInt(month) - 1];
        return `${day} de ${monthName} de ${year}`;
      }
      
      return dateString;
    },
    // Helpers para exportación estable
    async waitForChartsToSettle(delayMs = 2000) {
      // Dos frames para asegurar reflow + timeout para animaciones
      await this.$nextTick();
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
      await new Promise(r => setTimeout(r, delayMs));
    },
    // Métodos de filtros guardados
    loadSavedFilters() {
      const saved = localStorage.getItem('gymSalesFilters');
      if (saved) {
        try {
          this.savedFilters = JSON.parse(saved);
        } catch (error) {
          console.error('Error al cargar filtros guardados:', error);
          this.savedFilters = [];
        }
      }
    },
    saveSavedFilters() {
      localStorage.setItem('gymSalesFilters', JSON.stringify(this.savedFilters));
    },
    openSaveFilterModal() {
      // Solo limpiar si no estamos editando
      if (!this.isEditingFilter) {
        this.filterToSave.name = '';
        this.filterToSave.description = '';
      }
      this.showSaveFilterModal = true;
      if (this.saveFilterModal) {
        this.saveFilterModal.show();
      }
    },
    closeSaveFilterModal() {
      this.showSaveFilterModal = false;
      if (this.saveFilterModal) {
        this.saveFilterModal.hide();
      }
      
      // Limpiar estado de edición al cerrar
      this.isEditingFilter = false;
      this.filterToEdit = null;
    },
    saveCurrentFilter() {
      if (!this.filterToSave.name.trim()) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Nombre requerido',
          text: 'Por favor, ingresa un nombre para el filtro',
          confirmButtonColor: '#6bd098'
        });
        return;
      }

      if (this.isEditingFilter && this.filterToEdit) {
        // MODO EDICIÓN: Actualizar filtro existente
        console.log('💾 Actualizando filtro existente (GymSales):', this.filterToEdit.name);
        
        const filterIndex = this.savedFilters.findIndex(f => f.id === this.filterToEdit.id);
        if (filterIndex !== -1) {
          // Mantener ID original y fecha de creación, actualizar el resto
          this.savedFilters[filterIndex] = {
            ...this.savedFilters[filterIndex],
            name: this.filterToSave.name.trim(),
            description: this.filterToSave.description.trim(),
            filters: { ...this.filters },
            sorting: { ...this.sorting },
            updatedAt: new Date().toISOString() // Agregar fecha de actualización
          };
          
          this.saveSavedFilters();
          this.closeSaveFilterModal();
          
          this.$swal.fire({
            icon: 'success',
            title: '¡Filtro actualizado!',
            text: `El filtro "${this.filterToSave.name.trim()}" se ha actualizado correctamente`,
            confirmButtonColor: '#6bd098'
          });
        }
      } else {
        // MODO CREACIÓN: Crear nuevo filtro
        console.log('💾 Creando nuevo filtro (GymSales):', this.filterToSave.name.trim());
        
        const filterConfig = {
          id: Date.now(),
          name: this.filterToSave.name.trim(),
          description: this.filterToSave.description.trim(),
          filters: { ...this.filters },
          sorting: { ...this.sorting },
          createdAt: new Date().toISOString()
        };

        this.savedFilters.push(filterConfig);
        this.saveSavedFilters();
        this.closeSaveFilterModal();

        this.$swal.fire({
          icon: 'success',
          title: '¡Filtro guardado!',
          text: `El filtro "${filterConfig.name}" se ha guardado correctamente`,
          confirmButtonColor: '#6bd098'
        });
      }
    },
    applySavedFilter(filter) {
      console.log('🔄 Aplicando filtro guardado (GymSales):', filter.name);
      
      // CAMBIO v2.6: Los filtros guardados ahora realizan una nueva búsqueda en el servidor
      // Esto permite buscar con rangos de fecha y otros criterios guardados
      
      // 1. Aplicar configuración de filtros guardados
      this.filters = { ...filter.filters };
      this.sorting = { ...filter.sorting };
      
      // 2. Realizar búsqueda directamente sin modal adicional
      // El componente padre ya muestra indicadores de carga durante la búsqueda
      console.log('✅ Búsqueda iniciada con filtros guardados:', {
        nombre: filter.name,
        fechaDesde: filter.filters.dateFrom,
        fechaHasta: filter.filters.dateTo,
        sucursal: filter.filters.branch,
        estado: filter.filters.status,
        metodoPago: filter.filters.paymentMethod
      });
      
      // 3. Ejecutar búsqueda (el componente padre manejará los indicadores de carga)
      this.handleSearch();
      
      // Mostrar notificación breve no bloqueante
      this.$swal.fire({
        position: 'top-end',
        icon: 'info',
        title: `Filtro "${filter.name}" aplicado`,
        showConfirmButton: false,
        timer: 2000,
        toast: true
      });
    },
    
    // ========================================
    // MÉTODOS NUEVOS: FILTROS GUARDADOS MEJORADOS v2.2
    // ========================================
    applyFilterSetLocally(filterSet, sortingConfig) {
      if (!this.originalFilteredRecords || this.originalFilteredRecords.length === 0) {
        console.warn('⚠️ No hay datos originales para filtrar (GymSales)');
        return;
      }
      
      console.log('🔧 Aplicando conjunto de filtros localmente (GymSales):', {
        filtros: Object.keys(filterSet).filter(key => filterSet[key]),
        ordenamiento: sortingConfig,
        datosOriginales: this.originalFilteredRecords.length
      });
      
      // Comenzar con todos los datos originales (sin filtros previos)
      let filteredData = [...this.originalFilteredRecords];
      
      // Aplicar filtros básicos (simulando consulta al servidor)
      if (filterSet.status) {
        filteredData = filteredData.filter(sale => sale.estadoVenta === filterSet.status);
      }
      
      if (filterSet.paymentMethod) {
        filteredData = filteredData.filter(sale => sale.metodoPago === filterSet.paymentMethod);
      }
      
      if (filterSet.branch) {
        filteredData = filteredData.filter(sale => 
          sale.nombreSucursal === this.getBranchName(filterSet.branch) ||
          sale.nombreSucursal.includes(filterSet.branch)
        );
      }
      
      // Aplicar filtro de fechas si existe
      if (filterSet.dateFrom && filterSet.dateTo) {
        filteredData = this.filterRecordsByDateRange(filteredData, filterSet.dateFrom, filterSet.dateTo);
      }
      
      // Aplicar filtros avanzados
      const advancedFilters = {
        searchText: filterSet.searchText,
        amountFrom: filterSet.amountFrom,
        amountTo: filterSet.amountTo,
        clientName: filterSet.clientName,
        sellerName: filterSet.sellerName
      };
      
      const hasAdvancedFilters = Object.values(advancedFilters).some(value => 
        value && value.toString().trim() !== ''
      );
      
      if (hasAdvancedFilters) {
        filteredData = this.applyAdvancedFiltersToData(filteredData, advancedFilters);
      }
      
      // Aplicar ordenamiento si se especifica
      if (sortingConfig && sortingConfig.field) {
        filteredData = this.sortDataLocally(filteredData, sortingConfig.field, sortingConfig.direction);
      }
      
      console.log('📊 Filtros aplicados localmente (GymSales):', {
        registrosOriginales: this.originalFilteredRecords.length,
        registrosFiltrados: filteredData.length,
        filtrosAplicados: Object.keys(filterSet).filter(key => filterSet[key]).length
      });
      
      // Actualizar datos y UI
      this.updateDataWithLocalFilters(filteredData);
    },
    
    // ========================================
    // MÉTODO NUEVO: ORDENAMIENTO LOCAL
    // ========================================
    sortDataLocally(data, field, direction = 'asc') {
      return data.sort((a, b) => {
        let valueA, valueB;
        
        // Obtener valores según el campo
        switch (field) {
          case 'idTransaccion':
            // ✅ CORREGIDO: Manejo mejorado de idTransaccion (puede venir como objeto $numberLong o como valor directo)
            valueA = parseInt(a.idTransaccion?.$numberLong || a.idTransaccion) || 0;
            valueB = parseInt(b.idTransaccion?.$numberLong || b.idTransaccion) || 0;
            break;
          case 'ventaTotal':
            valueA = this.parseMonetaryValue(a.ventaTotal) || 0;
            valueB = this.parseMonetaryValue(b.ventaTotal) || 0;
            break;
          case 'fechaHora':
            valueA = this.parseChileanDate(a.fechaHora);
            valueB = this.parseChileanDate(b.fechaHora);
            // Si alguna fecha es inválida, darle prioridad baja
            if (!valueA) valueA = new Date(0); // 1970-01-01
            if (!valueB) valueB = new Date(0);
            break;
          case 'nombreCliente':
            valueA = (a.nombreCliente || '').toLowerCase();
            valueB = (b.nombreCliente || '').toLowerCase();
            break;
          case 'nombreVendedor':
            valueA = (a.nombreVendedor || '').toLowerCase();
            valueB = (b.nombreVendedor || '').toLowerCase();
            break;
          case 'estadoVenta':
            valueA = (a.estadoVenta || '').toLowerCase();
            valueB = (b.estadoVenta || '').toLowerCase();
            break;
          case 'metodoPago':
            valueA = (a.metodoPago || '').toLowerCase();
            valueB = (b.metodoPago || '').toLowerCase();
            break;
          case 'nombreSucursal':
            valueA = (a.nombreSucursal || '').toLowerCase();
            valueB = (b.nombreSucursal || '').toLowerCase();
            break;
          default:
            valueA = a[field] || '';
            valueB = b[field] || '';
        }
        
        // Comparar valores
        let result = 0;
        if (valueA < valueB) result = -1;
        else if (valueA > valueB) result = 1;
        
        // Aplicar dirección
        return direction === 'desc' ? -result : result;
      });
    },
    
    filterRecordsByDateRange(records, dateFrom, dateTo) {
      if (!dateFrom || !dateTo) {
        console.log('📅 Sin rango de fechas especificado, devolviendo todos los registros');
        return records;
      }
      
      // dateFrom y dateTo vienen en formato YYYY-MM-DD del input HTML5
      const fromDate = new Date(dateFrom);
      const toDate = new Date(dateTo);
      
      // Ajustar la fecha final al final del día
      toDate.setHours(23, 59, 59, 999);
      
      console.log('📅 Filtrando por rango de fechas:', {
        desde: fromDate.toLocaleDateString('es-CL'),
        hasta: toDate.toLocaleDateString('es-CL'),
        registrosAFiltrar: records.length
      });
      
      const filteredRecords = records.filter(sale => {
        const saleDate = this.parseChileanDate(sale.fechaHora);
        if (!saleDate) {
          console.warn('⚠️ Registro con fecha inválida omitido:', sale.fechaHora);
          return false;
        }
        
        const isInRange = saleDate >= fromDate && saleDate <= toDate;
        return isInRange;
      });
      
      console.log('📅 Resultado del filtro de fechas:', {
        registrosOriginales: records.length,
        registrosFiltrados: filteredRecords.length,
        porcentajeFiltrado: ((filteredRecords.length / records.length) * 100).toFixed(1) + '%'
      });
      
      return filteredRecords;
    },
    
    getFilterSummary(filter) {
      // Contar cuántos filtros están activos
      const activeFilters = [];
      
      if (filter.filters.status) activeFilters.push('Estado');
      if (filter.filters.paymentMethod) activeFilters.push('Método');
      if (filter.filters.branch) activeFilters.push('Sucursal');
      if (filter.filters.dateFrom && filter.filters.dateTo) activeFilters.push('Fechas');
      if (filter.filters.searchText) activeFilters.push('Texto');
      if (filter.filters.amountFrom || filter.filters.amountTo) activeFilters.push('Monto');
      if (filter.filters.clientName) activeFilters.push('Cliente');
      if (filter.filters.sellerName) activeFilters.push('Vendedor');
      
      const count = activeFilters.length;
      return count > 0 ? `${count} filtro${count > 1 ? 's' : ''}` : 'Sin filtros';
    },
    
    formatAmountRange(amountFrom, amountTo) {
      if (amountFrom && amountTo) {
        return `${this.formatCurrency(amountFrom)} - ${this.formatCurrency(amountTo)}`;
      } else if (amountFrom) {
        return `Desde ${this.formatCurrency(amountFrom)}`;
      } else if (amountTo) {
        return `Hasta ${this.formatCurrency(amountTo)}`;
      }
      return '';
    },
    
    editSavedFilter(filter) {
      console.log('✏️ Editando filtro (GymSales):', filter.name);
      
      // Marcar que estamos editando
      this.isEditingFilter = true;
      this.filterToEdit = filter;
      
      // Pre-cargar datos en el modal
      this.filterToSave.name = filter.name;
      this.filterToSave.description = filter.description || '';
      
      // Pre-cargar filtros actuales en el formulario
      this.filters = { ...filter.filters };
      this.sorting = { ...filter.sorting };
      
      // Abrir modal
      this.openSaveFilterModal();
    },
    
    duplicateSavedFilter(filter) {
      console.log('📋 Duplicando filtro (GymSales):', filter.name);
      
      const duplicatedFilter = {
        id: Date.now(), // Nuevo ID único
        name: `${filter.name} (Copia)`,
        description: `Copia de: ${filter.description || filter.name}`,
        filters: { ...filter.filters },
        sorting: { ...filter.sorting },
        createdAt: new Date().toISOString()
      };
      
      this.savedFilters.push(duplicatedFilter);
      this.saveSavedFilters();
      
      this.$swal.fire({
        icon: 'success',
        title: '¡Filtro duplicado!',
        text: `Se ha creado una copia del filtro "${filter.name}"`,
        timer: 2000,
        showConfirmButton: false
      });
    },
    deleteSavedFilter(filter) {
      this.$swal.fire({
        title: '¿Eliminar filtro?',
        text: `¿Estás seguro de eliminar el filtro "${filter.name}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      }).then((result) => {
        if (result.isConfirmed) {
          this.savedFilters = this.savedFilters.filter(f => f.id !== filter.id);
          this.saveSavedFilters();
          
          this.$swal.fire({
            icon: 'success',
            title: 'Filtro eliminado',
            text: `El filtro "${filter.name}" ha sido eliminado`,
            timer: 2000,
            showConfirmButton: false
          });
        }
      });
    },
    getBranchName(branchId) {
      // ACTUALIZADO v2.3: branches ahora es array de nombres directos
      if (!branchId) return '';
      
      // Si el branchId existe en el catálogo, devolverlo
      if (this.branches.includes(branchId)) {
        return branchId;
      }
      
      // Buscar por similitud parcial
      const foundBranch = this.branches.find(branch => 
        branch.toLowerCase().includes(branchId.toLowerCase()) ||
        branchId.toLowerCase().includes(branch.toLowerCase())
      );
      
      return foundBranch || branchId;
    },
    // Métodos de gráficos (TOTALMENTE RENOVADOS v2.3 - Sistema expandido con análisis profundo)
    // OPTIMIZADO v3.0: Versión asíncrona para evitar bloqueos
    // NOTA: El método updateChartData ahora viene del chartMixin
    // y se ejecuta de forma asíncrona para evitar bloqueos del navegador
    renderCharts() {
      // Renderizar TODOS los gráficos con Chart.js (EXPANDIDO v2.3)
      this.$nextTick(() => {
        console.log('🎨 Renderizando todos los gráficos...');
        
        // Gráficos existentes mejorados
        this.createStatusChart();
        this.createMethodChart();
        this.createBranchChart();
        this.createTrendChart();
        
        // Nuevos gráficos v2.3
        this.createSellerChart();
        this.createCategoryChart();
        this.createPlanFamilyChart();
        this.createAmountDistributionChart();
        this.createModalityChart();
        this.createCashierChart();
      // NUEVOS GRÁFICOS DE DESCUENTOS
      this.createDiscountCategoryChart();
      this.createDiscountTrendChart();
        
        console.log('🎨 Todos los gráficos renderizados exitosamente');
      });
    },
    createStatusChart() {
      if (!this.$refs.statusChart || this.chartData.salesByStatus.length === 0) return;

      // Destruir gráfico existente
      if (this.chartInstances.statusChart) {
        this.chartInstances.statusChart.destroy();
      }

      // Mapear colores específicos por estado
      const getColorForStatus = (status) => {
        switch (status) {
          case '[VIGENTE]':
            return '#28a745'; // Verde para VIGENTE
          case '[ANULADA]':
            return '#dc3545'; // Rojo para ANULADA
          default:
            return '#17a2b8'; // Azul para otros estados
        }
      };

      const ctx = this.$refs.statusChart.getContext('2d');
      this.chartInstances.statusChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: this.chartData.salesByStatus.map(item => item.label),
          datasets: [{
            data: this.chartData.salesByStatus.map(item => item.amount),
            backgroundColor: this.chartData.salesByStatus.map(item => getColorForStatus(item.label)),
            borderWidth: 2,
            borderColor: '#fff'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            position: 'bottom',
            labels: {
              padding: 20,
              usePointStyle: true,
              fontSize: 12
            }
          },
          tooltips: {
            callbacks: {
              label: (tooltipItem, data) => {
                const dataset = data.datasets[tooltipItem.datasetIndex];
                const total = dataset.data.reduce((sum, value) => sum + value, 0);
                const amount = dataset.data[tooltipItem.index];
                const percentage = ((amount / total) * 100).toFixed(1);
                const statusData = this.chartData.salesByStatus[tooltipItem.index];
                const count = statusData ? statusData.value : 0;
                return [
                  `${data.labels[tooltipItem.index]}:`,
                  `💰 Monto: ${this.formatCurrencyForCharts(amount)}`,
                  `📊 Cantidad: ${count} venta${count !== 1 ? 's' : ''}`,
                  `📈 Representa el ${percentage}% del total por estado`
                ];
              }
            }
          },
          animation: {
            animateScale: true,
            animateRotate: true
          }
        }
      });
    },
    createMethodChart() {
      if (!this.$refs.methodChart || this.chartData.salesByMethod.length === 0) return;

      // Destruir gráfico existente
      if (this.chartInstances.methodChart) {
        this.chartInstances.methodChart.destroy();
      }

      // Determinar si hay montos disponibles
      const hasAmounts = this.chartData.salesByMethod.some(item => item.amount);
      const useAmounts = hasAmounts;

      const ctx = this.$refs.methodChart.getContext('2d');
      this.chartInstances.methodChart = new Chart(ctx, {
        type: 'horizontalBar',
        data: {
          labels: this.chartData.salesByMethod.map(item => item.label),
          datasets: [{
            label: useAmounts ? 'Monto Total' : 'Cantidad de Ventas',
            data: this.chartData.salesByMethod.map(item => 
              useAmounts && item.amount ? item.amount : item.value
            ),
            backgroundColor: [
              '#007bff', // Azul
              '#28a745', // Verde
              '#ffc107', // Amarillo
              '#dc3545', // Rojo
              '#17a2b8', // Cyan
              '#6f42c1'  // Morado
            ],
            borderColor: [
              '#0056b3',
              '#1e7e34',
              '#e0a800',
              '#c82333',
              '#117a8b',
              '#5a2d91'
            ],
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            xAxes: [{
              ticks: {
                beginAtZero: true,
                callback: (value) => {
                  if (useAmounts) {
                    return this.formatCurrencyForCharts(value);
                  }
                  return value;
                }
              },
              scaleLabel: {
                display: true,
                labelString: useAmounts ? 'Monto Total' : 'Cantidad de Ventas'
              }
            }],
            yAxes: [{
              scaleLabel: {
                display: true,
                labelString: 'Método de Pago'
              }
            }]
          },
          legend: {
            display: false
          },
          tooltips: {
            callbacks: {
              label: (tooltipItem) => {
                const methodData = this.chartData.salesByMethod[tooltipItem.index];
                const parts = [];
                
                if (useAmounts && methodData.amount) {
                  // Si mostramos montos en el gráfico
                  parts.push(`💰 Monto: ${this.formatCurrencyForCharts(methodData.amount)}`);
                  parts.push(`📊 Cantidad: ${new Intl.NumberFormat('es-CL').format(methodData.value)} ventas`);
                } else {
                  // Si mostramos cantidades en el gráfico
                  parts.push(`📊 Ventas: ${new Intl.NumberFormat('es-CL').format(tooltipItem.yLabel)}`);
                  if (methodData && methodData.amount) {
                    parts.push(`💰 Monto: ${this.formatCurrencyForCharts(methodData.amount)}`);
                  }
                }
                
                // Calcular porcentaje
                const total = this.chartData.salesByMethod.reduce((sum, item) => {
                  if (useAmounts && item.amount) {
                    return sum + item.amount;
                  }
                  return sum + item.value;
                }, 0);
                const currentValue = useAmounts && methodData.amount ? methodData.amount : methodData.value;
                const percentage = total > 0 ? ((currentValue / total) * 100).toFixed(1) : '0';
                parts.push(`📈 Representa el ${percentage}% del total`);
                
                return parts;
              }
            }
          },
          animation: {
            duration: 1000,
            easing: 'easeInOutQuart',
            onComplete: function() {
              const chartInstance = this.chart;
              const ctx = chartInstance.ctx;
              ctx.textBaseline = 'middle';
              ctx.font = 'bold 12px Arial';
              
              this.data.datasets.forEach((dataset, i) => {
                const meta = chartInstance.controller.getDatasetMeta(i);
                meta.data.forEach((bar, index) => {
                  const data = dataset.data[index];
                  
                  // Formatear el valor
                  let value;
                  if (useAmounts) {
                    value = '$' + new Intl.NumberFormat('es-CL', { minimumFractionDigits: 0 }).format(data);
                  } else {
                    value = new Intl.NumberFormat('es-CL').format(data);
                  }
                  
                  // Calcular el ancho de la barra (horizontal)
                  const barWidth = bar._model.x - bar._model.base;
                  
                  // Calcular el ancho del texto
                  const textWidth = ctx.measureText(value).width;
                  
                  // Decidir si mostrar dentro o fuera de la barra
                  if (barWidth > textWidth + 20) {
                    // Mostrar dentro de la barra
                    ctx.textAlign = 'right';
                    ctx.fillStyle = '#fff';
                    const x = bar._model.x - 10;
                    const y = bar._model.y;
                    
                    // Agregar sombra para mejor legibilidad
                    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
                    ctx.shadowBlur = 3;
                    ctx.shadowOffsetX = 1;
                    ctx.shadowOffsetY = 1;
                    
                    ctx.fillText(value, x, y);
                    
                    // Resetear sombra
                    ctx.shadowColor = 'transparent';
                    ctx.shadowBlur = 0;
                    ctx.shadowOffsetX = 0;
                    ctx.shadowOffsetY = 0;
                  } else {
                    // Mostrar fuera de la barra (a la derecha)
                    ctx.textAlign = 'left';
                    ctx.fillStyle = '#444';
                    const x = bar._model.x + 5;
                    const y = bar._model.y;
                    ctx.fillText(value, x, y);
                  }
                });
              });
            }
          }
        }
      });
    },
    createBranchChart() {
      if (!this.$refs.branchChart || this.chartData.salesByBranch.length === 0) return;

      // Destruir gráfico existente
      if (this.chartInstances.branchChart) {
        this.chartInstances.branchChart.destroy();
      }

      // Determinar si hay montos disponibles
      const hasAmounts = this.chartData.salesByBranch.some(item => item.amount);
      const useAmounts = hasAmounts;

      const ctx = this.$refs.branchChart.getContext('2d');
      this.chartInstances.branchChart = new Chart(ctx, {
        type: 'horizontalBar',
        data: {
          labels: this.chartData.salesByBranch.map(item => item.label),
          datasets: [{
            label: useAmounts ? 'Monto Total' : 'Cantidad de Ventas',
            data: this.chartData.salesByBranch.map(item => 
              useAmounts && item.amount ? item.amount : item.value
            ),
            backgroundColor: [
              '#17a2b8', // Cyan
              '#28a745', // Verde
              '#ffc107', // Amarillo
              '#dc3545', // Rojo
              '#007bff', // Azul
              '#6f42c1'  // Morado
            ],
            borderColor: [
              '#117a8b',
              '#1e7e34',
              '#e0a800',
              '#c82333',
              '#0056b3',
              '#5a2d91'
            ],
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            xAxes: [{
              ticks: {
                beginAtZero: true,
                callback: (value) => {
                  if (useAmounts) {
                    return this.formatCurrencyForCharts(value);
                  }
                  return value;
                }
              },
              scaleLabel: {
                display: true,
                labelString: useAmounts ? 'Monto Total' : 'Cantidad de Ventas'
              }
            }],
            yAxes: [{
              scaleLabel: {
                display: true,
                labelString: 'Sucursales'
              }
            }]
          },
          legend: {
            display: false
          },
          tooltips: {
            callbacks: {
              label: (tooltipItem) => {
                const branchData = this.chartData.salesByBranch[tooltipItem.index];
                const parts = [];
                
                if (useAmounts && branchData.amount) {
                  // Si mostramos montos en el gráfico
                  parts.push(`💰 Monto: ${this.formatCurrencyForCharts(branchData.amount)}`);
                  parts.push(`📊 Cantidad: ${new Intl.NumberFormat('es-CL').format(branchData.value)} ventas`);
                } else {
                  // Si mostramos cantidades en el gráfico
                  parts.push(`📊 Ventas: ${new Intl.NumberFormat('es-CL').format(tooltipItem.xLabel)}`);
                  if (branchData && branchData.amount) {
                    parts.push(`💰 Monto: ${this.formatCurrencyForCharts(branchData.amount)}`);
                  }
                }
                
                // Calcular porcentaje
                const total = this.chartData.salesByBranch.reduce((sum, item) => {
                  if (useAmounts && item.amount) {
                    return sum + item.amount;
                  }
                  return sum + item.value;
                }, 0);
                const currentValue = useAmounts && branchData.amount ? branchData.amount : branchData.value;
                const percentage = total > 0 ? ((currentValue / total) * 100).toFixed(1) : '0';
                parts.push(`📈 Representa el ${percentage}% del total`);
                
                return parts;
              }
            }
          },
          animation: {
            duration: 1000,
            easing: 'easeInOutQuart',
            onComplete: function() {
              const chartInstance = this.chart;
              const ctx = chartInstance.ctx;
              ctx.textBaseline = 'middle';
              ctx.font = 'bold 12px Arial';
              
              this.data.datasets.forEach((dataset, i) => {
                const meta = chartInstance.controller.getDatasetMeta(i);
                meta.data.forEach((bar, index) => {
                  const data = dataset.data[index];
                  
                  // Formatear el valor
                  let value;
                  if (useAmounts) {
                    value = '$' + new Intl.NumberFormat('es-CL', { minimumFractionDigits: 0 }).format(data);
                  } else {
                    value = new Intl.NumberFormat('es-CL').format(data);
                  }
                  
                  // Calcular el ancho de la barra (horizontal)
                  const barWidth = bar._model.x - bar._model.base;
                  
                  // Calcular el ancho del texto
                  const textWidth = ctx.measureText(value).width;
                  
                  // Decidir si mostrar dentro o fuera de la barra
                  if (barWidth > textWidth + 20) {
                    // Mostrar dentro de la barra
                    ctx.textAlign = 'right';
                    ctx.fillStyle = '#fff';
                    const x = bar._model.x - 10;
                    const y = bar._model.y;
                    
                    // Agregar sombra para mejor legibilidad
                    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
                    ctx.shadowBlur = 3;
                    ctx.shadowOffsetX = 1;
                    ctx.shadowOffsetY = 1;
                    
                    ctx.fillText(value, x, y);
                    
                    // Resetear sombra
                    ctx.shadowColor = 'transparent';
                    ctx.shadowBlur = 0;
                    ctx.shadowOffsetX = 0;
                    ctx.shadowOffsetY = 0;
                  } else {
                    // Mostrar fuera de la barra (a la derecha)
                    ctx.textAlign = 'left';
                    ctx.fillStyle = '#444';
                    const x = bar._model.x + 5;
                    const y = bar._model.y;
                    ctx.fillText(value, x, y);
                  }
                });
              });
            }
          }
        }
      });
    },
    createTrendChart() {
      if (!this.$refs.trendChart || this.chartData.dailyTrend.length === 0) return;

      // Destruir gráfico existente
      if (this.chartInstances.trendChart) {
        this.chartInstances.trendChart.destroy();
      }

      const ctx = this.$refs.trendChart.getContext('2d');
      this.chartInstances.trendChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: this.chartData.dailyTrend.map(item => {
            // CORREGIDO: Formatear fecha evitando problemas de zona horaria
            // item.label viene en formato "YYYY-MM-DD"
            const dateParts = item.label.split('-');
            if (dateParts.length === 3) {
              const day = dateParts[2];
              const month = dateParts[1];
              return `${day}/${month}`;
            }
            return item.label; // Fallback
          }),
          datasets: [{
            label: 'Monto Total Diario',
            data: this.chartData.dailyTrend.map(item => item.amount || 0),
            borderColor: '#007bff',
            backgroundColor: 'rgba(0, 123, 255, 0.1)',
            borderWidth: 3,
            fill: true,
            tension: 0.4,
            pointBackgroundColor: '#007bff',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            yAxes: [{
              ticks: {
                beginAtZero: true,
                callback: (value) => {
                  return this.formatCurrencyForCharts(value);
                }
              },
              scaleLabel: {
                display: true,
                labelString: 'Monto Total'
              },
              gridLines: {
                color: 'rgba(0, 0, 0, 0.1)'
              }
            }],
            xAxes: [{
              scaleLabel: {
                display: true,
                labelString: 'Días'
              },
              gridLines: {
                color: 'rgba(0, 0, 0, 0.1)'
              }
            }]
          },
          legend: {
            display: true,
            position: 'top'
          },
          tooltips: {
            mode: 'index',
            intersect: false,
            callbacks: {
              title: (tooltipItems) => {
                // CORREGIDO: Formatear fecha evitando problemas de zona horaria
                const dateLabel = this.chartData.dailyTrend[tooltipItems[0].index].label;
                // dateLabel viene en formato "YYYY-MM-DD"
                const dateParts = dateLabel.split('-');
                if (dateParts.length === 3) {
                  const year = dateParts[0];
                  const month = dateParts[1];
                  const day = dateParts[2];
                  // Crear fecha sin problemas de zona horaria
                  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                  return date.toLocaleDateString('es-CL', { 
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  });
                }
                return dateLabel; // Fallback
              },
              label: (tooltipItem) => {
                const dailyData = this.chartData.dailyTrend[tooltipItem.index];
                const amount = dailyData ? dailyData.amount : 0;
                const count = dailyData ? dailyData.value : 0;
                return [
                  `💰 Monto: ${this.formatCurrencyForCharts(amount)}`,
                  `📊 Cantidad de Ventas: ${new Intl.NumberFormat('es-CL').format(count)}`
                ];
              }
            }
          },
          hover: {
            mode: 'nearest',
            intersect: true
          },
          animation: {
            duration: 1500,
            easing: 'easeInOutQuart'
          }
        }
      });
    },
    
    // ========================================
    // NUEVOS MÉTODOS PARA GRÁFICOS EXPANDIDOS v2.3
    // ========================================
    
    createSellerChart() {
      if (!this.$refs.sellerChart || this.chartData.salesBySeller.length === 0) return;

      // Destruir gráfico existente
      if (this.chartInstances.sellerChart) {
        this.chartInstances.sellerChart.destroy();
      }

      // Determinar si hay montos disponibles
      const hasAmounts = this.chartData.salesBySeller.some(item => item.amount);
      const useAmounts = hasAmounts;

      const ctx = this.$refs.sellerChart.getContext('2d');
      this.chartInstances.sellerChart = new Chart(ctx, {
        type: 'horizontalBar',
        data: {
          labels: this.chartData.salesBySeller.map(item => {
            // Truncar nombres largos para mejor visualización
            return item.label.length > 25 ? item.label.substring(0, 22) + '...' : item.label;
          }),
          datasets: [{
            label: useAmounts ? 'Monto Total' : 'Cantidad de Ventas',
            data: this.chartData.salesBySeller.map(item => 
              useAmounts && item.amount ? item.amount : item.value
            ),
            backgroundColor: [
              '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
              '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384'
            ],
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            xAxes: [{ 
              ticks: { 
                beginAtZero: true,
                callback: (value) => {
                  if (useAmounts) {
                    return this.formatCurrencyForCharts(value);
                  }
                  return value;
                }
              },
              scaleLabel: {
                display: true,
                labelString: useAmounts ? 'Monto Total' : 'Cantidad de Ventas'
              }
            }]
          },
          tooltips: {
            callbacks: {
              title: (tooltipItems) => {
                return this.chartData.salesBySeller[tooltipItems[0].index].label;
              },
              label: (tooltipItem) => {
                const sellerData = this.chartData.salesBySeller[tooltipItem.index];
                const parts = [];
                
                if (useAmounts && sellerData.amount) {
                  // Si mostramos montos en el gráfico
                  parts.push(`💰 Monto: ${this.formatCurrencyForCharts(sellerData.amount)}`);
                  parts.push(`📊 Cantidad: ${new Intl.NumberFormat('es-CL').format(sellerData.value)} ventas`);
                } else {
                  // Si mostramos cantidades en el gráfico
                  parts.push(`📊 Ventas: ${new Intl.NumberFormat('es-CL').format(tooltipItem.xLabel)}`);
                  if (sellerData && sellerData.amount) {
                    parts.push(`💰 Monto: ${this.formatCurrencyForCharts(sellerData.amount)}`);
                  }
                }
                
                // Calcular porcentaje
                const total = this.chartData.salesBySeller.reduce((sum, item) => {
                  if (useAmounts && item.amount) {
                    return sum + item.amount;
                  }
                  return sum + item.value;
                }, 0);
                const currentValue = useAmounts && sellerData.amount ? sellerData.amount : sellerData.value;
                const percentage = total > 0 ? ((currentValue / total) * 100).toFixed(1) : '0';
                parts.push(`📈 Representa el ${percentage}% del total`);
                
                return parts;
              }
            }
          },
          animation: {
            duration: 1000,
            easing: 'easeInOutQuart',
            onComplete: function() {
              const chartInstance = this.chart;
              const ctx = chartInstance.ctx;
              ctx.textBaseline = 'middle';
              ctx.font = 'bold 12px Arial';
              
              this.data.datasets.forEach((dataset, i) => {
                const meta = chartInstance.controller.getDatasetMeta(i);
                meta.data.forEach((bar, index) => {
                  const data = dataset.data[index];
                  
                  // Formatear el valor
                  let value;
                  if (useAmounts) {
                    value = '$' + new Intl.NumberFormat('es-CL', { minimumFractionDigits: 0 }).format(data);
                  } else {
                    value = new Intl.NumberFormat('es-CL').format(data);
                  }
                  
                  // Calcular el ancho de la barra (horizontal)
                  const barWidth = bar._model.x - bar._model.base;
                  
                  // Calcular el ancho del texto
                  const textWidth = ctx.measureText(value).width;
                  
                  // Decidir si mostrar dentro o fuera de la barra
                  if (barWidth > textWidth + 20) {
                    // Mostrar dentro de la barra
                    ctx.textAlign = 'right';
                    ctx.fillStyle = '#fff';
                    const x = bar._model.x - 10;
                    const y = bar._model.y;
                    
                    // Agregar sombra para mejor legibilidad
                    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
                    ctx.shadowBlur = 3;
                    ctx.shadowOffsetX = 1;
                    ctx.shadowOffsetY = 1;
                    
                    ctx.fillText(value, x, y);
                    
                    // Resetear sombra
                    ctx.shadowColor = 'transparent';
                    ctx.shadowBlur = 0;
                    ctx.shadowOffsetX = 0;
                    ctx.shadowOffsetY = 0;
                  } else {
                    // Mostrar fuera de la barra (a la derecha)
                    ctx.textAlign = 'left';
                    ctx.fillStyle = '#444';
                    const x = bar._model.x + 5;
                    const y = bar._model.y;
                    ctx.fillText(value, x, y);
                  }
                });
              });
            }
          }
        }
      });
    },

    createCategoryChart() {
      if (!this.$refs.categoryChart || this.chartData.salesByCategory.length === 0) return;

      if (this.chartInstances.categoryChart) {
        this.chartInstances.categoryChart.destroy();
      }

      // Determinar si hay montos disponibles
      const hasAmounts = this.chartData.salesByCategory.some(item => item.amount);
      const useAmounts = hasAmounts;

      const ctx = this.$refs.categoryChart.getContext('2d');
      this.chartInstances.categoryChart = new Chart(ctx, {
        type: 'horizontalBar',
        data: {
          labels: this.chartData.salesByCategory.map(item => {
            // Truncar nombres largos para mejor visualización
            return item.label.length > 30 ? item.label.substring(0, 27) + '...' : item.label;
          }),
          datasets: [{
            label: useAmounts ? 'Monto Total' : 'Cantidad Vendida',
            data: this.chartData.salesByCategory.map(item => 
              useAmounts && item.amount ? item.amount : item.value
            ),
            backgroundColor: [
              '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
              '#FF9F40', '#FF6384', '#C9CBCF', '#FF6B6B', '#66BB6A'
            ],
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            xAxes: [{ 
              ticks: { 
                beginAtZero: true,
                callback: (value) => {
                  if (useAmounts) {
                    return this.formatCurrencyForCharts(value);
                  }
                  return value;
                }
              },
              scaleLabel: {
                display: true,
                labelString: useAmounts ? 'Monto Total' : 'Cantidad Vendida'
              }
            }],
            yAxes: [{
              scaleLabel: {
                display: true,
                labelString: 'Categoría de Producto'
              }
            }]
          },
          tooltips: {
            callbacks: {
              title: (tooltipItems) => {
                // Mostrar el nombre completo de la categoría en el título del tooltip
                const index = Array.isArray(tooltipItems) ? tooltipItems[0].index : tooltipItems.index;
                return this.chartData.salesByCategory[index].label;
              },
              label: (tooltipItem) => {
                const categoryData = this.chartData.salesByCategory[tooltipItem.index];
                const parts = [];
                
                if (useAmounts && categoryData.amount) {
                  // Si mostramos montos en el gráfico
                  parts.push(`💰 Monto: ${this.formatCurrencyForCharts(categoryData.amount)}`);
                  parts.push(`📊 Cantidad: ${new Intl.NumberFormat('es-CL').format(categoryData.value)} productos`);
                } else {
                  // Si mostramos cantidades en el gráfico
                  parts.push(`📊 Cantidad: ${new Intl.NumberFormat('es-CL').format(tooltipItem.xLabel)} productos`);
                  if (categoryData && categoryData.amount) {
                    parts.push(`💰 Monto: ${this.formatCurrencyForCharts(categoryData.amount)}`);
                  }
                }
                
                // Calcular porcentaje
                const total = this.chartData.salesByCategory.reduce((sum, item) => {
                  if (useAmounts && item.amount) {
                    return sum + item.amount;
                  }
                  return sum + item.value;
                }, 0);
                const currentValue = useAmounts && categoryData.amount ? categoryData.amount : categoryData.value;
                const percentage = total > 0 ? ((currentValue / total) * 100).toFixed(1) : '0';
                parts.push(`📈 Representa el ${percentage}% del total`);
                
                return parts;
              }
            }
          },
          animation: {
            duration: 1000,
            easing: 'easeInOutQuart',
            onComplete: function() {
              const chartInstance = this.chart;
              const ctx = chartInstance.ctx;
              ctx.textBaseline = 'middle';
              ctx.font = 'bold 12px Arial';
              
              this.data.datasets.forEach((dataset, i) => {
                const meta = chartInstance.controller.getDatasetMeta(i);
                meta.data.forEach((bar, index) => {
                  const data = dataset.data[index];
                  
                  // Formatear el valor
                  let value;
                  if (useAmounts) {
                    value = '$' + new Intl.NumberFormat('es-CL', { minimumFractionDigits: 0 }).format(data);
                  } else {
                    value = new Intl.NumberFormat('es-CL').format(data);
                  }
                  
                  // Calcular el ancho de la barra (horizontal)
                  const barWidth = bar._model.x - bar._model.base;
                  
                  // Calcular el ancho del texto
                  const textWidth = ctx.measureText(value).width;
                  
                  // Decidir si mostrar dentro o fuera de la barra
                  if (barWidth > textWidth + 20) {
                    // Mostrar dentro de la barra
                    ctx.textAlign = 'right';
                    ctx.fillStyle = '#fff';
                    const x = bar._model.x - 10;
                    const y = bar._model.y;
                    
                    // Agregar sombra para mejor legibilidad
                    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
                    ctx.shadowBlur = 3;
                    ctx.shadowOffsetX = 1;
                    ctx.shadowOffsetY = 1;
                    
                    ctx.fillText(value, x, y);
                    
                    // Resetear sombra
                    ctx.shadowColor = 'transparent';
                    ctx.shadowBlur = 0;
                    ctx.shadowOffsetX = 0;
                    ctx.shadowOffsetY = 0;
                  } else {
                    // Mostrar fuera de la barra (a la derecha)
                    ctx.textAlign = 'left';
                    ctx.fillStyle = '#444';
                    const x = bar._model.x + 5;
                    const y = bar._model.y;
                    ctx.fillText(value, x, y);
                  }
                });
              });
            }
          }
        }
      });
    },

        createPlanFamilyChart() {
      if (!this.$refs.planFamilyChart || this.chartData.salesByPlanFamily.length === 0) return;

      if (this.chartInstances.planFamilyChart) {
        this.chartInstances.planFamilyChart.destroy();
      }

      // Determinar si hay montos disponibles
      const hasAmounts = this.chartData.salesByPlanFamily.some(item => item.amount);
      const useAmounts = hasAmounts;

      const ctx = this.$refs.planFamilyChart.getContext('2d');
      this.chartInstances.planFamilyChart = new Chart(ctx, {
        type: 'horizontalBar',
        data: {
          labels: this.chartData.salesByPlanFamily.map(item => {
            return item.label.length > 25 ? item.label.substring(0, 22) + '...' : item.label;
          }),
          datasets: [{
            label: useAmounts ? 'Monto Total' : 'Cantidad de Ventas',
            data: this.chartData.salesByPlanFamily.map(item => 
              useAmounts && item.amount ? item.amount : item.value
            ),
            backgroundColor: '#4BC0C0',
            borderColor: '#369999',
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            xAxes: [{ 
              ticks: { 
                beginAtZero: true,
                callback: (value) => {
                  if (useAmounts) {
                    return this.formatCurrencyForCharts(value);
                  }
                  return value;
                }
              },
              scaleLabel: {
                display: true,
                labelString: useAmounts ? 'Monto Total' : 'Cantidad de Ventas'
              }
            }],
            yAxes: [{
              scaleLabel: {
                display: true,
                labelString: 'Plan'
              }
            }]
          },
          tooltips: {
            callbacks: {
              title: (tooltipItems) => {
                return this.chartData.salesByPlanFamily[tooltipItems[0].index].label;
              },
              label: (tooltipItem) => {
                const planData = this.chartData.salesByPlanFamily[tooltipItem.index];
                const parts = [];
                
                if (useAmounts && planData.amount) {
                  // Si mostramos montos en el gráfico
                  parts.push(`💰 Monto: ${this.formatCurrencyForCharts(planData.amount)}`);
                  parts.push(`📊 Cantidad: ${new Intl.NumberFormat('es-CL').format(planData.value)} membresías`);
                } else {
                  // Si mostramos cantidades en el gráfico
                  parts.push(`📊 Ventas: ${new Intl.NumberFormat('es-CL').format(tooltipItem.xLabel)} membresías`);
                  if (planData && planData.amount) {
                    parts.push(`💰 Monto: ${this.formatCurrencyForCharts(planData.amount)}`);
                  }
                }
                
                // Calcular porcentaje
                const total = this.chartData.salesByPlanFamily.reduce((sum, item) => {
                  if (useAmounts && item.amount) {
                    return sum + item.amount;
                  }
                  return sum + item.value;
                }, 0);
                const currentValue = useAmounts && planData.amount ? planData.amount : planData.value;
                const percentage = total > 0 ? ((currentValue / total) * 100).toFixed(1) : '0';
                parts.push(`📈 Representa el ${percentage}% del total`);
                parts.push(`📊 Valores con descuentos aplicados`);
                
                return parts;
              }
            }
          },
          animation: {
            duration: 1000,
            easing: 'easeInOutQuart',
            onComplete: function() {
              const chartInstance = this.chart;
              const ctx = chartInstance.ctx;
              ctx.textBaseline = 'middle';
              ctx.font = 'bold 12px Arial';
              
              this.data.datasets.forEach((dataset, i) => {
                const meta = chartInstance.controller.getDatasetMeta(i);
                meta.data.forEach((bar, index) => {
                  const data = dataset.data[index];
                  
                  // Formatear el valor
                  let value;
                  if (useAmounts) {
                    value = '$' + new Intl.NumberFormat('es-CL', { minimumFractionDigits: 0 }).format(data);
                  } else {
                    value = new Intl.NumberFormat('es-CL').format(data);
                  }
                  
                  // Calcular el ancho de la barra (horizontal)
                  const barWidth = bar._model.x - bar._model.base;
                  
                  // Calcular el ancho del texto
                  const textWidth = ctx.measureText(value).width;
                  
                  // Decidir si mostrar dentro o fuera de la barra
                  if (barWidth > textWidth + 20) {
                    // Mostrar dentro de la barra
                    ctx.textAlign = 'right';
                    ctx.fillStyle = '#fff';
                    const x = bar._model.x - 10;
                    const y = bar._model.y;
                    
                    // Agregar sombra para mejor legibilidad
                    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
                    ctx.shadowBlur = 3;
                    ctx.shadowOffsetX = 1;
                    ctx.shadowOffsetY = 1;
                    
                    ctx.fillText(value, x, y);
                    
                    // Resetear sombra
                    ctx.shadowColor = 'transparent';
                    ctx.shadowBlur = 0;
                    ctx.shadowOffsetX = 0;
                    ctx.shadowOffsetY = 0;
                  } else {
                    // Mostrar fuera de la barra (a la derecha)
                    ctx.textAlign = 'left';
                    ctx.fillStyle = '#444';
                    const x = bar._model.x + 5;
                    const y = bar._model.y;
                    ctx.fillText(value, x, y);
                  }
                });
              });
            }
          }
        }
      });
    },

    createAmountDistributionChart() {
      if (!this.$refs.amountChart || this.chartData.amountDistribution.length === 0) return;

      if (this.chartInstances.amountChart) {
        this.chartInstances.amountChart.destroy();
      }

      const ctx = this.$refs.amountChart.getContext('2d');
      this.chartInstances.amountChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: this.chartData.amountDistribution.map(item => item.label),
          datasets: [{
            label: 'Cantidad de Ventas',
            data: this.chartData.amountDistribution.map(item => item.value),
            backgroundColor: [
              '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'
            ],
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            yAxes: [{ ticks: { beginAtZero: true, stepSize: 1 } }],
            xAxes: [{ ticks: { maxRotation: 45 } }]
          },
          tooltips: {
            callbacks: {
              label: (tooltipItem, data) => {
                const rangoMonto = data.labels[tooltipItem.index];
                return [
                  `💰 Rango: ${rangoMonto}`,
                  `📊 Ventas: ${tooltipItem.yLabel}`
                ];
              }
            }
          },
          animation: {
            duration: 1000,
            easing: 'easeInOutQuart',
            onComplete: function() {
              const chartInstance = this.chart;
              const ctx = chartInstance.ctx;
              ctx.textBaseline = 'middle';
              ctx.font = 'bold 12px Arial';
              
              this.data.datasets.forEach((dataset, i) => {
                const meta = chartInstance.controller.getDatasetMeta(i);
                meta.data.forEach((bar, index) => {
                  const data = dataset.data[index];
                  const value = new Intl.NumberFormat('es-CL').format(data);
                  
                  // Calcular la altura de la barra
                  const barHeight = bar._model.y - bar._model.base;
                  
                  // Calcular el ancho del texto
                  const textWidth = ctx.measureText(value).width;
                  const textHeight = 12; // Aproximadamente la altura del texto
                  
                  // Decidir si mostrar dentro o fuera de la barra
                  if (Math.abs(barHeight) > textHeight + 10) {
                    // Mostrar dentro de la barra
                    ctx.textAlign = 'center';
                    ctx.fillStyle = '#fff';
                    const x = bar._model.x;
                    const y = bar._model.base - 10; // 10px desde la base
                    
                    // Agregar sombra para mejor legibilidad
                    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
                    ctx.shadowBlur = 3;
                    ctx.shadowOffsetX = 1;
                    ctx.shadowOffsetY = 1;
                    
                    ctx.fillText(value, x, y);
                    
                    // Resetear sombra
                    ctx.shadowColor = 'transparent';
                    ctx.shadowBlur = 0;
                    ctx.shadowOffsetX = 0;
                    ctx.shadowOffsetY = 0;
                  } else {
                    // Mostrar fuera de la barra (arriba)
                    ctx.textAlign = 'center';
                    ctx.fillStyle = '#444';
                    const x = bar._model.x;
                    const y = bar._model.y - 10;
                    ctx.fillText(value, x, y);
                  }
                });
              });
            }
          }
        }
      });
    },

        createModalityChart() {
      if (!this.$refs.modalityChart || this.chartData.salesByModality.length === 0) return;

      if (this.chartInstances.modalityChart) {
        this.chartInstances.modalityChart.destroy();
      }

      const ctx = this.$refs.modalityChart.getContext('2d');
      this.chartInstances.modalityChart = new Chart(ctx, {
        type: 'pie',
        data: {
          labels: this.chartData.salesByModality.map(item => 
            item.label === 'Sin tipo' ? 'Otras Ventas sin Membresías' : item.label
          ),
          datasets: [{
            data: this.chartData.salesByModality.map(item => item.amount || 0),
            backgroundColor: ['#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0'],
            borderWidth: 2
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { position: 'bottom' },
          tooltips: {
            callbacks: {
              label: (tooltipItem, data) => {
                const total = data.datasets[0].data.reduce((sum, value) => sum + value, 0);
                const amount = data.datasets[0].data[tooltipItem.index];
                const percentage = ((amount / total) * 100).toFixed(1);
                const modalityData = this.chartData.salesByModality[tooltipItem.index];
                const count = modalityData ? modalityData.value : 0;
                const label = data.labels[tooltipItem.index];
                return [
                  `${label}:`,
                  `💰 Monto Total: ${this.formatCurrencyForCharts(amount)}`,
                  `📊 Cantidad de Ventas: ${new Intl.NumberFormat('es-CL').format(count)}`,
                  `📈 Representa el ${percentage}% del monto total`
                ];
              }
            }
          }
        }
      });
    },

        createCashierChart() {
      if (!this.$refs.cashierChart || this.chartData.salesByCashier.length === 0) return;

      if (this.chartInstances.cashierChart) {
        this.chartInstances.cashierChart.destroy();
      }

      const ctx = this.$refs.cashierChart.getContext('2d');
      this.chartInstances.cashierChart = new Chart(ctx, {
        type: 'horizontalBar',
        data: {
          labels: this.chartData.salesByCashier.map(item => item.label),
          datasets: [{
            label: 'Monto Total',
            data: this.chartData.salesByCashier.map(item => item.amount || 0),
            backgroundColor: '#9966FF',
            borderColor: '#7744CC',
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            xAxes: [{ 
              ticks: { 
                beginAtZero: true,
                callback: function(value) {
                  return new Intl.NumberFormat('es-CL', {
                    style: 'currency',
                    currency: 'CLP',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  }).format(value);
                }
              },
              scaleLabel: {
                display: true,
                labelString: 'Monto Total'
              }
            }],
            yAxes: [{
              ticks: {
                maxRotation: 0
              }
            }]
          },
          tooltips: {
            callbacks: {
              label: (tooltipItem) => {
                const cashierData = this.chartData.salesByCashier[tooltipItem.index];
                const amount = cashierData ? cashierData.amount : 0;
                const count = cashierData ? cashierData.value : 0;
                const total = this.chartData.salesByCashier.reduce((sum, item) => sum + (item.amount || 0), 0);
                const percentage = total > 0 ? ((amount / total) * 100).toFixed(1) : '0';
                return [
                  `💰 Monto Total: ${this.formatCurrencyForCharts(amount)}`,
                  `📊 Cantidad de Ventas: ${new Intl.NumberFormat('es-CL').format(count)}`,
                  `📈 Representa el ${percentage}% del total`
                ];
              }
            }
          },
          animation: {
            duration: 1000,
            easing: 'easeInOutQuart',
            onComplete: function() {
              const chartInstance = this.chart;
              const ctx = chartInstance.ctx;
              ctx.textBaseline = 'middle';
              ctx.font = 'bold 12px Arial';
              
              this.data.datasets.forEach((dataset, i) => {
                const meta = chartInstance.controller.getDatasetMeta(i);
                meta.data.forEach((bar, index) => {
                  const data = dataset.data[index];
                  const value = '$' + new Intl.NumberFormat('es-CL', { minimumFractionDigits: 0 }).format(data);
                  
                  // Calcular el ancho de la barra (horizontal)
                  const barWidth = bar._model.x - bar._model.base;
                  
                  // Calcular el ancho del texto
                  const textWidth = ctx.measureText(value).width;
                  
                  // Decidir si mostrar dentro o fuera de la barra
                  if (barWidth > textWidth + 20) {
                    // Mostrar dentro de la barra
                    ctx.textAlign = 'right';
                    ctx.fillStyle = '#fff';
                    const x = bar._model.x - 10;
                    const y = bar._model.y;
                    
                    // Agregar sombra para mejor legibilidad
                    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
                    ctx.shadowBlur = 3;
                    ctx.shadowOffsetX = 1;
                    ctx.shadowOffsetY = 1;
                    
                    ctx.fillText(value, x, y);
                    
                    // Resetear sombra
                    ctx.shadowColor = 'transparent';
                    ctx.shadowBlur = 0;
                    ctx.shadowOffsetX = 0;
                    ctx.shadowOffsetY = 0;
                  } else {
                    // Mostrar fuera de la barra (a la derecha)
                    ctx.textAlign = 'left';
                    ctx.fillStyle = '#444';
                    const x = bar._model.x + 5;
                    const y = bar._model.y;
                    ctx.fillText(value, x, y);
                  }
                });
              });
            }
          }
        }
      });
    },

    // NUEVOS MÉTODOS PARA GRÁFICOS DE DESCUENTOS
    
        createDiscountCategoryChart() {
      if (!this.$refs.discountCategoryChart || this.chartData.discountsByCategory.length === 0) return;

      if (this.chartInstances.discountCategoryChart) {
        this.chartInstances.discountCategoryChart.destroy();
      }

      const ctx = this.$refs.discountCategoryChart.getContext('2d');
      this.chartInstances.discountCategoryChart = new Chart(ctx, {
        type: 'horizontalBar',
        data: {
          labels: this.chartData.discountsByCategory.map(item => item.label),
          datasets: [{
            label: 'Total Descuentos',
            data: this.chartData.discountsByCategory.map(item => item.amount || 0),
            backgroundColor: [
              '#FF6B6B', '#FF9F43', '#FFA726', '#FFCC02',
              '#66BB6A', '#42A5F5', '#AB47BC', '#78909C'
            ],
            borderColor: [
              '#FF5252', '#FF8F00', '#FF9800', '#FFB300',
              '#4CAF50', '#2196F3', '#9C27B0', '#607D8B'
            ],
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            xAxes: [{
              ticks: {
                beginAtZero: true,
                callback: function(value) {
                  return new Intl.NumberFormat('es-CL', {
                    style: 'currency',
                    currency: 'CLP',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  }).format(value);
                }
              },
              scaleLabel: {
                display: true,
                labelString: 'Total Descuentos'
              }
            }],
            yAxes: [{
              ticks: {
                maxRotation: 0
              }
            }]
          },
          tooltips: {
            callbacks: {
              label: (tooltipItem, data) => {
                const total = data.datasets[0].data.reduce((sum, value) => sum + value, 0);
                const amount = data.datasets[0].data[tooltipItem.index];
                const percentage = total > 0 ? ((amount / total) * 100).toFixed(1) : '0';
                const categoryData = this.chartData.discountsByCategory[tooltipItem.index];
                const count = categoryData ? categoryData.value : 0;
                return [
                  `💸 Total Descuentos: ${this.formatCurrencyForCharts(amount)}`,
                  `📊 Cantidad de Ventas: ${new Intl.NumberFormat('es-CL').format(count)}`,
                  `📈 Representa el ${percentage}% del total de descuentos`
                ];
              }
            }
          },
          animation: {
            duration: 1000,
            easing: 'easeInOutQuart',
            onComplete: function() {
              const chartInstance = this.chart;
              const ctx = chartInstance.ctx;
              ctx.textBaseline = 'middle';
              ctx.font = 'bold 12px Arial';
              
              this.data.datasets.forEach((dataset, i) => {
                const meta = chartInstance.controller.getDatasetMeta(i);
                meta.data.forEach((bar, index) => {
                  const data = dataset.data[index];
                  const value = '$' + new Intl.NumberFormat('es-CL', { minimumFractionDigits: 0 }).format(data);
                  
                  // Calcular el ancho de la barra (horizontal)
                  const barWidth = bar._model.x - bar._model.base;
                  
                  // Calcular el ancho del texto
                  const textWidth = ctx.measureText(value).width;
                  
                  // Decidir si mostrar dentro o fuera de la barra
                  if (barWidth > textWidth + 20) {
                    // Mostrar dentro de la barra
                    ctx.textAlign = 'right';
                    ctx.fillStyle = '#fff';
                    const x = bar._model.x - 10;
                    const y = bar._model.y;
                    
                    // Agregar sombra para mejor legibilidad
                    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
                    ctx.shadowBlur = 3;
                    ctx.shadowOffsetX = 1;
                    ctx.shadowOffsetY = 1;
                    
                    ctx.fillText(value, x, y);
                    
                    // Resetear sombra
                    ctx.shadowColor = 'transparent';
                    ctx.shadowBlur = 0;
                    ctx.shadowOffsetX = 0;
                    ctx.shadowOffsetY = 0;
                  } else {
                    // Mostrar fuera de la barra (a la derecha)
                    ctx.textAlign = 'left';
                    ctx.fillStyle = '#444';
                    const x = bar._model.x + 5;
                    const y = bar._model.y;
                    ctx.fillText(value, x, y);
                  }
                });
              });
            }
          }
        }
      });
    },

    createDiscountTrendChart() {
      if (!this.$refs.discountTrendChart || this.chartData.discountsDailyTrend.length === 0) return;

      if (this.chartInstances.discountTrendChart) {
        this.chartInstances.discountTrendChart.destroy();
      }

      const ctx = this.$refs.discountTrendChart.getContext('2d');
      this.chartInstances.discountTrendChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: this.chartData.discountsDailyTrend.map(item => {
            // Formatear fecha evitando problemas de zona horaria
            const dateParts = item.label.split('-');
            if (dateParts.length === 3) {
              const day = dateParts[2];
              const month = dateParts[1];
              return `${day}/${month}`;
            }
            return item.label;
          }),
          datasets: [{
            label: 'Descuentos Diarios',
            data: this.chartData.discountsDailyTrend.map(item => item.amount),
            borderColor: '#FF6B6B',
            backgroundColor: 'rgba(255, 107, 107, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.3,
            pointBackgroundColor: '#FF6B6B',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 4
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            yAxes: [{
              ticks: {
                beginAtZero: false,
                callback: (value) => {
                  return this.formatCurrencyForCharts(value);
                }
              },
              scaleLabel: {
                display: true,
                labelString: 'Monto de Descuentos (valores negativos)'
              }
            }],
            xAxes: [{
              scaleLabel: {
                display: true,
                labelString: 'Fecha'
              }
            }]
          },
          tooltips: {
            callbacks: {
              title: (tooltipItems) => {
                const dateLabel = this.chartData.discountsDailyTrend[tooltipItems[0].index].label;
                const dateParts = dateLabel.split('-');
                if (dateParts.length === 3) {
                  const year = dateParts[0];
                  const month = dateParts[1];
                  const day = dateParts[2];
                  const monthNames = [
                    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
                    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
                  ];
                  return `${day} de ${monthNames[parseInt(month) - 1]} de ${year}`;
                }
                return dateLabel;
              },
              label: (tooltipItem) => {
                const discountData = this.chartData.discountsDailyTrend[tooltipItem.index];
                const amount = discountData ? discountData.amount : 0;
                return [
                  `💸 Total Descuentos: ${this.formatCurrencyForCharts(amount)}`,
                  `📊 Valores negativos representan descuentos aplicados`
                ];
              }
            }
          }
        }
      });
    },
    
    stopFetching() {
      this.canFetchMore = false;
    },
    async anularVenta(sale) {
      this.isAnulling = true;
      // Mostrar modal de carga
      this.$swal.fire({
        title: "Anulando venta...",
        text: "Por favor espere",
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          this.$swal.showLoading();
        },
      });

      try {
        if (process.env.NODE_ENV === 'development') {
          // Simulamos delay de red
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Simulamos respuesta exitosa
          const mockResponse = {
            success: true,
            message: "Venta anulada correctamente"
          };

          if (mockResponse.success) {
            // Actualizamos el estado de la venta en los registros
            const saleIndex = this.records.findIndex(
              record => record._id.$oid === sale._id.$oid
            );
            if (saleIndex !== -1) {
              this.records[saleIndex].estadoVenta = '[ANULADA]';
            }

            this.$swal.fire({
              icon: "success",
              title: "¡Éxito!",
              text: "La venta ha sido anulada correctamente",
              confirmButtonColor: "#6bd098",
            });
          }
        } else {
          // Llamada real a la API en producción
          const response = await this.$CentralGymOPS_API.post(
            "",
            {
              Parameters: {
                idVenta: sale._id.$oid,
                idTransaccion: sale.idTransaccion.$numberLong
              }
            },
            {
              headers: {
                "Content-Type": "application/json",
                token: sessionStorage.getItem("jCxbraoQeD"),
                operation: "/v1/sales/cancel/gymsale"
              }
            }
          );

          if (response.data && response.data.success) {
            // Actualizamos el estado de la venta en los registros
            const saleIndex = this.records.findIndex(
              record => record._id.$oid === sale._id.$oid
            );
            if (saleIndex !== -1) {
              this.records[saleIndex].estadoVenta = '[ANULADA]';
            }

            this.$swal.fire({
              icon: "success",
              title: "¡Éxito!",
              text: response.data.message || "La venta ha sido anulada correctamente",
              confirmButtonColor: "#6bd098",
            });
          } else {
            throw new Error(response.data?.message || "Error al anular la venta");
          }
        }
      } catch (error) {
        let errorMessage = "Ha ocurrido un error al anular la venta";

        if (error.response) {
          switch (error.response.status) {
            case 401:
              errorMessage = "No tienes autorización para anular esta venta. Por favor, inicia sesión nuevamente.";
              break;
            case 403:
              errorMessage = "No tienes permisos suficientes para anular ventas.";
              break;
            case 404:
              errorMessage = "No se encontró la venta especificada.";
              break;
            case 500:
              errorMessage = "Error interno del servidor. Por favor, intenta más tarde.";
              break;
            default:
              errorMessage = error.response.data?.message || 
                            "Error en la comunicación con el servidor";
          }
        } else if (error.request) {
          errorMessage = "No se pudo establecer conexión con el servidor. Por favor, verifica tu conexión a internet.";
        }

        await this.$swal.fire({
          icon: "error",
          title: "Error al anular la venta",
          html: `
            <div class="text-left">
              <p><strong>Detalles del error:</strong></p>
              <p>${errorMessage}</p>
              <p class="text-muted small">Si el problema persiste, contacta a soporte técnico.</p>
            </div>
          `,
          confirmButtonText: "Entendido",
          confirmButtonColor: "#6bd098",
        });

        if (error.response?.status === 401) {
          sessionStorage.clear();
          this.$router.push('/login');
          return;
        }
      } finally {
        this.isAnulling = false;
      }
    }
  }
};
</script>

<style>
/* Estilos para el modal y backdrop con z-index más altos */
.modal-backdrop {
  z-index: 9040 !important;
}

.modal {
  z-index: 9045 !important;
}

.modal-dialog {
  z-index: 9046 !important;
}

/* Asegurar que el contenido del modal esté por encima de todo */
.modal-content {
  z-index: 9047 !important;
}

/* Resto de los estilos del modal */
.modal-dialog.modal-lg {
  max-width: 80% !important;
  margin: 1.75rem auto;
  position: relative;
}

.modal-content {
  position: relative;
  background-color: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

/* Mejorar la visibilidad del backdrop */
.modal-backdrop.show {
  opacity: 0.7 !important;
  backdrop-filter: blur(5px);
}

/* Asegurar que el body mantenga el modal por encima */
body.modal-open {
  overflow: hidden;
  padding-right: 0 !important;
}

body.modal-open .modal {
  display: block;
  padding-right: 0 !important;
}

/* Ajustes para el tamaño y posicionamiento */
.modal-dialog.modal-lg {
  max-width: 80% !important;
  margin: 1.75rem auto;
}

/* Estilos para el contenido del modal */
.modal-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  border-top-left-radius: calc(0.5rem - 1px);
  border-top-right-radius: calc(0.5rem - 1px);
  padding: 1rem;
}

.modal-body {
  position: relative;
  flex: 1 1 auto;
  padding: 1.5rem;
  max-height: calc(100vh - 210px);
  overflow-y: auto;
}

.modal-footer {
  border-top: 1px solid #dee2e6;
  border-bottom-right-radius: calc(0.5rem - 1px);
  border-bottom-left-radius: calc(0.5rem - 1px);
  padding: 1rem;
}

/* Ajustes responsive */
@media (max-width: 992px) {
  .modal-dialog.modal-lg {
    max-width: 95% !important;
    margin: 1rem auto;
  }
}

/* Asegurar que el modal esté por encima del sidebar */
body.modal-open {
  overflow: hidden;
}

.modal-open .modal {
  overflow-x: hidden;
  overflow-y: auto;
}

/* Ajustes para el backdrop */
.modal-backdrop.show {
  opacity: 0.5;
  backdrop-filter: blur(5px);
}

/* Estilos para las cards dentro del modal */
.modal .card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  margin-bottom: 1rem;
}

.modal .card:last-child {
  margin-bottom: 0;
}

/* Ajustes para la tabla dentro del modal */
.modal .table-responsive {
  margin: 0;
}

.modal .table-sm td, 
.modal .table-sm th {
  padding: 0.5rem;
}

/* Estilos para estados de carga y paginación */
.table-loading-overlay {
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 10;
}

.table.opacity-50 {
  opacity: 0.5;
  pointer-events: none;
}

/* Estilos para la paginación */
.pagination .page-link {
  color: #6c757d;
  border-color: #dee2e6;
  transition: all 0.15s ease-in-out;
}

.pagination .page-link:hover {
  color: #495057;
  background-color: #e9ecef;
  border-color: #adb5bd;
}

.pagination .page-item.active .page-link {
  background-color: #007bff;
  border-color: #007bff;
  color: #fff;
}

.pagination .page-item.disabled .page-link {
  color: #6c757d;
  background-color: #fff;
  border-color: #dee2e6;
  cursor: not-allowed;
}

/* Indicador de búsqueda */
.alert-info {
  background-color: #d1ecf1;
  border-color: #bee5eb;
  color: #0c5460;
}

/* Controles de elementos por página */
.form-select-sm {
  font-size: 0.875rem;
  min-width: 70px;
}

/* Mensaje de sin resultados */
.text-muted i {
  opacity: 0.5;
}

/* Spinner de anulación en botón */
.btn .fa-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Estilos para ordenamiento de columnas */
.sortable-header {
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
  position: relative;
}

.sortable-header:hover {
  background-color: rgba(0, 123, 255, 0.1);
  color: #007bff;
}

.sortable-header.sort-active {
  background-color: rgba(0, 123, 255, 0.15);
  color: #007bff;
  font-weight: 600;
}

.sortable-header i {
  font-size: 0.8rem;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.sortable-header:hover i {
  opacity: 1;
}

/* Estilos para el resumen de totales */
.icon-shape {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1.2rem;
}

.bg-light {
  background-color: #f8f9fa !important;
}

/* Estilos para filtros avanzados */
.card-header .btn-link {
  color: #495057;
  font-weight: 500;
}

.card-header .btn-link:hover {
  color: #007bff;
  text-decoration: none;
}

.btn-group-vertical .btn {
  margin-bottom: 0.25rem;
}

.btn-group-vertical .btn:last-child {
  margin-bottom: 0;
}

/* Estilos para filtros de fecha */
.form-control-sm {
  font-size: 0.875rem;
}

/* Animaciones para elementos colapsables */
.card-body {
  transition: all 0.3s ease;
}

/* Estilos para botones de exportación */
.btn-toolbar .btn-group {
  margin-right: 0.5rem;
}

/* Mejoras responsive */
@media (max-width: 768px) {
  /* Filtros principales */
  .row.align-items-end .col-md-2,
  .row.align-items-end .col-md-3 {
    margin-bottom: 0.5rem;
  }
  
  .row.align-items-end .col-md-3:last-child {
    margin-bottom: 0;
  }
  
  .row.align-items-end .col-md-3:last-child .d-flex {
    justify-content: start !important;
  }

  /* Paginación */
  .pagination {
    font-size: 0.875rem;
  }
  
  .pagination .page-link {
    padding: 0.375rem 0.75rem;
  }

  /* Botones verticales */
  .btn-group-vertical {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .btn-group-vertical .btn {
    margin-bottom: 0.5rem;
    width: 100%;
  }

  /* Iconos */
  .icon-shape {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }

  .card-body .row .col-lg-3 {
    margin-bottom: 1rem;
  }

  /* Toolbar */
  .btn-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .btn-toolbar .btn-group {
    margin-bottom: 0.5rem;
    margin-right: 0;
  }

  /* Headers ordenables */
  .sortable-header {
    font-size: 0.875rem;
    padding: 0.5rem 0.25rem;
  }
  
  /* Fase 3 - Acciones en lote responsive */
  .btn-group.w-100 {
    display: flex;
    flex-direction: column;
  }

  .btn-group.w-100 .btn {
    margin-bottom: 0.25rem;
    border-radius: 0.375rem !important;
  }

  .btn-group.w-100 .btn:last-child {
    margin-bottom: 0;
  }

  .alert-primary .row {
    flex-direction: column;
  }

  .alert-primary .col-md-6 {
    margin-bottom: 1rem;
  }

  .alert-primary .col-md-6:last-child {
    margin-bottom: 0;
  }

  .card.border-light .card-body {
    padding: 1rem;
  }

  /* Gráficos responsive */
  .chart-container {
    height: 150px !important;
    margin-bottom: 1rem;
    padding: 5px;
  }

  .chart-container.no-data {
    font-size: 0.75rem;
  }

  /* Toolbar responsive adicional */
  .btn-toolbar {
    flex-wrap: wrap;
  }

  .btn-toolbar .btn-group {
    margin-bottom: 0.5rem;
    flex: 1;
  }

  .btn-toolbar .btn-group .btn {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
  }
}

/* Mejoras para tablets */
@media (min-width: 769px) and (max-width: 1024px) {
  .icon-shape {
    width: 44px;
    height: 44px;
    font-size: 1.1rem;
  }
  
  .btn-toolbar .btn-group .btn {
    font-size: 0.875rem;
    padding: 0.375rem 0.75rem;
  }
}

/* Estilos para selección múltiple */
.form-check {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 1.5rem;
}

.form-check-input {
  margin: 0;
  cursor: pointer;
}

.form-check-input:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.table-primary {
  background-color: rgba(13, 110, 253, 0.1);
  border-color: rgba(13, 110, 253, 0.2);
}

.table-primary:hover {
  background-color: rgba(13, 110, 253, 0.15);
}

/* Estilos para acciones en lote */
.alert-primary {
  border-left: 4px solid #0d6efd;
  background-color: rgba(13, 110, 253, 0.1);
  border-color: rgba(13, 110, 253, 0.2);
}

/* Estilos para filtros guardados */
.border-primary {
  border-color: #0d6efd !important;
}

.bg-primary {
  background-color: #0d6efd !important;
}

.card.border-light {
  border-color: rgba(0, 0, 0, 0.125) !important;
  transition: all 0.2s ease;
}

.card.border-light:hover {
  border-color: #0d6efd !important;
  box-shadow: 0 0.125rem 0.25rem rgba(13, 110, 253, 0.25);
}

/* Estilos para panel de gráficos */
.border-warning {
  border-color: #ffc107 !important;
}

.bg-warning {
  background-color: #ffc107 !important;
}

.chart-container {
  position: relative;
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 10px;
}

.chart-container canvas {
  max-width: 100%;
  height: 100% !important;
}

/* Estado cuando no hay datos */
.chart-container.no-data {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  background-color: #f8f9fa;
  flex-direction: column;
  padding: 1rem;
}

.chart-container.no-data::before {
  content: "📊 Sin datos para mostrar";
  font-size: 0.875rem;
  text-align: center;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.chart-container.no-data::after {
  content: "Revisa la consola para más detalles";
  font-size: 0.75rem;
  text-align: center;
  color: #999;
  font-style: italic;
}

/* Estilos para modal de guardar filtro */
.modal-content {
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.modal-header {
  border-bottom: 1px solid #dee2e6;
  border-top-left-radius: calc(0.5rem - 1px);
  border-top-right-radius: calc(0.5rem - 1px);
}

.modal-footer {
  border-top: 1px solid #dee2e6;
  border-bottom-right-radius: calc(0.5rem - 1px);
  border-bottom-left-radius: calc(0.5rem - 1px);
}

/* Estilos para dropdown de filtros guardados */
.dropdown-menu {
  border-radius: 0.375rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.15);
}

.dropdown-item {
  padding: 0.5rem 1rem;
  transition: background-color 0.15s ease-in-out;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.dropdown-item.text-danger:hover {
  background-color: #f8d7da;
  color: #721c24;
}

/* Estilos para botones de exportación */
.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
}

.btn-danger:hover {
  background-color: #c82333;
  border-color: #bd2130;
}

.btn-warning {
  background-color: #ffc107;
  border-color: #ffc107;
  color: #212529;
}

.btn-warning:hover {
  background-color: #e0a800;
  border-color: #d39e00;
  color: #212529;
}

/* Animaciones mejoradas */
.btn-group .btn,
.btn-toolbar .btn {
  transition: all 0.15s ease-in-out;
}

.card {
  transition: all 0.2s ease-in-out;
}

.alert {
  animation: slideInDown 0.3s ease-in-out;
}

@keyframes slideInDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Estilos específicos para el nuevo header similar a OnLineSalesTable */
.card-header.bg-success {
  background-color: #28A745 !important;
  padding: 1rem;
  padding-bottom: 1rem;
}

/* ========================================
   ESTILOS PARA ETIQUETAS DE FILTROS v2.5
   ======================================== */

/* Etiquetas de filtros en desktop */
.card-header .form-label.text-light {
  color: #f8f9fa !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  margin-top: 0.5rem !important;      /* Separar del borde superior */
  margin-bottom: 0.375rem !important; /* Reducir distancia excesiva con campos */
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  letter-spacing: 0.025em;
}

.card-header .form-label.text-light i {
  opacity: 0.9;
  font-size: 0.7rem;
}

/* Etiquetas de filtros en móvil */
.mobile-filters .form-label.text-white {
  color: #ffffff !important;
  font-weight: 700 !important;
  font-size: 0.8rem !important;
  margin-bottom: 0.375rem !important;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  letter-spacing: 0.025em;
  display: block;
}

.mobile-filters .form-label.text-white i {
  opacity: 1;
  font-size: 0.75rem;
  margin-right: 0.5rem !important;
}

/* Ajuste de espaciado para filtros con etiquetas */
.card-header .flex-column {
  align-items: flex-start !important;
  min-height: 3.5rem;           /* Altura reducida para mejor proporción */
  justify-content: flex-start;  /* Alineación natural sin forzar separación */
}

/* Mejoras responsive para etiquetas */
@media (max-width: 767.98px) {
  .mobile-filters .form-label.text-white {
    font-size: 0.85rem !important;
    margin-bottom: 0.5rem !important;
  }
  
  .mobile-filters .form-label.text-white i {
    font-size: 0.8rem;
  }
  
  .mobile-filters .mb-3 {
    margin-bottom: 1.25rem !important;
  }
}

/* Mejoras para accesibilidad de etiquetas */
.form-label[for] {
  cursor: pointer;
}

.form-label:hover {
  opacity: 0.9;
}

.btn-filter--border {
  border: 2px solid #f8f9fa !important;
  color: #fff !important;
  transition: all 0.2s ease;
}

.btn-filter--border:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background-color: rgba(255, 255, 255, 0.1) !important;
}

.cursor-pointer {
  cursor: pointer;
  transition: all 0.2s ease;
}

.cursor-pointer:hover {
  transform: scale(1.1);
  color: #ffc107 !important;
}

/* Estilos para la segunda fila de botones */
.card-header .border-top {
  margin-top: 0.5rem;
  padding-top: 0.75rem;
  border-color: rgba(255, 255, 255, 0.25) !important;
}

/* Espaciado mejorado para los botones de panel */
.btn-group .btn-filter--border {
  margin-left: 0.25rem;
  margin-right: 0.25rem;
  transition: all 0.2s ease;
  border-color: rgba(255, 255, 255, 0.5) !important;
}

.btn-group .btn-filter--border:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-group .btn-filter--border:first-child {
  margin-left: 0;
}

.btn-group .btn-filter--border:last-child {
  margin-right: 0;
}

/* Botones de panel - Estado normal (gris oscuro con borde blanco) */
.panel-toggle-btn.btn-filter--border {
  background-color: rgba(52, 58, 64, 0.8) !important;
  border-color: rgba(255, 255, 255, 0.8) !important;
  color: #fff !important;
  transition: all 0.3s ease !important;
}

/* Botones de panel - Estado activo (tono más claro) */
.panel-toggle-btn.btn-filter--border.active {
  background-color: rgba(108, 117, 125, 0.9) !important;
  border-color: rgba(255, 255, 255, 1) !important;
  color: #fff !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) !important;
}

/* Hover para ambos estados */
.panel-toggle-btn.btn-filter--border:hover {
  background-color: rgba(73, 80, 87, 0.9) !important;
  border-color: rgba(255, 255, 255, 1) !important;
  transform: translateY(-1px);
}

/* Estilos para filtros móviles */
.mobile-filters {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  padding: 1rem;
  margin-top: 0.5rem;
}

.mobile-filters .form-control,
.mobile-filters .form-select {
  background-color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.mobile-filters .form-control:focus,
.mobile-filters .form-select:focus {
  background-color: #fff;
  border-color: #ffc107;
  box-shadow: 0 0 0 0.25rem rgba(255, 193, 7, 0.25);
}

/* Estilos responsive para la nueva estructura */
@media (max-width: 767.98px) {
  .card-header .btn-group {
    flex-direction: column;
    width: 100%;
  }
  
  .card-header .btn-group .btn {
    margin: 0.25rem 0;
    width: 100%;
  }
  
  .mobile-filters .btn-group {
    flex-direction: row;
  }
  
  /* CORREGIDO: Tamaño consistente para todos los botones móviles */
  .mobile-filters .btn-group .btn,
  .mobile-btn-consistent {
    font-size: 0.75rem !important;
    padding: 0.375rem 0.5rem !important;
    height: 2.25rem !important;
    line-height: 1.2 !important;
    border-radius: 0.25rem !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  
  /* Ajustes específicos para botones de panel en móvil */
  .mobile-filters .panel-toggle-btn.btn-filter--border {
    background-color: rgba(52, 58, 64, 0.8) !important;
    border-color: rgba(255, 255, 255, 0.8) !important;
  }
  
  .mobile-filters .panel-toggle-btn.btn-filter--border.active {
    background-color: rgba(108, 117, 125, 0.9) !important;
    border-color: rgba(255, 255, 255, 1) !important;
  }
  
  /* NUEVO: Corrección para campos de formulario móviles */
  .mobile-filters .row {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  
  .mobile-filters .col-6 {
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }
  
  .mobile-filters .col-6.pe-2 {
    padding-right: 0.25rem !important;
  }
  
  .mobile-filters .col-6.ps-2 {
    padding-left: 0.25rem !important;
  }
  
  /* Asegurar que los selects no se salgan del contenedor */
  .mobile-filters .form-select {
    max-width: 100% !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  /* Espaciado consistente para iconos en botones móviles */
  .mobile-btn-consistent .fas,
  .mobile-btn-consistent .fa {
    margin-right: 0.25rem !important;
    font-size: 0.75rem !important;
  }
}

/* Mejoras para la accesibilidad en el nuevo diseño */
.text-light {
  color: #f8f9fa !important;
}

.border-opacity-25 {
  border-color: rgba(255, 255, 255, 0.25) !important;
}

/* Animaciones para el header */
.card-header {
  transition: all 0.3s ease;
}

.card-header .d-flex {
  align-items: center;
}

/* Estilos para los controles de filtro en el header */
.card-header .form-control,
.card-header .form-select {
  background-color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #495057;
}

.card-header .form-control:focus,
.card-header .form-select:focus {
  background-color: #fff;
  border-color: #80bdff;
  box-shadow: 0 0 0 0.25rem rgba(0, 123, 255, 0.25);
}

.card-header .form-control::placeholder {
  color: #6c757d;
  opacity: 0.8;
}

/* Contenedor de paginación visible */
.pagination-container {
  background-color: #f8f9fa !important;
  border: 2px solid #007bff !important;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.1);
  animation: fadeInPagination 0.3s ease-in-out;
}

.pagination-container h6 {
  color: #007bff !important;
  font-weight: 600;
  margin-bottom: 1rem !important;
}

@keyframes fadeInPagination {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.pagination-controls {
  border-top: 1px solid #dee2e6;
  padding-top: 0.75rem;
}

/* Información de página responsive para GymSales */
@media (max-width: 768px) {
  .pagination-container .d-flex {
    flex-direction: column;
    align-items: flex-start !important;
  }
  
  .pagination-container .text-end {
    text-align: left !important;
    margin-top: 0.5rem;
  }
  
  .pagination-container .d-flex.align-items-center {
    margin-bottom: 0.5rem;
  }
}

/* ========================================
   ESTILOS PARA FILTROS GUARDADOS MEJORADOS v2.2
   ======================================== */

/* Estilos para filtros guardados mejorados */
.saved-filter-card {
  transition: all 0.3s ease;
  border: 2px solid transparent !important;
}

.saved-filter-card:hover {
  border-color: #0d6efd !important;
  box-shadow: 0 4px 12px rgba(13, 110, 253, 0.15);
  transform: translateY(-2px);
}

.saved-filter-card .card-header {
  border-bottom: 1px solid #dee2e6;
  background-color: #f8f9fa !important;
}

.saved-filter-card .card-title {
  color: #495057;
  font-weight: 600;
}

.saved-filter-card .filter-tags {
  min-height: 2rem;
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
}

.saved-filter-card .filter-tags .badge {
  font-size: 0.65em;
  padding: 0.25em 0.5em;
  border-radius: 0.375rem;
}

.saved-filter-card .btn-group .btn {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
}

.saved-filter-card .btn-primary {
  background-color: #0d6efd;
  border-color: #0d6efd;
  font-weight: 500;
  transition: all 0.2s ease;
}

.saved-filter-card .btn-primary:hover {
  background-color: #0b52cc;
  border-color: #0a4cb8;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(13, 110, 253, 0.3);
}

.saved-filter-card .btn-outline-info:hover {
  background-color: #17a2b8;
  border-color: #17a2b8;
  color: white;
}

.saved-filter-card .btn-outline-success:hover {
  background-color: #28a745;
  border-color: #28a745;
  color: white;
}

.saved-filter-card .btn-outline-danger:hover {
  background-color: #dc3545;
  border-color: #dc3545;
  color: white;
}

/* Animación para aparición de tarjetas */
.saved-filter-card {
  animation: fadeInUp 0.3s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive para filtros guardados */
@media (max-width: 768px) {
  .saved-filter-card .filter-tags .badge {
    font-size: 0.6em;
    margin-bottom: 0.25rem;
  }
  
  .saved-filter-card .btn-group {
    flex-direction: column;
  }
  
  .saved-filter-card .btn-group .btn {
    margin-bottom: 0.25rem;
    border-radius: 0.375rem !important;
  }
}

/* Estilos para estado vacío de filtros guardados */
.empty-state {
  padding: 2rem 1rem;
}

.empty-state .alert {
  border-radius: 0.5rem;
  border: 2px solid #bee5eb;
  background-color: #f0f8ff;
}

.empty-state .alert-heading {
  color: #0c5460;
  font-weight: 600;
}

.empty-state ol {
  text-align: left;
  padding-left: 1.5rem;
}

.empty-state ol li {
  margin-bottom: 0.5rem;
  color: #495057;
}

.empty-state .badge {
  font-size: 0.75em;
  padding: 0.3em 0.6em;
}

@media (max-width: 768px) {
  .empty-state {
    padding: 1rem 0.5rem;
  }
  
  .empty-state .alert {
    max-width: 100% !important;
  }
}

/* ========================================
   ESTILOS PARA BADGES Y FILTROS AVANZADOS
   ======================================== */

/* Estilos específicos para badges de éxito */
.badge.badge-success {
  background-color: #28a745 !important;
  color: #fff !important;
  font-size: 0.65em;
  padding: 0.2em 0.4em;
  border-radius: 0.25rem;
}

/* ========================================
   ESTILOS v2.5.1: FILTROS BÁSICOS - POSICIONAMIENTO CORRECTO DE ETIQUETAS
   ======================================== */

/* Asegurar que todas las etiquetas de filtros estén arriba de los campos */
.card-header .form-label {
  display: block !important;
  width: 100% !important;
  margin-bottom: 0.25rem !important;
  font-weight: 600 !important;
  text-align: left !important;
}

/* Estilos específicos para filtros básicos en desktop */
.d-none.d-md-block .form-label {
  color: #f8f9fa !important;
  font-size: 0.875rem !important;
  margin-bottom: 0.25rem !important;
}

/* Estilos específicos para filtros básicos en móvil */
.mobile-filters .form-label {
  color: #ffffff !important;
  font-weight: bold !important;
  font-size: 0.875rem !important;
  margin-bottom: 0.25rem !important;
}

/* Asegurar espaciado correcto entre etiqueta y campo */
.card-header .form-control,
.card-header .form-select {
  margin-top: 0 !important;
}

/* Estilos para iconos en etiquetas */
.form-label .fas {
  margin-right: 0.25rem;
  opacity: 0.8;
}

/* Alineación perfecta de botones con campos */
.d-none.d-md-block .col-md-7 .d-flex {
  height: calc(1.875rem + 1.5rem) !important; /* altura del label + altura del select */
}

.d-none.d-md-block .col-md-7 .d-flex .d-flex {
  height: 2rem !important; /* altura exacta del select */
}

/* Espaciado adicional para evitar solapamiento */
.d-none.d-md-block .col-md-2 {
  padding-right: 0.75rem !important;
}

.d-none.d-md-block .col-md-7 {
  padding-left: 0.75rem !important;
}

/* Alineación vertical perfecta de botones */
.d-none.d-md-block .col-md-7 .mt-auto {
  margin-top: auto !important;
  margin-bottom: 0 !important;
}

/* Espaciado optimizado para filtros básicos */
.d-none.d-md-block .row {
  margin-bottom: 0.25rem !important;
}

.d-none.d-md-block .col-md-3,
.d-none.d-md-block .col-md-2,
.d-none.d-md-block .col-md-7 {
  margin-bottom: 0.25rem !important;
}

/* Responsive - Asegurar correcta visualización en todos los tamaños */
@media (max-width: 767.98px) {
  .mobile-filters .form-label {
    display: block !important;
    width: 100% !important;
    margin-bottom: 0.25rem !important;
  }
}

@media (min-width: 768px) {
  .d-none.d-md-block .form-label {
    display: block !important;
    width: 100% !important;
    margin-bottom: 0.25rem !important;
  }
}

/* ========================================
   ESTILOS NUEVOS v2.5.2: CAMPO SUCURSAL CON ANCHO FIJO
   ======================================== */

/* Ancho fijo para el campo Sucursal - igual al campo Búsqueda General */
.branch-select-fixed-width {
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  box-sizing: border-box !important;
  /* Evitar que el contenido largo del dropdown expanda el select */
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  overflow: hidden !important;
}

/* Asegurar que las opciones del dropdown no se vean cortadas */
.branch-select-fixed-width option {
  white-space: normal !important;
  text-overflow: initial !important;
  overflow: visible !important;
}

/* Columnas con espaciado consistente para evitar solapamiento */
.d-none.d-md-block .col-md-3 {
  padding-right: 0.75rem !important;
  padding-left: 0.75rem !important;
}

/* Espaciado específico para la segunda fila de filtros */
.d-none.d-md-block .row:nth-child(2) .col-md-3:first-child {
  padding-right: 0.75rem !important;
}

.d-none.d-md-block .row:nth-child(2) .col-md-3:nth-child(2) {
  padding-left: 0.75rem !important;
  padding-right: 0.75rem !important;
}

.d-none.d-md-block .row:nth-child(2) .col-md-6:last-child {
  padding-left: 0.75rem !important;
}

/* Responsive - Asegurar ancho fijo también en móvil */
@media (max-width: 767.98px) {
  .mobile-filters .form-select {
    width: 100% !important;
    max-width: 100% !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    overflow: hidden !important;
  }
  
  .mobile-filters .form-select option {
    white-space: normal !important;
    text-overflow: initial !important;
    overflow: visible !important;
  }
}

/* ========================================
   ESTILOS NUEVOS v2.3: ESTADOS CON COLORES PASTEL
   ======================================== */

/* Estado VIGENTE - Verde pastel */
.bg-success-pastel {
  background-color: #d4edda !important; /* Verde pastel claro */
  color: #155724 !important; /* Verde oscuro para contraste */
  border: 1px solid #c3e6cb !important; /* Borde verde suave */
  font-weight: 600 !important;
  padding: 0.375rem 0.75rem !important;
  border-radius: 0.375rem !important;
  font-size: 0.75rem !important;
  display: inline-block;
  min-width: 80px;
  text-align: center;
}

/* Estado ANULADA - Rojo pastel */
.bg-danger-pastel {
  background-color: #f8d7da !important; /* Rojo pastel claro */
  color: #721c24 !important; /* Rojo oscuro para contraste */
  border: 1px solid #f5c6cb !important; /* Borde rojo suave */
  font-weight: 600 !important;
  padding: 0.375rem 0.75rem !important;
  border-radius: 0.375rem !important;
  font-size: 0.75rem !important;
  display: inline-block;
  min-width: 80px;
  text-align: center;
}

/* Efectos hover para los badges de estado */
.bg-success-pastel:hover {
  background-color: #c3e6cb !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(21, 87, 36, 0.1);
  transition: all 0.2s ease;
}

.bg-danger-pastel:hover {
  background-color: #f5c6cb !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(114, 28, 36, 0.1);
  transition: all 0.2s ease;
}

/* Estilos específicos para badges con fondo oscuro (contraste mejorado) */
.badge.bg-dark {
  background-color: #212529 !important;
  color: #fff !important;
}

/* Alert para filtros avanzados */
.alert-sm {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
}

.alert-warning {
  background-color: #fff3cd;
  border-color: #ffeaa7;
  color: #856404;
}

.alert-warning .fas {
  color: #f39c12;
}

.alert-info {
  background-color: #d1ecf1;
  border-color: #bee5eb;
  color: #0c5460;
}

.alert-info .fas {
  color: #17a2b8;
}

/* Indicador de filtrado en progreso */
.alert-warning {
  background-color: #fff3cd;
  border-color: #ffeaa7;
  color: #856404;
  animation: pulse-warning 2s infinite;
}

@keyframes pulse-warning {
  0% { opacity: 1; }
  50% { opacity: 0.8; }
  100% { opacity: 1; }
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.1em;
}

/* Indicadores de carga para gráficos */
.chart-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 10;
}

.chart-container {
  position: relative;
  min-height: 200px;
}

.chart-container.no-data {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
}

.chart-container.no-data::after {
  content: "Sin datos para mostrar";
  color: #6c757d;
  font-size: 14px;
}

/* Animación de progreso para exportación */
.export-progress-wrapper {
  padding: 20px;
}

.progress {
  height: 30px;
  font-size: 14px;
}

.progress-bar {
  transition: width 0.3s ease;
}

/* Estilos para gráficos en pantalla completa */
.btn-outline-secondary {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.btn-outline-secondary:hover {
  opacity: 1;
}

/* Modal de pantalla completa */
.modal.show {
  animation: fadeIn 0.3s ease-in-out;
  display: flex !important;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
}

.modal-dialog-centered {
  display: flex;
  align-items: center;
  min-height: calc(100vh - 40px);
  margin: auto;
}

.modal-dialog-scrollable .modal-content {
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-dialog-scrollable .modal-body {
  overflow-y: auto;
  flex: 1;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Canvas en modal pantalla completa */
.modal-body .chart-container {
  background-color: #fff;
  padding: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
}

/* Tabla de datos en el modal */
.modal-body .table-responsive {
  max-height: 400px;
  overflow-y: auto;
}

.modal-body .table {
  font-size: 0.9rem;
}

.modal-body .table .badge {
  width: 20px;
  height: 20px;
  display: inline-block;
  border-radius: 3px;
  vertical-align: middle;
}

/* Estilos adicionales para mejorar el modal */
.modal-header {
  position: relative;
  padding-right: 100px; /* Espacio para el botón de cerrar */
}

.modal-title {
  display: flex;
  align-items: center;
  font-weight: 600;
}

/* Mejora visual del botón de cerrar */
.modal-header .btn-danger {
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  transition: all 0.2s;
}

.modal-header .btn-danger:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0,0,0,0.3);
}

/* Asegurar scroll suave */
.modal-body {
  scroll-behavior: smooth;
}

/* Mejora visual de la tabla en el modal */
.modal-body h6 {
  font-weight: 600;
  color: #495057;
  margin-top: 2rem;
}

/* Footer del modal */
.modal-footer {
  background-color: #f8f9fa;
  justify-content: center;
  padding: 1rem 2rem;
}

/* Botón de descarga en el modal */
.modal-footer .btn-primary,
.modal-footer .btn-danger {
  padding: 0.5rem 2rem;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: all 0.2s;
  margin: 0 0.5rem;
}

.modal-footer .btn-primary:hover,
.modal-footer .btn-danger:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.2);
}

/* Botón PDF específico */
.modal-footer .btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
}

.modal-footer .btn-danger:hover {
  background-color: #c82333;
  border-color: #bd2130;
}

/* Estilos para la columna CAJA */
.caja-column {
  white-space: nowrap !important;
  min-width: 80px !important;
  width: auto !important;
}

/* Prevenir que el texto de la columna CAJA se corte o baje */
th.caja-column,
td.caja-column {
  white-space: nowrap !important;
  overflow: visible !important;
  text-overflow: unset !important;
}

/* Dar un poco más de padding a la columna CAJA para mejor legibilidad */
td.caja-column {
  padding-right: 1rem !important;
}
</style> 